import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PmsComplianceCheckin from './PmsComplianceCheckin';
import { toIsoDate, todayIso, fmtDateShortYear, fmtDateTimeMedium, fmtDateTimeShort, fmtDateDMY, fmtTimeHHMM, fmtDateDMYDash, fmtDateMedium, toDateTimeLocalValue, dateTimeLocalToIso } from '../utils/dateUtils';
import { buildGuestPortalUrl } from '../utils/guestPortalUrl';
import useIsMobile from '../hooks/useIsMobile';
import LockReadCardModal from '../components/LockReadCardModal';
import LockEncodeModal from '../components/LockEncodeModal';
/* room-rack-redesign v2 */

const FIELD_LABELS = {
  rate_amount: 'Rate (Stay Total)',
  rate_plan_code: 'Rate Plan',
  room_type_id: 'Room Type',
  stay_dates: 'Stay Dates',
  room_added: 'Room Added',
  room_removed: 'Room Removed',
  room_count: 'No. of Rooms'
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const normalizeCountry = (value) => String(value || '').trim().toLowerCase();
const buildDefaultComplianceData = (hotelCountry) => ({
  nationality: hotelCountry || 'India',
  is_foreign_guest: false
});
const formatDateTime = (value) => fmtDateTimeMedium(value);
const formatDepartureWithPolicyTime = (value, defaultCheckoutTime = '11:00') => {
  if (!value) return '-';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = String(d.getFullYear()).slice(-2);
  const time = /^\d{2}:\d{2}$/.test(String(defaultCheckoutTime || '').slice(0, 5))
    ? String(defaultCheckoutTime).slice(0, 5)
    : '11:00';
  const [hhRaw, mmRaw] = time.split(':');
  const hh24 = Number(hhRaw || 0);
  const mm = String(mmRaw || '00').padStart(2, '0');
  const period = hh24 >= 12 ? 'PM' : 'AM';
  const hh12 = hh24 % 12 || 12;
  return `${day}-${month}-${year} ${String(hh12).padStart(2, '0')}-${mm} ${period}`;
};
const formatRackPax = (room) => {
  const adults = Number(room?.line_adults);
  const children = Number(room?.line_children);
  if (Number.isFinite(adults) && Number.isFinite(children) && (adults > 0 || children > 0)) {
    return `${adults + children} (${adults}A / ${children}C)`;
  }
  const total = Number(room?.pax_count ?? room?.total_pax ?? 0);
  return Number.isFinite(total) && total > 0 ? String(total) : '-';
};
const NO_TAX_PROFILE_OPTION = '__NO_TAX__';

const parseApiResponse = async (res) => {
  const contentType = String(res.headers?.get('content-type') || '').toLowerCase();
  const rawText = await res.text();
  if (!rawText) return { ok: res.ok, status: res.status, data: null, text: '' };
  const looksJson = contentType.includes('application/json') || /^[\[{]/.test(rawText.trim());
  if (looksJson) {
    try {
      const data = JSON.parse(rawText);
      return { ok: res.ok, status: res.status, data, text: rawText };
    } catch (_) {
      return { ok: res.ok, status: res.status, data: null, text: rawText };
    }
  }
  return { ok: res.ok, status: res.status, data: null, text: rawText };
};

const deriveCheckoutBillingContext = (folio) => {
  const billTo = String(folio?.bill_to || 'SELF').trim().toUpperCase();
  const corporateId = Number(folio?.corporate_id || 0);
  const travelAgentId = Number(folio?.travel_agent_id || 0);
  if (billTo === 'CORPORATE' && corporateId > 0) {
    return {
      payerType: 'CORPORATE',
      ownerEntityType: 'CORPORATE',
      ownerEntityId: corporateId,
      billToLabel: folio?.corporate_name || `Corporate #${corporateId}`
    };
  }
  if (travelAgentId > 0) {
    return {
      payerType: 'TRAVEL_AGENT',
      ownerEntityType: 'TRAVEL_AGENT',
      ownerEntityId: travelAgentId,
      billToLabel: folio?.travel_agent_name || `Travel Agent #${travelAgentId}`
    };
  }
  return {
    payerType: 'SELF',
    ownerEntityType: 'PROPERTY',
    ownerEntityId: null,
    billToLabel: folio?.primary_guest_name || 'Guest'
  };
};

const formatBillingProfileAddress = (profile) => {
  const parts = [
    profile?.address_line1,
    profile?.address_line2,
    profile?.city,
    profile?.state,
    profile?.country,
    profile?.pin_code
  ].map((value) => String(value || '').trim()).filter(Boolean);
  return parts.join(', ');
};

function PmsCommandCenter({ token, selectedProperty, role, permissions, onOpenWalkIn, onEditReservation, onNewReservation, onNewWalkIn, onOpenExceptionBill }) {
  // Hotel Admin / Super Admin — also gates the folio posting Void column.
  const isAdminUser = role === 'admin' || role === 'hotel_admin' || Boolean(permissions?.hotel_admin);
  const { isMobile, isTablet } = useIsMobile();
  const [date, setDate] = useState(toIsoDate(new Date()));
  // Command Center → Arrivals tab. Kept independent of the Room Rack state above —
  // it reads the same `date`, but has its own data source and doesn't touch `load()`.
  const [activeTab, setActiveTab] = useState('ROOM_RACK');
  const [arrivals, setArrivals] = useState([]);
  const [arrivalsLoading, setArrivalsLoading] = useState(false);
  const [arrivalsError, setArrivalsError] = useState('');
  const [arrivalsStatusFilter, setArrivalsStatusFilter] = useState('all');
  const [arrivalsSourceFilter, setArrivalsSourceFilter] = useState('');
  const [arrivalsPaymentFilter, setArrivalsPaymentFilter] = useState('');
  const [noShowBusy, setNoShowBusy] = useState(null); // reservation_id currently being marked
  const [rackFloorFilter, setRackFloorFilter] = useState('');
  const [rackBlockFilter, setRackBlockFilter] = useState('');
  const [rackRoomTypeFilter, setRackRoomTypeFilter] = useState('');
  const [rackStatusFilter, setRackStatusFilter] = useState('');
  const [rackFloors, setRackFloors] = useState([]);
  const [rackBlocks, setRackBlocks] = useState([]);
  const [rackRoomTypes, setRackRoomTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryDetail, setSummaryDetail] = useState(null); // { type, label, color, rows, loading }
  const [rack, setRack] = useState([]);
  // REQ-06 — Express one-click eligibility maps: { checkin: {room_id: {...}}, checkout: {stay_id: {...}} }
  const [expressElig, setExpressElig] = useState({ checkin: {}, checkout: {} });
  const [expressBusy, setExpressBusy] = useState(false);
  const [matrix, setMatrix] = useState([]);
  const [matrixByRoomType, setMatrixByRoomType] = useState({});
  const [matrixRoomTypeFilter, setMatrixRoomTypeFilter] = useState('ALL');
  const [forecastMap, setForecastMap] = useState({}); // ER-MOM-08: date -> AI occupancy forecast overlay
  const [snapshotFrom, setSnapshotFrom] = useState(toIsoDate(new Date()));
  const [snapshotTo, setSnapshotTo] = useState(toIsoDate(addDays(new Date(), 9)));
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [changeTargetRoomId, setChangeTargetRoomId] = useState('');
  // REQ-19 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md §14) — optional occupancy
  // change riding along with the room move. Empty = leave occupancy
  // unchanged (the original Change Room behavior).
  const [changeTargetAdults, setChangeTargetAdults] = useState('');
  const [changeTargetChildren, setChangeTargetChildren] = useState('');
  const [changeRoomMessage, setChangeRoomMessage] = useState('');
  // Shown INSIDE the "Room X — Actions" popup itself, not the page-level
  // banner behind it — a Change Room failure (e.g. "Target room is not
  // vacant") was easy to miss sitting behind the still-open modal.
  const [changeRoomError, setChangeRoomError] = useState('');
  const [swapRoomError, setSwapRoomError] = useState('');
  const [swapTargetStayId, setSwapTargetStayId] = useState('');
  const [roomFolio, setRoomFolio] = useState(null);
  const [roomFolioLoading, setRoomFolioLoading] = useState(false);
  const [roomFolioError, setRoomFolioError] = useState('');
  const [activeFolioId, setActiveFolioId] = useState(null); // currently selected folio tab
  const [checkInModal, setCheckInModal] = useState(null);
  const [checkInForm, setCheckInForm] = useState({ checkin_datetime: '', checkout_datetime: '', deposit_amount: '', deposit_remarks: '' });
  const [checkInOptions, setCheckInOptions] = useState([]);
  const [checkInSelectionId, setCheckInSelectionId] = useState('');
  const [checkInSelection, setCheckInSelection] = useState(null);
  const [checkInBusy, setCheckInBusy] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInError, setCheckInError] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);

  // Compliance step state for check-in modal
  const [checkInStep, setCheckInStep] = useState(1);
  const [checkInComplianceData, setCheckInComplianceData] = useState(buildDefaultComplianceData(selectedProperty?.country));
  const [checkInFieldRules, setCheckInFieldRules] = useState([]);
  const [checkInPolicyConfig, setCheckInPolicyConfig] = useState({});
  const [checkInCountryOptions, setCheckInCountryOptions] = useState([]);
  const [checkInIdTypeOptions, setCheckInIdTypeOptions] = useState([]);
  const [checkInComplianceErrors, setCheckInComplianceErrors] = useState({});
  const [checkInOverrideMode, setCheckInOverrideMode] = useState(false);
  const [checkInOverrideReason, setCheckInOverrideReason] = useState('');
  // ── Upsell Engine (ER-MOM-06) — coverage-aware offers shown at check-in ──
  const [upsellOffers, setUpsellOffers] = useState([]);
  const [upsellCtx, setUpsellCtx] = useState(null);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [upsellAddedIds, setUpsellAddedIds] = useState([]);
  const [upsellBusyId, setUpsellBusyId] = useState(null);
  const [upsellError, setUpsellError] = useState('');
  // Room-upgrade offers (ER-MOM-06) — available higher room types priced as a delta.
  const [upgradeOffers, setUpgradeOffers] = useState([]);
  const [upgradeBusyId, setUpgradeBusyId] = useState(null);
  const [upgradeDone, setUpgradeDone] = useState(null);

  const [coModal, setCoModal] = useState(null);   // checkout modal room data
  const [coFolio, setCoFolio] = useState(null);   // { stay details, folio, lines }
  const [coPolicy, setCoPolicy] = useState(null); // property config
  const [coForm, setCoForm] = useState({
    actual_checkout_datetime: '',
    late_checkout_charge: 0,
    checkout_scope: 'MASTER_ONLY',
    settlement_mode: 'CASH',
    settlement_corporate_id: '',
    settlement_travel_agent_id: '',
    billing_profile_id: '',
    payment_received: false,
    payment_amount: '',
    payment_collected_mode: '',
    payment_reference_no: '',
    payment_remarks: '',
    split_payment: false,
    payment_lines: [{ mode: 'CASH', amount: '', reference_no: '' }],
    customer_gstin: '',
    customer_company: ''
  });
  const [coBusy, setCoBusy] = useState(false);
  const [coLoading, setCoLoading] = useState(false);
  const [coError, setCoError] = useState('');
  const [coResult, setCoResult] = useState(null); // invoice after checkout
  // Read Card modal — diagnoses an arbitrary card on any online encoder.
  const [lockReadModalOpen, setLockReadModalOpen] = useState(false);
  // Encode Card modal — issue/re-issue a key card for the active room's stay.
  const [lockEncodeModal, setLockEncodeModal] = useState(null);
  const [coBillingProfiles, setCoBillingProfiles] = useState([]);
  const [coBillingLoading, setCoBillingLoading] = useState(false);
  const [coBillingError, setCoBillingError] = useState('');
  // Settlement-entity pickers (populated when the operator chooses
  // settlement_mode = CORPORATE / TRAVEL_AGENT). Loaded alongside the folio.
  const [coCorporates, setCoCorporates] = useState([]);
  const [coTravelAgents, setCoTravelAgents] = useState([]);

  const [invoiceModal, setInvoiceModal] = useState(null); // full invoice with lines
  const [invoiceSplitTab, setInvoiceSplitTab] = useState(0); // 0 = All, 1/2/... = specific split
  const [defaultCheckoutTime, setDefaultCheckoutTime] = useState('11:00');
  const [hkUpdatingRoomId, setHkUpdatingRoomId] = useState('');
  const [hkStaff, setHkStaff] = useState([]);
  const [hkStaffPickRoom, setHkStaffPickRoom] = useState(null); // { room_id, room_number }
  const [hkSelectedStaff, setHkSelectedStaff] = useState('');
  const [hkRemarks, setHkRemarks] = useState('');
  const [hkRoomLog, setHkRoomLog] = useState([]);
  const [hkRequests, setHkRequests] = useState([]);
  const [checkoutRequests, setCheckoutRequests] = useState([]);
  const [noshowScores, setNoshowScores] = useState([]); // AI no-show risk scores
  const [aiHkScores, setAiHkScores] = useState([]); // AI housekeeping priority scores
  const [showPriorityReqForm, setShowPriorityReqForm] = useState(false);
  const [prReqForm, setPrReqForm] = useState({ room_id: '', request_type: 'EXPRESS_CLEANING', priority: 'EXPRESS', remarks: '' });

  // Room block confirmation modal
  const [blockModal, setBlockModal] = useState(null); // { room_id, room_number }
  // Undo Check-In (REQ-13, docs/SRS_AMBERPARAMOUNT_REQ08-13.md)
  const [undoCheckinModal, setUndoCheckinModal] = useState(null); // { reservation_id, room_number, guest_name }
  const [undoCheckinReason, setUndoCheckinReason] = useState('');
  const [undoCheckinBusy, setUndoCheckinBusy] = useState(false);
  const [undoCheckinError, setUndoCheckinError] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockType, setBlockType] = useState('MAINTENANCE');
  // Dated block window — required so the CM push covers only these days,
  // not the rate-plan validity horizon (~365 days), which EaseRoom rejects.
  const [blockStartDate, setBlockStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [blockEndDate, setBlockEndDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); });

  // --- Folio Posting Modal state ---
  const [postingModal, setPostingModal] = useState(null); // { stay_id, folio_id, folio_label }
  const [postingForm, setPostingForm] = useState({
    service_key: '',
    posting_type: 'MANUAL',
    source_module: 'FRONT_OFFICE',
    allowance_mode: 'PERCENT',
    allowance_percent: '',
    allowance_base_amount: '',
    tax_profile_id: '',
    debit_amount: '',
    credit_amount: '',
    revenue_code: '',
    revenue_description: '',
    tax_mode: 'EXCLUSIVE', // 'EXCLUSIVE' (amount + GST on top, default) | 'INCLUSIVE' (amount already includes GST)
    gst_percent: '',
    cgst_amount: '',
    sgst_amount: '',
    igst_amount: '',
    tax_amount: '',
    reference_type: '',
    reference_id: '',
    remarks: '',
    payment_mode: 'CASH'
  });
  const [postingBusy, setPostingBusy] = useState(false);
  const [postingError, setPostingError] = useState('');
  const [postingRoutingMsg, setPostingRoutingMsg] = useState(null); // { routed_amount, target_stay_id } shown in folio area after routing
  const [postingTaxProfiles, setPostingTaxProfiles] = useState([]);
  const [postingServiceCatalog, setPostingServiceCatalog] = useState({ master_services: [], ledger_accounts: [] });

  // --- Split Folio Modal state ---
  const [splitModal, setSplitModal] = useState(null); // { stay_id, folio_id, folio_label, move_lines, split_lines, excluded_count }
  const [splitMode, setSplitMode] = useState('MOVE_LINES'); // 'MOVE_LINES' | 'SET_SPLIT_NO'
  const [splitSelectedLines, setSplitSelectedLines] = useState(new Set());
  const [splitNoDraftByLedger, setSplitNoDraftByLedger] = useState({});
  const [splitNoBaseByLedger, setSplitNoBaseByLedger] = useState({});
  const [splitTargetAction, setSplitTargetAction] = useState('CREATE_NEW'); // 'CREATE_NEW' | 'EXISTING_FOLIO'
  const [splitTargetFolioId, setSplitTargetFolioId] = useState('');
  const [splitTargetLabel, setSplitTargetLabel] = useState('');
  const [splitStayFolios, setSplitStayFolios] = useState([]); // all folios for the stay (for target dropdown)
  const [splitFoliosLoading, setSplitFoliosLoading] = useState(false);
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitError, setSplitError] = useState('');

  // --- Route Folio Modal state ---
  const [routeModal, setRouteModal] = useState(null); // { stay_id, folio_id, folio_label }
  const [routeForm, setRouteForm] = useState({
    route_to: 'SELF',
    party_id: '',
    amount: '',
    reference_no: '',
    remarks: '',
    auto_transfer: true
  });
  const [routeBusy, setRouteBusy] = useState(false);
  const [routeError, setRouteError] = useState('');

  // --- City Ledger Transfer Modal state ---
  const [transferModal, setTransferModal] = useState(null); // { stay_id, folio_id, folio_label }
  const [transferForm, setTransferForm] = useState({
    party_type: 'CORPORATE',
    party_id: '',
    amount: '',
    reference_no: '',
    remarks: ''
  });
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState('');

  // --- Guest Transfer Modal state ---
  const [guestTransferModal, setGuestTransferModal] = useState(null); // { stay_id, folio_id, folio_label, room_number, lines }
  const [guestTransferSelected, setGuestTransferSelected] = useState(new Set());
  const [guestTransferTargetStayId, setGuestTransferTargetStayId] = useState('');
  const [guestTransferRemarks, setGuestTransferRemarks] = useState('');
  const [guestTransferBusy, setGuestTransferBusy] = useState(false);
  const [guestTransferError, setGuestTransferError] = useState('');
  const [guestTransferSuccess, setGuestTransferSuccess] = useState('');

  // --- Rate History Modal state (Hotel Admin only) ---
  const [rateHistoryModal, setRateHistoryModal] = useState(null); // { reservation_id }
  const [rateHistoryRows, setRateHistoryRows] = useState([]);
  const [rateHistoryLoading, setRateHistoryLoading] = useState(false);
  const [rateHistoryError, setRateHistoryError] = useState('');

  // --- Linked Rooms Modal state ---
  const [linkedRoomsModal, setLinkedRoomsModal] = useState(null); // { stay_id, stay_no }
  const [linkTab, setLinkTab] = useState('LINKED_STAYS'); // 'LINKED_STAYS' | 'ROUTING' | 'AUDIT'
  // Linked Stays tab
  const [linkGroup, setLinkGroup] = useState(null);
  const [linkMembers, setLinkMembers] = useState([]);
  const [linkGroupLoading, setLinkGroupLoading] = useState(false);
  const [linkGroupError, setLinkGroupError] = useState('');
  const [linkTargetStayId, setLinkTargetStayId] = useState('');
  const [linkReason, setLinkReason] = useState('');
  const [unlinkMode, setUnlinkMode] = useState('STOP_FUTURE_ONLY');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState('');
  // Routing Rules tab
  const [routingRules, setRoutingRules] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState('');
  const [newRoute, setNewRoute] = useState({ from_stay_id: '', to_stay_id: '', routing_mode: 'ALL_CHARGES', valid_from: '', valid_to: '', percentage: '', transaction_code: '', precedence_rank: '', reason: '' });
  const [newRouteBusy, setNewRouteBusy] = useState(false);
  const [newRouteError, setNewRouteError] = useState('');
  const [editRoute, setEditRoute] = useState(null); // { routing_rule_id, routing_mode, valid_from, valid_to, percentage, transaction_code, precedence_rank, reason }
  const [editRouteBusy, setEditRouteBusy] = useState(false);
  const [editRouteError, setEditRouteError] = useState('');
  const [routeCloseBusy, setRouteCloseBusy] = useState('');
  const [routeCloseError, setRouteCloseError] = useState('');
  // Audit Log tab
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [reversalBusy, setReversalBusy] = useState(''); // entity_id (txn) being reversed
  const [reversalError, setReversalError] = useState('');

  const [corporates, setCorporates] = useState([]);
  const [travelAgents, setTravelAgents] = useState([]);

  const [checkoutRoundOffEnabled, setCheckoutRoundOffEnabled] = useState(false);
  // Front Office dashboard alert banner visibility — property-wide, set on
  // Hotel Config > PMS Policy Configuration. Default on until the fetch below
  // resolves (or for a property with no explicit setting saved yet).
  const [showLateCheckoutAlert, setShowLateCheckoutAlert] = useState(true);
  const [showNightAuditAlert, setShowNightAuditAlert] = useState(true);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  useEffect(() => {
    const stayId = activeRoom?.stay_id;
    const isOccupied = String(activeRoom?.occupancy_status || '').toUpperCase() === 'OCCUPIED';
    if (!stayId || !isOccupied) {
      setRoomFolio(null);
      setRoomFolioError('');
      setRoomFolioLoading(false);
      setActiveFolioId(null);
      return;
    }
    let isMounted = true;
    setRoomFolioLoading(true);
    setRoomFolioError('');
    setPostingRoutingMsg(null);
    setActiveFolioId(null);
    fetch(`/api/v1/pms/stays/${stayId}/folio`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!isMounted) return;
        if (!ok) throw new Error(data?.error || 'Failed to load guest folio');
        setRoomFolio(data);
        setActiveFolioId(data?.folio?.folio_id || null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setRoomFolio(null);
        setRoomFolioError(err?.message || 'Failed to load guest folio');
      })
      .finally(() => {
        if (!isMounted) return;
        setRoomFolioLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [activeRoom?.stay_id, activeRoom?.occupancy_status]);

  // Load housekeeping log when room detail panel opens
  useEffect(() => {
    if (!activeRoom?.room_id) { setHkRoomLog([]); return; }
    (async () => {
      try {
        const res = await fetch(`/api/v1/pms/housekeeping/log?room_id=${activeRoom.room_id}&limit=5`, { headers: headers() });
        const data = await res.json().catch(() => []);
        setHkRoomLog(Array.isArray(data) ? data : []);
      } catch (_) { setHkRoomLog([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom?.room_id]);

  // Checkout Auto Round Off toggle — whether Amount To Collect (and the
  // checkout payment-match requirement) rounds to the nearest whole rupee.
  useEffect(() => {
    if (!selectedProperty?.id) {
      setCheckoutRoundOffEnabled(false);
      return;
    }
    let isMounted = true;
    fetch('/api/v1/pms/property-config/checkout-auto-round-off', { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!isMounted) return;
        setCheckoutRoundOffEnabled(ok && data?.checkout_auto_round_off_enabled === true);
      })
      .catch(() => {
        if (!isMounted) return;
        setCheckoutRoundOffEnabled(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedProperty?.id, token]);

  // Front Office dashboard alert banner visibility (property-wide).
  useEffect(() => {
    if (!selectedProperty?.id) {
      setShowLateCheckoutAlert(true);
      setShowNightAuditAlert(true);
      return;
    }
    let isMounted = true;
    fetch('/api/v1/pms/property-config/dashboard-alert-visibility', { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!isMounted || !ok) return;
        setShowLateCheckoutAlert(data?.show_late_checkout_alert !== false);
        setShowNightAuditAlert(data?.show_night_audit_alert !== false);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [selectedProperty?.id, token]);

  useEffect(() => {
    if (!selectedProperty?.id) {
      setPostingTaxProfiles([]);
      return;
    }
    let isMounted = true;
    fetch('/api/v1/pms/tax-profiles', { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!isMounted) return;
        if (!ok) {
          setPostingTaxProfiles([]);
          return;
        }
        setPostingTaxProfiles(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setPostingTaxProfiles([]);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedProperty?.id, token]);

  useEffect(() => {
    if (!selectedProperty?.id) {
      setPostingServiceCatalog({ master_services: [], ledger_accounts: [] });
      return;
    }
    let isMounted = true;
    fetch('/api/v1/pms/folio-posting/catalog', { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!isMounted) return;
        if (!ok) {
          setPostingServiceCatalog({ master_services: [], ledger_accounts: [] });
          return;
        }
        setPostingServiceCatalog({
          master_services: Array.isArray(data?.master_services) ? data.master_services : [],
          ledger_accounts: Array.isArray(data?.ledger_accounts) ? data.ledger_accounts : []
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setPostingServiceCatalog({ master_services: [], ledger_accounts: [] });
      });
    return () => {
      isMounted = false;
    };
  }, [selectedProperty?.id, token]);

  useEffect(() => {
    if (!selectedProperty?.id) {
      setCorporates([]);
      setTravelAgents([]);
      return;
    }
    let isMounted = true;
    Promise.all([
      fetch('/api/v1/pms/corporates?all=1', { headers: headers() }),
      fetch('/api/v1/pms/travel-agents?all=1', { headers: headers() })
    ])
      .then(async ([corpRes, taRes]) => {
        const corpData = await corpRes.json().catch(() => ([]));
        const taData = await taRes.json().catch(() => ([]));
        if (!isMounted) return;
        setCorporates(corpRes.ok && Array.isArray(corpData) ? corpData : []);
        setTravelAgents(taRes.ok && Array.isArray(taData) ? taData : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setCorporates([]);
        setTravelAgents([]);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedProperty?.id, token]);

  // Load compliance field rules whenever property changes
  useEffect(() => {
    if (!selectedProperty?.id) return;
    Promise.all([
      fetch('/api/v1/compliance/fields', { headers: headers() }),
      fetch('/api/v1/compliance/policy/guest-identity-document-rules', { headers: headers() }),
      fetch('/api/v1/core/countries', { headers: headers() }),
      fetch('/api/v1/pms/id-types', { headers: headers() })
    ])
      .then(async ([fieldsRes, policyRes, countriesRes, idTypesRes]) => {
        const [fieldsData, policyData, countriesData, idTypesData] = await Promise.all([
          fieldsRes.json().catch(() => []),
          policyRes.json().catch(() => ({})),
          countriesRes.json().catch(() => []),
          idTypesRes.json().catch(() => [])
        ]);
        if (Array.isArray(fieldsData)) setCheckInFieldRules(fieldsData);
        if (policyRes.ok && policyData && typeof policyData === 'object') {
          setCheckInPolicyConfig(policyData);
        } else {
          setCheckInPolicyConfig({});
        }
        if (Array.isArray(countriesData)) {
          setCheckInCountryOptions(countriesData);
        } else {
          setCheckInCountryOptions([]);
        }
        setCheckInIdTypeOptions(Array.isArray(idTypesData) ? idTypesData : []);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id, token]);

  const load = async () => {
    if (!selectedProperty?.id) {
      setSummary(null);
      setRack([]);
      setMatrix([]);
      setMatrixByRoomType({});
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rackParams = new URLSearchParams();
      rackParams.set('date', date);
      if (rackFloorFilter.trim()) rackParams.set('floor_no', rackFloorFilter.trim());
      if (rackBlockFilter.trim()) rackParams.set('block_name', rackBlockFilter.trim());
      if (rackRoomTypeFilter) rackParams.set('room_type_id', rackRoomTypeFilter);
      const [sumRes, rackRes, hkReqRes, coReqRes] = await Promise.all([
        fetch(`/api/v1/pms/command-center/summary?date=${encodeURIComponent(date)}`, { headers: headers() }),
        fetch(`/api/v1/pms/room-rack/live?${rackParams.toString()}`, { headers: headers() }),
        fetch('/api/v1/pms/housekeeping/requests?status=', { headers: headers() }).catch(() => null),
        fetch('/api/v1/pms/checkout-requests', { headers: headers() }).catch(() => null)
      ]);
      const [sumParsed, rackParsed] = await Promise.all([
        parseApiResponse(sumRes),
        parseApiResponse(rackRes)
      ]);
      if (!sumParsed.ok) throw new Error(sumParsed.data?.error || sumParsed.text || 'Failed to load summary');
      if (!rackParsed.ok) throw new Error(rackParsed.data?.error || rackParsed.text || 'Failed to load room rack');
      setSummary(sumParsed.data || null);
      setRack(Array.isArray(rackParsed.data) ? rackParsed.data : []);
      loadSnapshot();
      // REQ-06 — Express eligibility (silent; a failure just hides the ⚡ affordance)
      try {
        const exRes = await fetch(`/api/v1/pms/express/eligibility?date=${encodeURIComponent(date)}`, { headers: headers() });
        if (exRes.ok) {
          const exData = await exRes.json();
          setExpressElig({ checkin: exData.checkin || {}, checkout: exData.checkout || {} });
        } else {
          setExpressElig({ checkin: {}, checkout: {} });
        }
      } catch (_) { setExpressElig({ checkin: {}, checkout: {} }); }
      // Load active housekeeping requests for priority badges
      if (hkReqRes && hkReqRes.ok) {
        const reqData = await hkReqRes.json().catch(() => []);
        setHkRequests(Array.isArray(reqData) ? reqData.filter(r => ['OPEN', 'IN_PROGRESS'].includes(r.request_status)) : []);
      }
      if (coReqRes && coReqRes.ok) {
        const coData = await coReqRes.json().catch(() => []);
        setCheckoutRequests(Array.isArray(coData) ? coData.filter(r => ['PENDING', 'ACKNOWLEDGED'].includes(r.status)) : []);
      }
      // Load AI no-show risk scores (silent — does not block UI if feature is off)
      try {
        const nsRes = await fetch('/api/v1/ai/noshow-predictor/scores', { headers: headers() });
        if (nsRes.ok) {
          const nsData = await nsRes.json();
          setNoshowScores(nsData.scores || []);
        }
      } catch (_) { /* feature may be disabled — ignore */ }
      // Load AI housekeeping priority scores (silent)
      try {
        const hkAiRes = await fetch('/api/v1/ai/housekeeping-optimizer/scores', { headers: headers() });
        if (hkAiRes.ok) {
          const hkAiData = await hkAiRes.json();
          setAiHkScores(hkAiData.scores || []);
        }
      } catch (_) { /* feature may be disabled — ignore */ }
    } catch (err) {
      setError(err?.message || 'Failed to load command center');
    } finally {
      setLoading(false);
    }
  };

  const loadSummaryDetail = async (type, label, color) => {
    if (!selectedProperty?.id) return;
    setSummaryDetail({ type, label, color, rows: [], loading: true });
    try {
      const res = await fetch(`/api/v1/pms/command-center/summary-detail?date=${encodeURIComponent(date)}&type=${type}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load details');
      setSummaryDetail({ type, label, color, rows: data.rows || [], loading: false });
    } catch (err) {
      setSummaryDetail(prev => prev ? { ...prev, loading: false, error: err.message } : null);
    }
  };

  const loadArrivals = async () => {
    if (!selectedProperty?.id) { setArrivals([]); return; }
    setArrivalsLoading(true);
    setArrivalsError('');
    try {
      const res = await fetch(`/api/v1/pms/command-center/arrivals?date=${encodeURIComponent(date)}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load arrivals');
      setArrivals(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setArrivalsError(err?.message || 'Failed to load arrivals');
      setArrivals([]);
    } finally {
      setArrivalsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ARRIVALS') loadArrivals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, date, selectedProperty?.id]);

  const markNoShow = async (reservationId) => {
    if (!reservationId || !window.confirm('Mark this reservation as No Show? The room stays available for the rest of the day.')) return;
    setNoShowBusy(reservationId);
    try {
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}/status`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'NO_SHOW' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to mark No Show');
      loadArrivals();
    } catch (err) {
      setArrivalsError(err?.message || 'Failed to mark No Show');
    } finally {
      setNoShowBusy(null);
    }
  };

  const loadSnapshot = async (fromOverride, toOverride) => {
    if (!selectedProperty?.id) return;
    const sf = fromOverride || snapshotFrom;
    const st = toOverride || snapshotTo;
    setSnapshotLoading(true);
    try {
      const matRes = await fetch(`/api/v1/pms/availability/matrix?from=${encodeURIComponent(sf)}&to=${encodeURIComponent(st)}`, { headers: headers() });
      const matParsed = await parseApiResponse(matRes);
      if (!matParsed.ok) throw new Error(matParsed.data?.error || matParsed.text || 'Failed to load availability matrix');
      const matData = matParsed.data;
      setMatrix(Array.isArray(matData) ? matData : Array.isArray(matData?.totals) ? matData.totals : []);
      setMatrixByRoomType(matData?.by_room_type || {});

      // ER-MOM-08: overlay AI occupancy forecast onto the calendar.
      // Silent-fail: if the feature is off or no forecast exists, show no overlay.
      try {
        const fcRes = await fetch(`/api/v1/ai/occupancy-forecast/calendar-overlay?from=${encodeURIComponent(sf)}&to=${encodeURIComponent(st)}`, { headers: headers() });
        if (fcRes.ok) {
          const fcData = await fcRes.json();
          const map = {};
          (fcData.forecasts || []).forEach(f => { if (f.forecast_date) map[f.forecast_date] = f; });
          setForecastMap(map);
        } else {
          setForecastMap({});
        }
      } catch (_) { setForecastMap({}); }
    } catch (err) {
      setError(err?.message || 'Failed to load snapshot');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const runRoomAction = async (action, room, reason) => {
    if (!room?.room_id) return;
    setError('');
    try {
      const url = action === 'block'
        ? `/api/v1/pms/rooms/${room.room_id}/block`
        : `/api/v1/pms/rooms/${room.room_id}/clear`;
      const body = action === 'block'
        ? {
            block_reason: reason?.block_reason || '',
            block_notes: reason?.block_notes || '',
            block_start_date: reason?.block_start_date || null,
            block_end_date: reason?.block_end_date || null,
          }
        : {};
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Room action failed');
      setActiveRoom(null);
      setBlockModal(null);
      setBlockReason('');
      setBlockType('MAINTENANCE');
      const t = new Date().toISOString().slice(0, 10);
      const tm = new Date(); tm.setDate(tm.getDate() + 1);
      setBlockStartDate(t);
      setBlockEndDate(tm.toISOString().slice(0, 10));
      load();
    } catch (err) {
      setError(err?.message || 'Room action failed');
    }
  };

  const submitUndoCheckin = async () => {
    if (!undoCheckinModal?.reservation_id) return;
    if (!undoCheckinReason.trim()) { setUndoCheckinError('Please enter a reason — Undo Check-In is audited.'); return; }
    setUndoCheckinBusy(true); setUndoCheckinError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${undoCheckinModal.reservation_id}/undo-checkin`, {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: undoCheckinReason.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Undo Check-In failed');
      setUndoCheckinModal(null);
      setUndoCheckinReason('');
      setActiveRoom(null);
      load();
      if (data.warning) window.alert(data.warning);
    } catch (err) {
      setUndoCheckinError(err?.message || 'Undo Check-In failed');
    } finally {
      setUndoCheckinBusy(false);
    }
  };

  const openCheckInModal = (room) => {
    if (!room?.room_id) return;
    const occ = String(room.occupancy_status || '').toUpperCase();
    const maint = String(room.maintenance_status || '').toUpperCase();
    if (room?.stay_id || occ === 'OCCUPIED' || ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint)) return;
    setCheckInModal(room);
    setCheckInOptions([]);
    setCheckInSelectionId('');
    setCheckInSelection(null);
    setCheckInForm({ checkin_datetime: '', checkout_datetime: '', deposit_amount: '', deposit_remarks: '' });
    setCheckInError('');
    setCheckInResult(null);
    setCheckInStep(1);
    setCheckInComplianceData(buildDefaultComplianceData(selectedProperty?.country));
    setCheckInComplianceErrors({});
    setCheckInOverrideMode(false);
    setCheckInOverrideReason('');
    setCheckInLoading(true);
    setActiveRoom(null);
    fetch(`/api/v1/pms/rooms/${room.room_id}/eligible-checkins?date=${encodeURIComponent(date)}`, { headers: headers() })
      .then(async (res) => {
        const data = await res.json().catch(() => ([]));
        if (!res.ok) throw new Error(data?.error || 'Failed to load eligible reservations');
        const rows = Array.isArray(data) ? data : [];
        setCheckInOptions(rows);
        if (!rows.length) {
          setCheckInError('No eligible CONFIRMED/GUARANTEED reservations found for this room/date.');
          return;
        }
        const selected = rows[0];
        const serverNow = res.headers.get('date') ? new Date(res.headers.get('date')) : new Date();
        const depDate = selected.departure_date ? String(selected.departure_date).slice(0, 10) : toIsoDate(serverNow);
        setCheckInSelectionId(String(selected.reservation_id));
        setCheckInSelection(selected);
        const coTime1 = defaultCheckoutTime || '11:00';
        const proposedCo1 = new Date(`${depDate}T${coTime1}`);
        const coDate1 = proposedCo1 <= serverNow ? toIsoDate(new Date(proposedCo1.getTime() + 86400000)) : depDate;
        setCheckInForm({
          checkin_datetime: toDateTimeLocalValue(serverNow),
          checkout_datetime: `${coDate1}T${coTime1}`,
          deposit_amount: '',
          deposit_remarks: ''
        });
        loadCompliancePrefill(selected.reservation_id);
      })
      .catch((err) => setCheckInError(err?.message || 'Failed to load eligible reservations'))
      .finally(() => setCheckInLoading(false));
  };

  // Pre-fill compliance form from saved guest_master values for the selected
  // reservation. Without this, opening Check-In always shows blank fields even
  // when gender/dob/id_proof were captured earlier (e.g. at reservation time).
  const loadCompliancePrefill = async (reservationId) => {
    if (!reservationId) return;
    try {
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}`, { headers: headers() });
      if (!res.ok) return;
      const data = await res.json();
      const fallback = buildDefaultComplianceData(selectedProperty?.country);
      setCheckInComplianceData({
        ...fallback,
        guest_name: data.primary_guest_name || fallback.guest_name || '',
        gender: data.gender || '',
        dob: data.dob ? String(data.dob).slice(0, 10) : '',
        nationality: data.nationality || fallback.nationality || '',
        is_foreign_guest: Boolean(data.is_foreign_guest),
        address: data.address || '',
        purpose_of_visit: data.purpose_of_visit || '',
        id_proof_type: data.id_proof_type || '',
        id_proof_no: data.id_proof_no || '',
        passport_number: data.passport_number || '',
        visa_number: data.visa_number || '',
        visa_expiry_date: data.visa_expiry_date ? String(data.visa_expiry_date).slice(0, 10) : '',
        arrival_stamp_date: data.arrival_stamp_date ? String(data.arrival_stamp_date).slice(0, 10) : '',
        date_of_arrival_in_india: data.date_of_arrival_in_india ? String(data.date_of_arrival_in_india).slice(0, 10) : '',
        place_of_issue: data.place_of_issue || '',
      });
    } catch { /* leave defaults — user can fill manually */ }
  };

  const onCheckInSelectionChange = (reservationId) => {
    const selected = checkInOptions.find((row) => String(row.reservation_id) === String(reservationId)) || null;
    setCheckInSelectionId(String(reservationId || ''));
    setCheckInSelection(selected);
    if (!selected) return;
    const depDate = selected.departure_date ? String(selected.departure_date).slice(0, 10) : toIsoDate(new Date());
    const coTime2 = defaultCheckoutTime || '11:00';
    const proposedCo2 = new Date(`${depDate}T${coTime2}`);
    const coDate2 = proposedCo2 <= new Date() ? toIsoDate(new Date(proposedCo2.getTime() + 86400000)) : depDate;
    setCheckInForm((f) => ({ ...f, checkout_datetime: `${coDate2}T${coTime2}` }));
    loadCompliancePrefill(selected.reservation_id);
  };

  const validateComplianceFields = () => {
    const errs = {};
    const hotelCountry = selectedProperty?.country || 'India';
    const normalizedHotelCountry = normalizeCountry(hotelCountry);
    const normalizedNationality = normalizeCountry(checkInComplianceData.nationality);
    const isForeign = Boolean(checkInComplianceData.is_foreign_guest) || (
      normalizedNationality
        ? (normalizedHotelCountry ? normalizedNationality !== normalizedHotelCountry : (normalizedNationality !== 'india' && normalizedNationality !== 'indian'))
        : false
    );
    checkInFieldRules.forEach((r) => {
      if (!r.is_mandatory || !r.is_visible) return;
      if (r.applies_to === 'FOREIGN' && !isForeign) return;
      if (r.applies_to === 'INDIAN' && isForeign) return;
      // Domestic ID fields are hidden in the UI for foreign guests — skip validation too
      if (isForeign && (r.field_name === 'id_proof_type' || r.field_name === 'id_proof_no')) return;
      const val = checkInComplianceData[r.field_name];
      if (!val || String(val).trim() === '') {
        errs[r.field_name] = `${r.label || r.field_name} is required`;
      }
    });
    // Expiry dates must be today or later — an already-expired document is invalid.
    const todayStr = new Date().toISOString().slice(0, 10);
    if (isForeign) {
      if (checkInComplianceData.visa_expiry_date && checkInComplianceData.visa_expiry_date < todayStr) {
        errs.visa_expiry_date = 'Visa Expiry Date cannot be in the past';
      }
    } else if (checkInComplianceData.id_expiry_date && checkInComplianceData.id_expiry_date < todayStr) {
      errs.id_expiry_date = 'ID Expiry Date cannot be in the past';
    }
    return errs;
  };

  const goToComplianceStep = () => {
    if (!checkInSelection?.reservation_id) {
      setCheckInError('Please select a reservation first.');
      return;
    }
    setCheckInError('');
    setCheckInStep(2);
  };

  const submitCheckIn = async () => {
    if (!checkInSelection?.reservation_id) {
      setCheckInError('Please select reservation to check-in');
      return;
    }
    if (!checkInOverrideMode) {
      const errs = validateComplianceFields();
      if (Object.keys(errs).length > 0) {
        setCheckInComplianceErrors(errs);
        setCheckInStep(2);
        return;
      }
    }
    setCheckInComplianceErrors({});
    setCheckInBusy(true);
    setCheckInError('');
    setCheckInResult(null);
    try {
      const payload = { room_id: checkInModal.room_id };
      // Tell the server which booked room line this is, so a multi-room
      // reservation checks in the right line instead of always the first.
      if (checkInSelection?.line_id) payload.line_id = checkInSelection.line_id;
      if (checkInForm.checkin_datetime) payload.checkin_datetime = dateTimeLocalToIso(checkInForm.checkin_datetime);
      if (checkInForm.checkout_datetime) payload.checkout_datetime = dateTimeLocalToIso(checkInForm.checkout_datetime);
      if (Number(checkInForm.deposit_amount) > 0) {
        payload.deposit_amount = Number(checkInForm.deposit_amount);
        payload.deposit_remarks = checkInForm.deposit_remarks || 'Advance at check-in';
      }
      // When override is on, blank pre-filled compliance fields (returning-guest leak)
      // before they hit the check-in payload. Backend /compliance/override stamps the
      // reservation header so the police report masks any field that might survive.
      const COMPLIANCE_FIELDS_TO_BLANK = [
        'address', 'purpose_of_visit',
        'id_proof_type', 'id_proof_no', 'id_expiry_date',
        'passport_number', 'visa_number', 'visa_expiry_date',
        'arrival_stamp_date', 'date_of_arrival_in_india', 'place_of_issue',
        'id_scan_data', 'id_scan_name', 'passport_scan_data', 'passport_scan_name',
        'cform_submission_status'
      ];
      const cleanedCompliance = checkInOverrideMode
        ? Object.fromEntries(Object.entries(checkInComplianceData).map(([k, v]) =>
            [k, COMPLIANCE_FIELDS_TO_BLANK.includes(k) ? null : v]))
        : checkInComplianceData;
      Object.assign(payload, cleanedCompliance);
      if (checkInOverrideMode && checkInOverrideReason.trim()) {
        fetch('/api/v1/compliance/override', {
          method: 'POST',
          headers: { ...headers(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ reservation_id: checkInSelection.reservation_id, override_reason: checkInOverrideReason })
        }).catch(() => {});
      }
      const res = await fetch(`/api/v1/pms/reservations/${checkInSelection.reservation_id}/checkin`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Check-in failed');
      setCheckInResult(data);
      load();
      loadUpsell(checkInSelection.reservation_id);
    } catch (err) {
      setCheckInError(err?.message || 'Check-in failed');
    } finally {
      setCheckInBusy(false);
    }
  };

  // REQ-06 — Express one-click Check-In. Fast path over /reservations/:id/checkin:
  // no compliance wizard (guard already confirmed it's captured), no deposit prompt.
  // Guards live server-side too; on any rejection (check-in window, vacancy race)
  // we surface the message and the operator falls back to the full "Resv Check-In".
  const expressCheckIn = async (room) => {
    if (!room?.reservation_id || !room?.room_id || expressBusy) return;
    const elig = expressElig.checkin?.[room.room_id];
    if (!elig?.eligible) return;
    if (!window.confirm(`Express check-in ${room.primary_guest_name || 'guest'} into Room ${room.room_number}?`)) return;
    setExpressBusy(true);
    try {
      const res = await fetch(`/api/v1/pms/reservations/${room.reservation_id}/checkin`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_id: room.room_id, checkin_datetime: new Date().toISOString() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Express check-in failed');
      setActiveRoom(null);
      load();
      loadUpsell(room.reservation_id);
    } catch (err) {
      alert(`${err?.message || 'Express check-in failed'}\n\nUse "Resv Check-In" for the full flow.`);
    } finally {
      setExpressBusy(false);
    }
  };

  // REQ-06 — Express one-click Checkout. Only offered when folio balance = 0, so
  // the settlement is a zero-collection CASH close. On a CTD warning (409) or any
  // other rejection we fall back to the full checkout modal.
  const expressCheckout = async (room) => {
    if (!room?.stay_id || expressBusy) return;
    const elig = expressElig.checkout?.[room.stay_id];
    if (!elig?.eligible) return;
    if (!window.confirm(`Express checkout Room ${room.room_number}? Folio balance is settled (₹0).`)) return;
    setExpressBusy(true);
    try {
      const res = await fetch(`/api/v1/pms/stays/${room.stay_id}/checkout`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actual_checkout_datetime: new Date().toISOString(),
          settlement_mode: 'CASH',
          payment_amount: 0,
          checkout_scope: 'MASTER_ONLY'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // CTD warning or anything else → open the full modal so the operator decides
        openCheckoutModal(room);
        return;
      }
      setActiveRoom(null);
      load();
    } catch (err) {
      openCheckoutModal(room);
    } finally {
      setExpressBusy(false);
    }
  };

  // Best-effort: fetch coverage-aware upsell offers for the just-checked-in guest.
  // Never blocks or errors the check-in — the folio already exists, offers are a bonus.
  const loadUpsell = async (reservationId) => {
    setUpsellOffers([]); setUpsellCtx(null); setUpsellAddedIds([]); setUpsellError('');
    setUpgradeOffers([]); setUpgradeDone(null);
    if (!reservationId) return;
    setUpsellLoading(true);
    try {
      const [recRes, upgRes] = await Promise.all([
        fetch(`/api/v1/upsell/recommendations/${reservationId}`, { headers: headers() }),
        fetch(`/api/v1/pms/reservations/${reservationId}/upgrade-offers`, { headers: headers() }),
      ]);
      const data = await recRes.json().catch(() => ({}));
      if (recRes.ok && data.enabled && Array.isArray(data.offers)) {
        setUpsellOffers(data.offers);
        setUpsellCtx(data.context || null);
      }
      const upg = await upgRes.json().catch(() => ({}));
      if (upgRes.ok && upg.enabled && Array.isArray(upg.offers)) setUpgradeOffers(upg.offers);
    } catch { /* upsell is optional — swallow */ }
    finally { setUpsellLoading(false); }
  };

  // One-tap room upgrade → moves the guest to the better room + posts the delta.
  const acceptUpgrade = async (offer) => {
    if (!checkInSelection?.reservation_id) return;
    setUpgradeBusyId(offer.room_type_id);
    setUpsellError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${checkInSelection.reservation_id}/upgrade`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_room_id: offer.to_room_id, to_room_type_id: offer.room_type_id, rate_plan_code: offer.rate_plan_code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Could not upgrade');
      setUpgradeDone({ name: offer.room_type_name, room: offer.to_room_number, gross: data.delta_gross });
      setUpgradeOffers([]);
      load();
    } catch (err) {
      setUpsellError(err?.message || 'Could not upgrade room');
    } finally {
      setUpgradeBusyId(null);
    }
  };

  // One-tap accept → server recomputes price + posts to the guest's open folio.
  const acceptUpsell = async (offer) => {
    if (!checkInSelection?.reservation_id) return;
    let qty = 1;
    if (offer.requires_qty) {
      const entered = window.prompt(`Quantity for "${offer.name}"?`, '1');
      if (entered == null) return;
      qty = Math.max(1, parseInt(entered, 10) || 1);
    }
    setUpsellBusyId(offer.product_id);
    setUpsellError('');
    try {
      const res = await fetch('/api/v1/upsell/accept', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reservation_id: checkInSelection.reservation_id, product_id: offer.product_id, qty, trigger_stage: 'CHECKIN' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Could not add offer');
      setUpsellAddedIds((prev) => [...prev, offer.product_id]);
    } catch (err) {
      setUpsellError(err?.message || 'Could not add upsell');
    } finally {
      setUpsellBusyId(null);
    }
  };

  const openCheckoutModal = async (room) => {
    if (!room?.stay_id) return;
    setCoModal(room);
    setCoFolio(null);
    setCoResult(null);
    setCoError('');
    setCoBillingProfiles([]);
    setCoBillingError('');
    setCoBillingLoading(false);
    setCoCorporates([]);
    setCoTravelAgents([]);
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const nowStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setCoForm({
      actual_checkout_datetime: nowStr,
      late_checkout_charge: 0,
      checkout_scope: 'MASTER_ONLY',
      settlement_mode: 'CASH',
      settlement_corporate_id: '',
      settlement_travel_agent_id: '',
      billing_profile_id: '',
      payment_received: false,
      payment_amount: '',
      payment_collected_mode: '',
      payment_reference_no: '',
      payment_remarks: '',
      split_payment: false,
      payment_lines: [{ mode: 'CASH', amount: '', reference_no: '' }],
      customer_gstin: '',
      customer_company: ''
    });
    setCoLoading(true);
    setActiveRoom(null);
    try {
      const [folioRes, policyRes, corpRes, taRes] = await Promise.all([
        fetch(`/api/v1/pms/stays/${room.stay_id}/folio`, { headers: headers() }),
        fetch('/api/v1/pms/property-config', { headers: headers() }),
        fetch('/api/v1/pms/corporates', { headers: headers() }),
        fetch('/api/v1/pms/travel-agents', { headers: headers() })
      ]);
      const folioData = await folioRes.json();
      const policyData = await policyRes.json();
      const corpData = corpRes.ok ? await corpRes.json().catch(() => []) : [];
      const taData = taRes.ok ? await taRes.json().catch(() => []) : [];
      if (!folioRes.ok) throw new Error(folioData?.error || 'Failed to load folio');
      setCoFolio(folioData);
      setCoCorporates(Array.isArray(corpData) ? corpData : []);
      setCoTravelAgents(Array.isArray(taData) ? taData : []);
      const linkedPendingBalance = Number(folioData?.linked_child_pending_balance || 0);
      const defaultCheckoutScope = Math.abs(linkedPendingBalance) > 0.009 ? 'COMBINED_LINKED' : 'MASTER_ONLY';
      // Re-checkout after an Undo Check-Out: the stay still carries the ORIGINAL
      // actual_checkout_datetime (undo preserves it deliberately). Default to it
      // rather than to "now" — the guest left when they left, and the corrected
      // invoice must not silently re-date the departure. Operator can still edit.
      const priorCheckoutLocal = folioData?.actual_checkout_datetime
        ? toDateTimeLocalValue(folioData.actual_checkout_datetime)
        : '';
      setCoForm((f) => ({
        ...f,
        checkout_scope: defaultCheckoutScope,
        ...(priorCheckoutLocal ? { actual_checkout_datetime: priorCheckoutLocal } : {}),
        customer_gstin: folioData?.guest_gstin || '',
        customer_company: folioData?.guest_company || ''
      }));
      setCoPolicy(policyData || null);
      const billingContext = deriveCheckoutBillingContext(folioData);
      setCoBillingLoading(true);
      setCoBillingError('');
      try {
        const q = new URLSearchParams();
        q.set('payer_type', billingContext.payerType);
        q.set('owner_entity_type', billingContext.ownerEntityType);
        if (billingContext.ownerEntityId) q.set('owner_entity_id', String(billingContext.ownerEntityId));
        const billingRes = await fetch(`/api/v1/pms/billing-profiles?${q.toString()}`, { headers: headers() });
        const billingData = await billingRes.json().catch(() => ([]));
        if (!billingRes.ok) throw new Error(billingData?.error || 'Failed to load billing profiles');
        const profiles = Array.isArray(billingData) ? billingData : [];
        setCoBillingProfiles(profiles);
        const defaultProfile = profiles.find((row) => row?.is_default === true) || profiles[0] || null;
        setCoForm((f) => ({ ...f, billing_profile_id: defaultProfile ? String(defaultProfile.billing_profile_id) : '' }));
      } catch (billingErr) {
        setCoBillingProfiles([]);
        setCoBillingError(billingErr?.message || 'Failed to load billing profiles');
      } finally {
        setCoBillingLoading(false);
      }
      // Auto-detect late checkout
      if (policyData && folioData) {
        const expectedCoDate = folioData.expected_checkout_datetime
          ? new Date(folioData.expected_checkout_datetime)
          : null;
        if (expectedCoDate && !priorCheckoutLocal) {
          // suggest actual checkout = now; user can adjust. Skipped when the
          // stay already carries a checkout time (undone checkout being redone)
          // so the original departure time survives the re-checkout.
          setCoForm((f) => ({ ...f, actual_checkout_datetime: nowStr }));
        }
      }
    } catch (err) {
      setCoError(err?.message || 'Failed to load checkout data');
    } finally {
      setCoLoading(false);
    }
  };

  const calcLateCharge = () => {
    if (!coFolio || !coPolicy || !coForm.actual_checkout_datetime) return { isLate: false, hoursLate: 0, suggestedCharge: 0 };
    const expectedDt = coFolio.expected_checkout_datetime
      ? new Date(coFolio.expected_checkout_datetime)
      : null;
    if (!expectedDt) return { isLate: false, hoursLate: 0, suggestedCharge: 0 };
    const graceMins = Number(coPolicy.late_checkout_grace_minutes || 30);
    const hourlyCharge = Number(coPolicy.late_checkout_hourly_charge || 0);
    const actualDt = new Date(coForm.actual_checkout_datetime);
    const diffMs = actualDt - expectedDt - (graceMins * 60000);
    if (diffMs <= 0) return { isLate: false, hoursLate: 0, suggestedCharge: 0 };
    const hoursLate = Math.ceil(diffMs / 3600000);
    return { isLate: true, hoursLate, suggestedCharge: hoursLate * hourlyCharge };
  };

  const getCheckoutSettlementSummary = () => {
    const reservationAmount = Number(coFolio?.reservation_amount || 0);
    const advanceCollected = Number(coFolio?.advance_collected || 0);
    // Bug 8650 — checkout settles EVERY open folio of the stay (Main + Self /
    // split folios), so the collectible must sum all folios, not just the
    // displayed/primary one.
    const allFoliosList = Array.isArray(coFolio?.all_folios) ? coFolio.all_folios : [];
    const folioDebit = allFoliosList.length
      ? allFoliosList.reduce((s, f) => s + Number(f.total_debit || 0), 0)
      : Number(coFolio?.folio?.total_debit || 0);
    const folioCredit = allFoliosList.length
      ? allFoliosList.reduce((s, f) => s + Number(f.total_credit || 0), 0)
      : Number(coFolio?.folio?.total_credit || 0);
    const folioBalance = allFoliosList.length
      ? allFoliosList.reduce((s, f) => s + Number(f.balance || 0), 0)
      : Number(coFolio?.folio?.balance || 0);
    const lateCharge = Number(coForm?.late_checkout_charge || 0);
    const checkoutScope = String(coForm?.checkout_scope || 'MASTER_ONLY').toUpperCase();
    const linkedPendingBalanceFromLines = (Array.isArray(coFolio?.lines) ? coFolio.lines : []).reduce((sum, row) => {
      if (!row?.linked_not_routed) return sum;
      return sum + Number(row.debit_amount || 0) - Number(row.credit_amount || 0);
    }, 0);
    const linkedPendingBalance = Number.isFinite(Number(coFolio?.linked_child_pending_balance))
      ? Number(coFolio.linked_child_pending_balance)
      : linkedPendingBalanceFromLines;
    const includesLinkedChildPending = checkoutScope === 'COMBINED_LINKED';
    const includedLinkedPending = includesLinkedChildPending ? linkedPendingBalance : 0;

    // Checkout Auto Round Off — when enabled, round every "Amount To Collect"
    // figure to the nearest whole rupee so what's displayed here matches what
    // the checkout endpoint will actually require (it posts the paisa
    // difference to the folio as a ROUND_OFF line before validating payment).
    const roundIfEnabled = (n) => (checkoutRoundOffEnabled ? Math.round(n) : n);
    const bookingCollectRaw = reservationAmount - advanceCollected;
    const bookingAmountToCollect = roundIfEnabled(Math.max(0, bookingCollectRaw));
    const bookingAdvanceExcess = Math.max(0, -bookingCollectRaw);
    const currentCollectRaw = folioBalance + includedLinkedPending;
    const amountToCollectCurrent = roundIfEnabled(Math.max(0, currentCollectRaw));
    const advanceExcessCurrent = Math.max(0, -currentCollectRaw);
    const finalCollectRaw = currentCollectRaw + lateCharge;
    const amountToCollectFinal = roundIfEnabled(Math.max(0, finalCollectRaw));
    const advanceExcessFinal = Math.max(0, -finalCollectRaw);

    return {
      reservationAmount,
      advanceCollected,
      bookingAmountToCollect,
      bookingAdvanceExcess,
      folioDebit,
      folioCredit,
      folioBalance,
      linkedPendingBalance,
      includedLinkedPending,
      includesLinkedChildPending,
      checkoutScope,
      lateCharge,
      amountToCollectCurrent,
      advanceExcessCurrent,
      amountToCollectFinal,
      advanceExcessFinal
    };
  };

  const submitCheckout = async () => {
    if (!coModal?.stay_id) return;
    setCoBusy(true); setCoError('');
    try {
      const summary = getCheckoutSettlementSummary();
      const requiredAmount = Number(summary.amountToCollectFinal || 0);
      const settlementMode = String(coForm.settlement_mode || 'CASH').toUpperCase();
      const paymentCollectedMode = String(coForm.payment_collected_mode || '').toUpperCase();
      const paymentReferenceNo = String(coForm.payment_reference_no || '').trim();
      const effectivePaymentMode = paymentCollectedMode || settlementMode;
      const useSplitPayment = !!coForm.split_payment;
      // Split-tender: one or more {mode, amount, reference_no} lines instead
      // of the single Received Amount/Mode/Reference trio. Filtered to only
      // lines someone actually filled in (blank rows from "+ Add" are ignored).
      const splitLines = useSplitPayment
        ? (coForm.payment_lines || [])
            .map((l) => ({ mode: String(l.mode || '').toUpperCase(), amount: Number(l.amount || 0), reference_no: String(l.reference_no || '').trim() }))
            .filter((l) => l.amount > 0)
        : [];
      const paymentAmount = useSplitPayment
        ? splitLines.reduce((s, l) => s + l.amount, 0)
        : Number(coForm.payment_amount || 0);
      if (Array.isArray(coBillingProfiles) && coBillingProfiles.length > 1 && !(Number(coForm.billing_profile_id || 0) > 0)) {
        throw new Error('Select billing profile before checkout.');
      }
      // Settlement modes CORPORATE / TRAVEL_AGENT route the open balance to the
      // chosen party's city ledger — no cash collected at checkout — so the
      // payment_received gate above doesn't apply. OTA = pre-collected by the
      // channel, also no payment_received gate.
      const isCreditSettlement = settlementMode === 'CORPORATE' || settlementMode === 'TRAVEL_AGENT' || settlementMode === 'OTA';
      if (settlementMode === 'CORPORATE' && !(Number(coForm.settlement_corporate_id) > 0)) {
        throw new Error('Pick the corporate that is settling the bill.');
      }
      if (settlementMode === 'TRAVEL_AGENT' && !(Number(coForm.settlement_travel_agent_id) > 0)) {
        throw new Error('Pick the travel agent that is settling the bill.');
      }
      if (useSplitPayment) {
        if (!splitLines.length) throw new Error('Add at least one payment line with an amount.');
        for (const line of splitLines) {
          if (!line.mode) throw new Error('Each payment line needs a mode.');
          if ((line.mode === 'CARD' || line.mode === 'UPI') && !line.reference_no) {
            throw new Error(`Reference No is required for a ${line.mode} payment line.`);
          }
        }
        if (requiredAmount > 0 && !isCreditSettlement && Math.abs(paymentAmount - requiredAmount) > 0.009) {
          throw new Error(`Payment lines must total Amount To Collect (Final): ${toMoney(requiredAmount)}. Currently: ${toMoney(paymentAmount)}.`);
        }
      } else {
        if (requiredAmount > 0 && !isCreditSettlement) {
          if (!coForm.payment_received) throw new Error('Please mark amount as received from guest before checkout.');
          if (paymentAmount <= 0) throw new Error(`Enter received amount. Pending amount: ${toMoney(requiredAmount)}.`);
          if (Math.abs(paymentAmount - requiredAmount) > 0.009) throw new Error(`Received amount must match Amount To Collect (Final): ${toMoney(requiredAmount)}.`);
          if ((effectivePaymentMode === 'CARD' || effectivePaymentMode === 'UPI') && !paymentReferenceNo) {
            throw new Error(`Reference No is required for ${effectivePaymentMode}.`);
          }
        }
        // Billed settlements (Corporate/Travel Agent/OTA) can still have a direct
        // collection on the side — validate its reference the same way even
        // though isCreditSettlement skips the block above entirely.
        if (isCreditSettlement && paymentAmount > 0 && (effectivePaymentMode === 'CARD' || effectivePaymentMode === 'UPI') && !paymentReferenceNo) {
          throw new Error(`Reference No is required for ${effectivePaymentMode}.`);
        }
      }
      const payload = {
        actual_checkout_datetime: dateTimeLocalToIso(coForm.actual_checkout_datetime),
        late_checkout_charge: Number(coForm.late_checkout_charge || 0),
        checkout_scope: String(coForm.checkout_scope || 'MASTER_ONLY').toUpperCase(),
        settlement_mode: coForm.settlement_mode || 'CASH',
        customer_gstin: coForm.customer_gstin ? coForm.customer_gstin.trim().toUpperCase() : null,
        customer_company: coForm.customer_company ? coForm.customer_company.trim() : null
      };
      if (settlementMode === 'CORPORATE') {
        payload.settlement_corporate_id = Number(coForm.settlement_corporate_id);
      }
      if (settlementMode === 'TRAVEL_AGENT') {
        payload.settlement_travel_agent_id = Number(coForm.settlement_travel_agent_id);
      }
      if (Number(coForm.billing_profile_id || 0) > 0) {
        payload.billing_profile_id = Number(coForm.billing_profile_id);
      }
      if (useSplitPayment && splitLines.length) {
        payload.payment_lines = splitLines;
      } else if (paymentAmount > 0) {
        payload.payment_amount = paymentAmount;
        payload.payment_reference_no = paymentReferenceNo;
        payload.payment_remarks = (coForm.payment_remarks || '').trim() || 'Final payment at checkout';
        if (paymentCollectedMode) payload.payment_collected_mode = paymentCollectedMode;
      }
      let res = await fetch(`/api/v1/pms/stays/${coModal.stay_id}/checkout`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      let data = await res.json();
      // Bug 8668 — Close to Departure (CTD) is a soft warning at checkout:
      // confirm with the operator, then retry with override_ctd so the backend
      // proceeds (and logs the override).
      if (!res.ok && data?.ctd_warning) {
        if (!window.confirm(data.error || 'This date is Closed to Departure. Proceed with checkout?')) {
          return; // operator declined — abort checkout
        }
        res = await fetch(`/api/v1/pms/stays/${coModal.stay_id}/checkout`, {
          method: 'POST',
          headers: { ...headers(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, override_ctd: true })
        });
        data = await res.json();
      }
      if (!res.ok) throw new Error(data?.error || 'Checkout failed');
      // Load invoice detail
      const invRes = await fetch(`/api/v1/pms/invoices/${data.invoice_id}`, { headers: headers() });
      const invData = await invRes.json();
      setCoResult(invRes.ok ? invData : data);
      setInvoiceSplitTab(0);
      // Fire-and-forget: cancel the room's key in the lock system. If no
      // lock agent is online, the backend returns 503; we ignore (the
      // operator can manually cancel on Onity). Never block PMS checkout
      // on lock-system reachability.
      try {
        const roomNo = coModal?.room_no || coModal?.room_number;
        if (roomNo) {
          fetch('/api/v1/lock/checkout', {
            method: 'POST',
            headers: { ...headers(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ reservation_id: coModal.reservation_id, rooms: [String(roomNo)] })
          }).catch(() => null);
        }
      } catch (_) { /* never blocks PMS checkout */ }
      load();
    } catch (err) {
      const message = err?.message || 'Checkout failed';
      const pendingMatch = message.match(/Pending:\s*([0-9]+(?:\.[0-9]+)?)/i);
      if (pendingMatch) {
        const pendingAmount = Number(pendingMatch[1]);
        if (Number.isFinite(pendingAmount)) {
          setCoForm((f) => ({ ...f, payment_received: pendingAmount > 0, payment_amount: pendingAmount.toFixed(2) }));
        }
      }
      setCoError(message);
    } finally {
      setCoBusy(false);
    }
  };

  const openInvoiceModal = async (invoiceId) => {
    try {
      const res = await fetch(`/api/v1/pms/invoices/${invoiceId}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load invoice');
      setInvoiceModal(data);
      setInvoiceSplitTab(0);
    } catch (err) {
      setError(err?.message || 'Failed to load invoice');
    }
  };

  const toMoney = (value) => Number(value || 0).toFixed(2);
  const buildInvoiceShareText = (inv) => {
    if (!inv) return '';
    return [
      `Invoice: ${inv.invoice_no || '-'}`,
      `Guest: ${inv.guest_name || '-'}`,
      `Room: ${(inv.room_numbers && inv.room_numbers.length ? inv.room_numbers.join(', ') : inv.room_number) || '-'}`,
      `Check-In: ${inv.checkin_datetime ? String(inv.checkin_datetime).slice(0, 19).replace('T', ' ') : '-'}`,
      `Check-Out: ${inv.checkout_datetime ? String(inv.checkout_datetime).slice(0, 19).replace('T', ' ') : '-'}`,
      `Total Debit: ${toMoney(inv.total_debit)}`,
      `Total Credit: ${toMoney(inv.total_credit)}`,
      `Net Payable: ${toMoney(inv.balance)}`,
      `Settlement: ${inv.settlement_mode || '-'}`
    ].join('\n');
  };

  // ── Number to Words (Indian format) ──────────────────────────────────────
  const numberToWordsLocal = (amount) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convert = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };
    const num = Math.abs(Number(amount) || 0);
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    let words = rupees === 0 ? 'Zero' : convert(rupees);
    words = 'Rupees ' + words;
    if (paise > 0) words += ' and ' + convert(paise) + ' Paise';
    return words + ' Only';
  };

  // ── Professional Tax Invoice HTML Builder ──────────────────────────────────
  const SAC_CODES_LOCAL = {
    ROOM_TARIFF: '996311', KOT_ROOM_CHARGE: '996331', BAR_ROOM_CHARGE: '996331',
    ROOM_SERVICE: '996331', LAUNDRY: '999710', MINIBAR_ROOM_CHARGE: '996331',
    LATE_CHECKOUT: '996311', ALLOWANCE: '996311', MANUAL: '999790', BANQUET_CHARGE: '996339'
  };
  const POSTING_LABELS_LOCAL = {
    ROOM_TARIFF: 'Room Charges', KOT_ROOM_CHARGE: 'Food & Beverage', BAR_ROOM_CHARGE: 'Bar Charges',
    ROOM_SERVICE: 'Room Service', LAUNDRY: 'Laundry Service', MINIBAR_ROOM_CHARGE: 'Minibar',
    LATE_CHECKOUT: 'Late Checkout', ALLOWANCE: 'Allowance / Waiver', MANUAL: 'Miscellaneous', BANQUET_CHARGE: 'Banquet Charges'
  };
  const getDescLocal = (line) => { const pt = String(line.posting_type || '').toUpperCase(); if (line.remarks && line.remarks.length > 5) return line.remarks; return POSTING_LABELS_LOCAL[pt] || line.revenue_desc || line.posting_type || '-'; };
  const getPayLabelLocal = (line) => { const pt = String(line.posting_type || '').toUpperCase(); if (pt === 'ADVANCE') return 'Advance Payment'; if (pt === 'CHECKOUT_PAYMENT') return 'Payment at Checkout'; if (pt.includes('CORPORATE')) return 'Corporate Credit'; return line.remarks || line.revenue_desc || pt || 'Payment'; };

  const buildInvoiceHtmlLocal = (inv, printLines, splitLabel, copyLabel) => {
    if (!inv) return '';
    const lines = Array.isArray(printLines) ? printLines : [];
    const hotel = inv.hotel || {};
    const guest = inv.guest_detail || {};
    const totalDebit = lines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
    const totalCredit = lines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
    const totalSgst = lines.reduce((s, l) => s + Number(l.sgst_amount || 0), 0);
    const totalCgst = lines.reduce((s, l) => s + Number(l.cgst_amount || 0), 0);
    const totalIgst = lines.reduce((s, l) => s + Number(l.igst_amount || 0), 0);
    const totalTaxableValue = lines.reduce((s, l) => s + (Number(l.debit_amount || 0) - Number(l.tax_amount || 0)), 0);
    const netPayable = totalDebit - totalCredit;
    const roundOff = Math.round(netPayable) - netPayable;
    const payableAmount = Math.round(netPayable);
    const ci = inv.checkin_datetime ? new Date(inv.checkin_datetime) : null;
    const co = inv.checkout_datetime ? new Date(inv.checkout_datetime) : null;
    const roomNts = ci && co ? Math.max(1, Math.ceil((co - ci) / 86400000)) : '-';
    const fmtDate = (dt) => fmtDateDMY(dt);
    const fmtTime = (dt) => fmtTimeHHMM(dt);
    const hotelName = hotel.billing_company_name || hotel.property_name || 'Hotel';
    const hotelAddr = [hotel.address_line1, hotel.address_line2, hotel.city, hotel.state, hotel.pin_code, hotel.country].filter(Boolean).join(', ');
    const hotelGstin = hotel.gstin_number || '';
    const hotelStateCode = hotelGstin ? hotelGstin.substring(0, 2) : '';
    const guestGstin = inv.customer_gstin || guest.guest_gstin || ''; // invoice-level GSTIN first, same as PmsInvoiceRegister
    const guestCompany = guest.guest_company || '';
    const guestAddr = [guest.guest_address, guest.guest_city, guest.guest_state, guest.guest_country, guest.guest_pin].filter(Boolean).join(', ') || '';
    const guestEmail = guest.guest_email || inv.guest_email || '';
    const mealPlanMap = { EP: 'European Plan (Room Only)', CP: 'Continental Plan (Breakfast)', MAP: 'Modified American Plan (B+D)', AP: 'American Plan (All Meals)' };
    const mealPlanCode = (guest.meal_plan || 'EP').toUpperCase();
    const mealPlanLabel = mealPlanMap[mealPlanCode] || mealPlanCode;
    const ratePlanCode = guest.rate_plan_code || '';
    const chargeLines = lines.filter(l => Number(l.debit_amount || 0) > 0);
    const paymentLines = lines.filter(l => Number(l.credit_amount || 0) > 0);
    const totalPayments = paymentLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
    const hasIgst = totalIgst > 0;
    const invTitle = splitLabel ? `${inv.invoice_no || 'Invoice'} - ${splitLabel}` : (inv.invoice_no || 'Invoice');
    const invoiceDate = fmtDate(inv.checkout_datetime || inv.created_at);

    // Tax groups
    const taxGroups = {};
    chargeLines.forEach(l => { const rate = Number(l.gst_percent || 0); if (rate <= 0) return; if (!taxGroups[rate]) taxGroups[rate] = { taxable: 0, cgst: 0, sgst: 0, igst: 0 }; taxGroups[rate].taxable += Number(l.debit_amount || 0) - Number(l.tax_amount || 0); taxGroups[rate].cgst += Number(l.cgst_amount || 0); taxGroups[rate].sgst += Number(l.sgst_amount || 0); taxGroups[rate].igst += Number(l.igst_amount || 0); });
    const taxGroupKeys = Object.keys(taxGroups).sort((a, b) => Number(a) - Number(b));

    const rowsHtml = chargeLines.map((line, idx) => {
      const baseAmt = Number(line.debit_amount || 0) - Number(line.tax_amount || 0);
      const pt = String(line.posting_type || '').toUpperCase();
      const sacCode = line.sac_code || SAC_CODES_LOCAL[pt] || '999790';
      return `<tr style="${idx % 2 === 1 ? 'background:#fafafa;' : ''}">
        <td class="tc">${idx + 1}</td>
        <td class="tl">${fmtDate(line.posting_date || line.created_at)}</td>
        <td class="tl">${getDescLocal(line)}</td>
        <td class="tc">${sacCode}</td>
        <td class="tc">${line.room_number || inv.room_number || ''}</td>
        <td class="tr">${toMoney(baseAmt)}</td>
        <td class="tc">${line.gst_percent ? Number(line.gst_percent) + '%' : '-'}</td>
        ${hasIgst ? `<td class="tr">${toMoney(line.igst_amount || 0)}</td>` : `<td class="tr">${toMoney(line.cgst_amount || 0)}</td><td class="tr">${toMoney(line.sgst_amount || 0)}</td>`}
        <td class="tr" style="font-weight:600;">${toMoney(line.debit_amount)}</td>
      </tr>`;
    }).join('');

    const taxSummaryHtml = taxGroupKeys.map(rate => { const g = taxGroups[rate]; return `<tr><td class="tl" style="font-size:10px;">${Number(rate)}%</td><td class="tr" style="font-size:10px;">${toMoney(g.taxable)}</td>${hasIgst ? `<td class="tr" style="font-size:10px;">${toMoney(g.igst)}</td>` : `<td class="tr" style="font-size:10px;">${toMoney(g.cgst)}</td><td class="tr" style="font-size:10px;">${toMoney(g.sgst)}</td>`}<td class="tr" style="font-size:10px;font-weight:600;">${toMoney(g.cgst + g.sgst + g.igst)}</td></tr>`; }).join('');

    const paymentRowsHtml = paymentLines.map(l => `<tr><td style="padding:2px 8px;font-size:10px;">${getPayLabelLocal(l)}${l.payment_mode ? ` <span style="color:#777;font-weight:400;">(${l.payment_mode}${l.reference_no ? ` — ${l.reference_no}` : ''})</span>` : ''}</td><td style="padding:2px 8px;font-size:10px;text-align:right;">${toMoney(l.credit_amount)}</td></tr>`).join('');

    const colCount = hasIgst ? 9 : 10;

    return `<html><head><title>${invTitle}</title>
<style>
  @page { size: A4; margin: 10mm 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; padding: 8px; }
  table { border-collapse: collapse; }
  .inv { max-width:800px; margin:0 auto; border:2px solid #333; }
  .tc { padding:5px 6px; border:1px solid #bbb; text-align:center; font-size:10.5px; }
  .tl { padding:5px 6px; border:1px solid #bbb; text-align:left; font-size:10.5px; }
  .tr { padding:5px 6px; border:1px solid #bbb; text-align:right; font-size:10.5px; }
  .lbl { padding:2px 0; font-weight:600; color:#333; width:110px; font-size:10.5px; }
  .val { padding:2px 0; font-size:10.5px; }
  @media print { body { padding: 0; } }
</style>
</head><body>
<div class="inv">
  <div style="padding:12px 18px;text-align:center;border-bottom:2px solid #333;">
    ${hotel.logo_url ? `<img src="${hotel.logo_url.startsWith('http') ? hotel.logo_url : window.location.origin + hotel.logo_url}" alt="Logo" style="height:50px;margin-bottom:6px;" onerror="this.style.display='none'"><br>` : ''}
    <div style="font-size:17px;font-weight:700;letter-spacing:1.5px;">${hotelName}</div>
    ${hotelAddr ? `<div style="font-size:10px;margin-top:3px;color:#444;">${hotelAddr}</div>` : ''}
    <div style="font-size:9.5px;color:#555;margin-top:2px;">${[hotel.phone ? `Tel: ${hotel.phone}` : '', hotel.email ? `Email: ${hotel.email}` : '', hotel.website ? `Web: ${hotel.website}` : ''].filter(Boolean).join(' | ')}</div>
    ${hotelGstin ? `<div style="font-size:10px;margin-top:3px;font-weight:600;">GSTIN: ${hotelGstin}${hotelStateCode ? ` | State Code: ${hotelStateCode}` : ''}</div>` : ''}
  </div>
  <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 18px;background:#f0f0f0;border-bottom:2px solid #333;font-weight:700;">
    <div style="font-size:14px;letter-spacing:3px;">TAX INVOICE</div>
    <div style="text-align:right;font-size:10.5px;"><div>Invoice No: <span style="font-size:12px;">${inv.invoice_no || '-'}${splitLabel ? ` (${splitLabel})` : ''}</span></div><div style="font-weight:400;margin-top:1px;">Date: ${invoiceDate}</div></div>
  </div>
  ${copyLabel ? `<div style="text-align:right;padding:3px 18px;font-size:9.5px;font-weight:700;font-style:italic;color:#555;border-bottom:1px solid #ddd;">${copyLabel}</div>` : ''}
  <div style="display:flex;border-bottom:1.5px solid #333;">
    <div style="flex:1;padding:8px 18px;border-right:1.5px solid #333;">
      <div style="font-size:10px;font-weight:700;color:#555;text-transform:uppercase;margin-bottom:4px;letter-spacing:0.5px;">Guest Details</div>
      <table style="width:100%;"><tr><td class="lbl">Name</td><td class="val" style="font-weight:600;">${inv.guest_name || '-'}</td></tr>${guestCompany ? `<tr><td class="lbl">Company</td><td class="val">${guestCompany}</td></tr>` : ''}${guestGstin ? `<tr><td class="lbl">Guest GSTIN</td><td class="val" style="font-weight:600;">${guestGstin}</td></tr>` : ''}${guestAddr ? `<tr><td class="lbl">Address</td><td class="val" style="font-size:10px;">${guestAddr}</td></tr>` : ''}<tr><td class="lbl">Mobile</td><td class="val">${inv.guest_mobile || '-'}</td></tr>${guestEmail ? `<tr><td class="lbl">Email</td><td class="val" style="font-size:10px;">${guestEmail}</td></tr>` : ''}<tr><td class="lbl">Nationality</td><td class="val">${guest.nationality || 'INDIAN'}</td></tr><tr><td class="lbl">Pax</td><td class="val">${guest.total_pax || '-'}</td></tr></table>
    </div>
    <div style="flex:1;padding:8px 18px;">
      <div style="font-size:10px;font-weight:700;color:#555;text-transform:uppercase;margin-bottom:4px;letter-spacing:0.5px;">Stay Details</div>
      <table style="width:100%;"><tr><td class="lbl">Room No</td><td class="val" style="text-align:right;font-weight:600;font-size:13px;">${(inv.room_numbers && inv.room_numbers.length ? inv.room_numbers.join(', ') : inv.room_number) || '-'}</td></tr><tr><td class="lbl">Room Type</td><td class="val" style="text-align:right;">${inv.room_type_name || '-'}</td></tr><tr><td class="lbl">Reservation No</td><td class="val" style="text-align:right;">${inv.reservation_no || '-'}</td></tr><tr><td class="lbl">Check-In</td><td class="val" style="text-align:right;">${fmtDate(inv.checkin_datetime)}${fmtTime(inv.checkin_datetime) ? ' ' + fmtTime(inv.checkin_datetime) : ''}</td></tr><tr><td class="lbl">Check-Out</td><td class="val" style="text-align:right;">${fmtDate(inv.checkout_datetime)}${fmtTime(inv.checkout_datetime) ? ' ' + fmtTime(inv.checkout_datetime) : ''}</td></tr><tr><td class="lbl">No. of Nights</td><td class="val" style="text-align:right;font-weight:600;">${roomNts}</td></tr>${ratePlanCode ? `<tr><td class="lbl">Rate Plan</td><td class="val" style="text-align:right;">${ratePlanCode}</td></tr>` : ''}<tr><td class="lbl">Meal Plan</td><td class="val" style="text-align:right;">${mealPlanLabel}</td></tr></table>
    </div>
  </div>
  <div style="padding:8px 12px 4px;">
    <table style="width:100%;">
      <thead><tr style="background:#e8ecf0;"><th class="tc" style="width:30px;font-size:10px;">S.No</th><th class="tl" style="font-size:10px;">Date</th><th class="tl" style="font-size:10px;">Description</th><th class="tc" style="font-size:10px;">SAC</th><th class="tc" style="font-size:10px;">Room</th><th class="tr" style="font-size:10px;">Taxable Amt</th><th class="tc" style="font-size:10px;">GST%</th>${hasIgst ? `<th class="tr" style="font-size:10px;">IGST</th>` : `<th class="tr" style="font-size:10px;">CGST</th><th class="tr" style="font-size:10px;">SGST</th>`}<th class="tr" style="font-size:10px;">Amount</th></tr></thead>
      <tbody>${rowsHtml || `<tr><td colspan="${colCount}" style="padding:8px;border:1px solid #bbb;color:#888;text-align:center;">No charge items</td></tr>`}</tbody>
      <tfoot><tr style="background:#e8ecf0;font-weight:700;"><td class="tr" colspan="${hasIgst ? 5 : 5}" style="font-size:10.5px;">Sub Total</td><td class="tr" style="font-size:10.5px;">${toMoney(totalTaxableValue)}</td><td class="tc"></td>${hasIgst ? `<td class="tr" style="font-size:10.5px;">${toMoney(totalIgst)}</td>` : `<td class="tr" style="font-size:10.5px;">${toMoney(totalCgst)}</td><td class="tr" style="font-size:10.5px;">${toMoney(totalSgst)}</td>`}<td class="tr" style="font-size:11px;">${toMoney(totalDebit)}</td></tr></tfoot>
    </table>
  </div>
  <div style="display:flex;border-top:1.5px solid #333;font-size:10.5px;">
    <div style="flex:1;padding:8px 14px;border-right:1px solid #ccc;">
      <div style="font-size:9.5px;font-weight:700;color:#555;text-transform:uppercase;margin-bottom:4px;">Tax Summary</div>
      <table style="width:100%;border:1px solid #ccc;"><thead><tr style="background:#f5f5f5;"><th style="padding:3px 6px;text-align:left;font-size:9.5px;border:1px solid #ccc;">Rate</th><th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">Taxable</th>${hasIgst ? `<th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">IGST</th>` : `<th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">CGST</th><th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">SGST</th>`}<th style="padding:3px 6px;text-align:right;font-size:9.5px;border:1px solid #ccc;">Total Tax</th></tr></thead><tbody>${taxSummaryHtml || `<tr><td colspan="${hasIgst ? 4 : 5}" style="padding:3px 6px;font-size:9.5px;color:#888;border:1px solid #ccc;">No tax</td></tr>`}</tbody></table>
    </div>
    <div style="width:250px;padding:8px 14px;">
      <table style="width:100%;font-size:10.5px;">
        <tr><td style="padding:3px 0;">Gross Total</td><td style="text-align:right;font-weight:700;padding:3px 0;">${toMoney(totalDebit)}</td></tr>
        ${paymentLines.length > 0 ? `<tr><td colspan="2" style="padding:4px 0 2px;font-size:9.5px;font-weight:700;color:#555;border-top:1px solid #ddd;">PAYMENTS</td></tr>${paymentRowsHtml}<tr style="border-top:1px solid #ccc;"><td style="padding:3px 0;font-size:10px;">Total Paid</td><td style="text-align:right;padding:3px 0;font-size:10px;">${toMoney(totalPayments)}</td></tr>` : ''}
        ${Math.abs(roundOff) >= 0.01 ? `<tr><td style="padding:2px 0;font-size:10px;">Round Off</td><td style="text-align:right;padding:2px 0;font-size:10px;">${roundOff >= 0 ? '+' : ''}${roundOff.toFixed(2)}</td></tr>` : ''}
        <tr style="border-top:2px solid #333;"><td style="padding:5px 0;font-weight:700;font-size:12px;">Net Payable</td><td style="text-align:right;font-weight:700;font-size:12px;padding:5px 0;">${toMoney(payableAmount > 0 ? payableAmount : 0)}</td></tr>
      </table>
    </div>
  </div>
  <div style="padding:5px 18px;border-top:1.5px solid #333;font-size:10px;background:#f8f8f8;"><b>Amount in words:</b> ${numberToWordsLocal(payableAmount > 0 ? payableAmount : totalDebit)}</div>
  <div style="display:flex;justify-content:space-between;padding:5px 18px;border-top:1px solid #ccc;font-size:10px;"><div><b>Settlement Mode:</b> ${inv.settlement_mode || '-'}</div>${hotel.state ? `<div><b>Place of Supply:</b> ${hotel.state}${hotelStateCode ? ` (${hotelStateCode})` : ''}</div>` : ''}</div>
  <div style="padding:6px 18px;border-top:1px solid #ddd;font-size:9px;color:#666;line-height:1.4;"><div style="font-weight:600;margin-bottom:2px;">Terms & Conditions:</div><div>1. This is a computer-generated invoice.</div><div>2. I agree that I am responsible for the full payment of this bill in the event it is not paid by the Company, Organisation or Person indicated.</div><div>3. Subject to local jurisdiction. E. & O.E.</div></div>
  <div style="text-align:center;padding:16px 18px 8px;border-top:1px solid #ddd;font-size:10.5px;color:#555;">This is a system-generated invoice and does not require a signature.</div>
  <div style="text-align:center;padding:6px 18px;border-top:1px solid #eee;font-size:10px;color:#555;font-style:italic;">Thank you for staying with us. We look forward to welcoming you again!</div>
</div></body></html>`;
  };

  const handleInvoicePrint = async (inv, printLines, splitLabel, copyLabel) => {
    if (!inv) return;
    // If this is from checkout result (no hotel/lines), fetch full invoice data
    let fullInv = inv;
    if (!inv.hotel && inv.invoice_id) {
      try {
        const res = await fetch(`/api/v1/pms/invoices/${inv.invoice_id}`, { headers: headers() });
        const data = await res.json();
        if (res.ok && data) fullInv = data;
      } catch (_) { /* fall back to original inv */ }
    }
    const lines = Array.isArray(printLines) ? printLines : (Array.isArray(fullInv.lines) ? fullInv.lines : []);
    const html = buildInvoiceHtmlLocal(fullInv, lines, splitLabel, copyLabel || 'Original');
    const popup = window.open('', '_blank', 'width=900,height=700');
    if (!popup) return;
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const shareInvoiceWhatsApp = (inv) => {
    if (!inv) return;
    const text = encodeURIComponent(buildInvoiceShareText(inv));
    const mobileDigits = String(inv.guest_mobile || '').replace(/\D/g, '');
    const base = mobileDigits ? `https://wa.me/${mobileDigits}` : 'https://wa.me/';
    window.open(`${base}?text=${text}`, '_blank');
  };

  const shareInvoiceEmail = (inv) => {
    if (!inv) return;
    const subject = encodeURIComponent(`Final Invoice ${inv.invoice_no || ''}`.trim());
    const body = encodeURIComponent(buildInvoiceShareText(inv));
    const mailTo = `mailto:${encodeURIComponent(inv.guest_email || '')}?subject=${subject}&body=${body}`;
    window.location.href = mailTo;
  };

  const openWalkInForVacantRoom = (room) => {
    if (!room?.room_id) return;
    const occ = String(room.occupancy_status || '').toUpperCase();
    const maint = String(room.maintenance_status || '').toUpperCase();
    if (occ !== 'VACANT' || ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint)) {
      setError('Walk-In Check-In is available only for vacant rooms.');
      return;
    }
    if (typeof onOpenWalkIn === 'function') {
      onOpenWalkIn({
        room_id: Number(room.room_id),
        room_number: room.room_number || '',
        room_type_name: room.room_type_name || ''
      });
    }
    setActiveRoom(null);
  };

  const changeRoomForStay = async () => {
    if (!activeRoom?.stay_id) return setChangeRoomError('No active stay selected for room change');
    if (!changeTargetRoomId) return setChangeRoomError('Select a target room');
    setChangeRoomError('');
    setChangeRoomMessage('');
    try {
      // REQ-19 — adults/children are optional; omit them entirely to leave
      // occupancy unchanged (a plain room move, same as before this change).
      const body = { to_room_id: Number(changeTargetRoomId), reason: 'Changed from command center' };
      if (changeTargetAdults !== '') body.adults = Number(changeTargetAdults);
      if (changeTargetChildren !== '') body.children = Number(changeTargetChildren);
      const res = await fetch(`/api/v1/pms/stays/${activeRoom.stay_id}/change-room`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Room change failed');
      if (data.repriced) {
        const sign = Number(data.delta_gross) > 0 ? '+' : '';
        setChangeRoomMessage(`Room changed — rate adjusted ${sign}₹${Number(data.delta_gross || 0).toFixed(2)} (posted to folio).`);
      } else if (data.type_or_occupancy_changed) {
        setChangeRoomMessage('Room changed. The tariff is unchanged — post any difference with Manual Posting.');
      }
      setActiveRoom(null);
      setChangeTargetRoomId('');
      setChangeTargetAdults('');
      setChangeTargetChildren('');
      load();
    } catch (err) {
      // Shown inside the still-open "Room X — Actions" popup (see JSX below),
      // not the page-level banner — that one sits BEHIND this modal and was
      // easy to miss entirely.
      setChangeRoomError(err?.message || 'Room change failed');
    }
  };

  const swapRoomForStay = async () => {
    if (!activeRoom?.stay_id) return setSwapRoomError('No active stay selected for room swap');
    if (!swapTargetStayId) return setSwapRoomError('Select stay to swap with');
    setSwapRoomError('');
    try {
      const res = await fetch(`/api/v1/pms/stays/${activeRoom.stay_id}/swap-room`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ with_stay_id: Number(swapTargetStayId), reason: 'Swapped from command center' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Room swap failed');
      setActiveRoom(null);
      setSwapTargetStayId('');
      load();
    } catch (err) {
      // Same fix as Change Room — shown inside the still-open popup, not the
      // page-level banner behind it.
      setSwapRoomError(err?.message || 'Room swap failed');
    }
  };

  const resolveEffectiveTaxPercent = (profile) => {
    const direct = Number(profile?.tax_percent || 0);
    const computed = Number(profile?.cgst_percent || 0) + Number(profile?.sgst_percent || 0) + Number(profile?.igst_percent || 0);
    return direct > 0 ? direct : computed;
  };

  const computePostingTaxBreakup = ({ debitAmount, selectedProfile, gstPercent, taxMode }) => {
    const amount = Number(debitAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      return {
        gst_percent: selectedProfile ? String(resolveEffectiveTaxPercent(selectedProfile)) : (gstPercent || ''),
        cgst_amount: '',
        sgst_amount: '',
        igst_amount: '',
        tax_amount: ''
      };
    }

    let cgstPercent = Number(selectedProfile?.cgst_percent || 0);
    let sgstPercent = Number(selectedProfile?.sgst_percent || 0);
    let igstPercent = Number(selectedProfile?.igst_percent || 0);
    let effectiveGstPercent = Number(gstPercent || 0);

    const profilePercentTotal = cgstPercent + sgstPercent + igstPercent;
    if (profilePercentTotal > 0) {
      effectiveGstPercent = profilePercentTotal;
    } else if (selectedProfile) {
      effectiveGstPercent = resolveEffectiveTaxPercent(selectedProfile);
      const regime = String(selectedProfile?.tax_regime || '').toUpperCase();
      if (effectiveGstPercent > 0) {
        if (regime === 'GST') {
          cgstPercent = effectiveGstPercent / 2;
          sgstPercent = effectiveGstPercent / 2;
          igstPercent = 0;
        } else {
          cgstPercent = 0;
          sgstPercent = 0;
          igstPercent = effectiveGstPercent;
        }
      }
    }

    // Inclusive mode: the entered amount is the tax-inclusive gross — back out
    // the taxable base first (base = gross / (1 + rate/100)), then compute each
    // tax component off that base at the same percentages exclusive mode uses.
    // The entered amount itself is left untouched (still posted as-is); it's
    // debit_includes_tax (set at submit time) that tells the backend not to
    // add this tax_amount on top of it a second time.
    const isInclusive = String(taxMode || 'EXCLUSIVE').toUpperCase() === 'INCLUSIVE';
    const taxableBase = isInclusive && effectiveGstPercent > 0 ? amount / (1 + effectiveGstPercent / 100) : amount;

    const cgstAmount = Number((taxableBase * (cgstPercent / 100)).toFixed(2));
    const sgstAmount = Number((taxableBase * (sgstPercent / 100)).toFixed(2));
    const igstAmount = Number((taxableBase * (igstPercent / 100)).toFixed(2));
    const totalTax = Number((cgstAmount + sgstAmount + igstAmount).toFixed(2));

    return {
      gst_percent: effectiveGstPercent > 0 ? String(Number(effectiveGstPercent.toFixed(3))) : '',
      cgst_amount: cgstAmount > 0 ? String(cgstAmount) : '0',
      sgst_amount: sgstAmount > 0 ? String(sgstAmount) : '0',
      igst_amount: igstAmount > 0 ? String(igstAmount) : '0',
      tax_amount: totalTax > 0 ? String(totalTax) : '0'
    };
  };

  const postingTypeOptions = useMemo(() => {
    const rows = Array.isArray(postingServiceCatalog.master_services) ? postingServiceCatalog.master_services : [];
    if (rows.length) {
      return rows.map((row) => ({
        service_key: row.service_key,
        label: row.service_label || row.posting_type || row.service_key,
        posting_type: row.posting_type || 'MANUAL',
        source_module: row.source_module || 'FRONT_OFFICE',
        revenue_code: row.revenue_code || '',
        revenue_description: row.revenue_description || ''
      }));
    }
    return [
      { service_key: 'MANUAL_MISC', label: 'Miscellaneous', posting_type: 'MANUAL', source_module: 'FRONT_OFFICE', revenue_code: 'RC_MISC', revenue_description: 'Miscellaneous Charge' },
      { service_key: 'LAUNDRY', label: 'Laundry', posting_type: 'LAUNDRY', source_module: 'HOUSEKEEPING', revenue_code: 'RC_LAUNDRY', revenue_description: 'Laundry Service' },
      { service_key: 'ROOM_SERVICE', label: 'Room Service', posting_type: 'ROOM_SERVICE', source_module: 'FRONT_OFFICE', revenue_code: 'RC_ROOM_SERVICE', revenue_description: 'Room Service' },
      { service_key: 'ROOM_TARIFF', label: 'Room Tariff', posting_type: 'ROOM_TARIFF', source_module: 'FRONT_OFFICE', revenue_code: 'RC_ROOM', revenue_description: 'Room Tariff' },
      { service_key: 'MINIBAR_ROOM_CHARGE', label: 'Minibar', posting_type: 'MINIBAR_ROOM_CHARGE', source_module: 'HOUSEKEEPING', revenue_code: 'RC_MINIBAR', revenue_description: 'Minibar Charge' },
      { service_key: 'ADVANCE_PAYMENT', label: 'Advance / Payment', posting_type: 'ADVANCE', source_module: 'FRONT_OFFICE', revenue_code: 'RC_ADVANCE', revenue_description: 'Advance Payment' },
      { service_key: 'ALLOWANCE', label: 'Allowance / Waiver', posting_type: 'ALLOWANCE', source_module: 'FRONT_OFFICE', revenue_code: 'RC_ALLOWANCE', revenue_description: 'Allowance / Waiver' }
    ];
  }, [postingServiceCatalog]);

  const applyPostingTypeSelection = (selectedServiceKey) => {
    const selected = postingTypeOptions.find((opt) => String(opt.service_key) === String(selectedServiceKey));
    if (!selected) {
      setPostingForm((f) => ({ ...f, service_key: selectedServiceKey || '' }));
      return;
    }
    const postingTypeUpper = String(selected.posting_type || '').toUpperCase();
    const isMinibarPosting = postingTypeUpper === 'MINIBAR_ROOM_CHARGE';
    const isAllowancePosting = postingTypeUpper === 'ALLOWANCE';
    const isPaymentPosting = ['ADVANCE', 'PAYMENT', 'ADVANCE_PAYMENT', 'CHECKOUT_PAYMENT'].includes(postingTypeUpper);
    setPostingForm((f) => ({
      ...f,
      service_key: selected.service_key || '',
      posting_type: selected.posting_type || f.posting_type || 'MANUAL',
      source_module: isMinibarPosting ? 'HOUSEKEEPING' : (selected.source_module || f.source_module || 'FRONT_OFFICE'),
      revenue_code: selected.revenue_code || '',
      revenue_description: selected.revenue_description || '',
      debit_amount: (isAllowancePosting || isPaymentPosting) ? '' : f.debit_amount,
      credit_amount: (isAllowancePosting || isPaymentPosting) ? f.credit_amount : '',
      allowance_mode: isAllowancePosting ? (f.allowance_mode || 'PERCENT') : 'PERCENT',
      allowance_percent: isAllowancePosting ? (f.allowance_percent || '') : '',
      allowance_base_amount: isAllowancePosting
        ? (f.allowance_base_amount || String(Math.max(0, Number(roomFolio?.folio?.balance || 0)).toFixed(2)))
        : '',
      tax_profile_id: (isAllowancePosting || isPaymentPosting) ? NO_TAX_PROFILE_OPTION : f.tax_profile_id,
      gst_percent: (isAllowancePosting || isPaymentPosting) ? '0' : f.gst_percent,
      cgst_amount: (isAllowancePosting || isPaymentPosting) ? '0' : f.cgst_amount,
      sgst_amount: (isAllowancePosting || isPaymentPosting) ? '0' : f.sgst_amount,
      igst_amount: (isAllowancePosting || isPaymentPosting) ? '0' : f.igst_amount,
      tax_amount: (isAllowancePosting || isPaymentPosting) ? '0' : f.tax_amount
    }));
  };

  const printGuestInvoice = () => {
    if (!roomFolio) return;
    const folio = roomFolio;
    const prop = selectedProperty || {};
    const hotelName = prop.name || 'Hotel';
    const hotelCity = [prop.city, prop.state, prop.country].filter(Boolean).join(', ');
    const currency = prop.currency_code || '₹';

    const billTo = String(folio.bill_to || 'SELF').toUpperCase();
    const billToName = billTo === 'CORPORATE' && folio.corporate_name
      ? folio.corporate_name
      : billTo === 'TRAVEL_AGENT' && folio.travel_agent_name
        ? folio.travel_agent_name
        : folio.primary_guest_name || '-';
    const billToLabel = billTo === 'CORPORATE' ? 'Corporate Account' : billTo === 'TRAVEL_AGENT' ? 'Travel Agent' : 'Guest';

    const PAYMENT_TYPES = new Set(['ADVANCE', 'PAYMENT', 'ADVANCE_PAYMENT', 'CHECKOUT_PAYMENT', 'REFUND', 'VOID', 'CITY_LEDGER_TRANSFER']);
    const ALLOWANCE_TYPES = new Set(['ALLOWANCE']);
    const allLines = Array.isArray(folio.lines) ? folio.lines : [];
    // linked_not_routed rows are child-folio informational rows, not this folio's settlement rows.
    const linkedPendingLines = allLines.filter((l) => Boolean(l?.linked_not_routed));
    const lines = allLines.filter((l) => !l?.linked_not_routed);
    const chargeLines = lines.filter((l) => {
      const pt = String(l.posting_type || '').toUpperCase();
      return !PAYMENT_TYPES.has(pt) && !ALLOWANCE_TYPES.has(pt);
    });
    const paymentLines = lines.filter((l) => PAYMENT_TYPES.has(String(l.posting_type || '').toUpperCase()));
    const allowanceLines = lines.filter((l) => ALLOWANCE_TYPES.has(String(l.posting_type || '').toUpperCase()));
    const totalCharges = chargeLines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
    const totalTax = chargeLines.reduce((s, l) => s + Number(l.cgst_amount || 0) + Number(l.sgst_amount || 0) + Number(l.igst_amount || 0), 0);
    const totalPayments = paymentLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
    const totalAllowances = allowanceLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
    const balance = Number(folio?.folio?.balance ?? (totalCharges - totalAllowances - totalPayments));

    const fmt = (v) => `${currency} ${Number(v || 0).toFixed(2)}`;
    const fmtDate = (v) => fmtDateDMYDash(v);
    const ptLabel = (pt) => {
      const map = { ROOM_TARIFF: 'Room Tariff', KOT_ROOM_CHARGE: 'KOT / F&B', BAR_ROOM_CHARGE: 'Bar Charges', MINIBAR_ROOM_CHARGE: 'Minibar', LAUNDRY: 'Laundry', ROOM_SERVICE: 'Room Service', MANUAL: 'Miscellaneous', ALLOWANCE: 'Allowance / Waiver', LATE_CHECKOUT: 'Late Checkout', ROUTED_IN: 'Linked Room Charge', ROUTED_OUT: 'Routed to Master', FOLIO_ROUTE_TRANSFER: 'Route Transfer' };
      return map[String(pt || '').toUpperCase()] || String(pt || '-');
    };
    const pymtLabel = (pt) => {
      const map = { ADVANCE: 'Advance Payment', PAYMENT: 'Payment', ADVANCE_PAYMENT: 'Advance Payment', CHECKOUT_PAYMENT: 'Checkout Payment', REFUND: 'Refund', CITY_LEDGER_TRANSFER: 'City Ledger Transfer' };
      return map[String(pt || '').toUpperCase()] || String(pt || '-');
    };

    const chargeRows = chargeLines.map((l) => `
      <tr>
        <td>${fmtDate(l.posting_date || l.created_at)}</td>
        <td>${ptLabel(l.posting_type)}${l.remarks && l.remarks !== 'Manual posting' ? ` — ${l.remarks}` : ''}</td>
        <td class="amt">${fmt(l.debit_amount)}</td>
        <td class="amt">${Number(l.cgst_amount) > 0 ? fmt(l.cgst_amount) : '-'}</td>
        <td class="amt">${Number(l.sgst_amount) > 0 ? fmt(l.sgst_amount) : '-'}</td>
        <td class="amt">${Number(l.igst_amount) > 0 ? fmt(l.igst_amount) : '-'}</td>
      </tr>`).join('');

    const paymentRows = paymentLines.map((l) => `
      <tr>
        <td>${fmtDate(l.posting_date || l.created_at)}</td>
        <td>${pymtLabel(l.posting_type)}${l.remarks ? ` — ${l.remarks}` : ''}</td>
        <td class="amt" style="color:#166534">${fmt(l.credit_amount)}</td>
      </tr>`).join('');

    const allowanceRows = allowanceLines.map((l) => `
      <tr>
        <td>${fmtDate(l.posting_date || l.created_at)}</td>
        <td>${ptLabel(l.posting_type)}${l.remarks ? ` â€” ${l.remarks}` : ''}</td>
        <td class="amt" style="color:#b45309">${fmt(l.credit_amount)}</td>
      </tr>`).join('');

    const linkedPendingRows = linkedPendingLines.map((l) => {
      const debit = Number(l.debit_amount || 0);
      const credit = Number(l.credit_amount || 0);
      const net = debit - credit;
      const netColor = net >= 0 ? '#c53030' : '#166534';
      return `
      <tr>
        <td>${fmtDate(l.posting_date || l.created_at)}</td>
        <td>${ptLabel(l.posting_type)}${l.remarks ? ` â€” ${l.remarks}` : ''}</td>
        <td class="amt" style="color:${netColor}">${fmt(Math.abs(net))}${net < 0 ? ' Cr' : ''}</td>
      </tr>`;
    }).join('');

    const invoiceNo = `INV-${folio.reservation_no || folio.stay_id || ''}`;
    const today = fmtDateMedium(new Date());

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Invoice ${invoiceNo}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #111; padding: 20px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid var(--theme-primary); padding-bottom: 12px; margin-bottom: 14px; }
  .hotel-name { font-size: 18px; font-weight: bold; color: var(--theme-primary); }
  .hotel-sub { font-size: 11px; color: #555; margin-top: 3px; }
  .inv-title { font-size: 15px; font-weight: bold; color: var(--theme-primary); text-align: right; }
  .inv-meta { font-size: 11px; color: #555; text-align: right; margin-top: 3px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 14px; }
  .info-box { border: 1px solid #d1d5db; border-radius: 5px; padding: 8px 10px; }
  .info-box-title { font-size: 10px; font-weight: bold; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 5px; }
  .info-row { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 2px; }
  .info-row span:first-child { color: #6b7280; }
  .info-row span:last-child { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  thead tr { background: var(--theme-primary); color: #fff; }
  th { padding: 6px 8px; text-align: left; font-size: 11px; }
  td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
  .amt { text-align: right; }
  .section-title { font-size: 11px; font-weight: bold; color: var(--theme-text); text-transform: uppercase; letter-spacing: .06em; margin: 10px 0 4px; }
  .summary-box { border: 1px solid #e5e7eb; border-radius: 5px; padding: 8px 12px; margin-left: auto; width: 280px; }
  .sum-row { display: flex; justify-content: space-between; font-size: 12px; padding: 3px 0; border-bottom: 1px solid #f3f4f6; }
  .sum-row.total { font-weight: bold; font-size: 13px; color: var(--theme-primary); border-top: 2px solid var(--theme-primary); border-bottom: none; padding-top: 5px; }
  .sum-row.balance-due { font-weight: bold; font-size: 13px; color: ${balance > 0 ? '#991b1b' : '#166534'}; }
  .footer { margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 8px; font-size: 10px; color: #9ca3af; text-align: center; }
  .print-btn { display: block; margin: 0 auto 16px; padding: 6px 20px; background: var(--theme-primary); color: #fff; border: none; border-radius: 5px; font-size: 13px; cursor: pointer; }
  @media print { .print-btn { display: none; } }
</style></head><body>
<button class="print-btn" onclick="window.print()">🖨 Print / Save PDF</button>

<div class="header">
  <div>
    <div class="hotel-name">${hotelName}</div>
    <div class="hotel-sub">${hotelCity}</div>
  </div>
  <div>
    <div class="inv-title">TAX INVOICE</div>
    <div class="inv-meta">Invoice No: <b>${invoiceNo}</b></div>
    <div class="inv-meta">Date: <b>${today}</b></div>
  </div>
</div>

<div class="info-grid">
  <div class="info-box">
    <div class="info-box-title">Guest Details</div>
    <div class="info-row"><span>Name</span><span>${folio.primary_guest_name || '-'}</span></div>
    ${folio.mobile_number ? `<div class="info-row"><span>Mobile</span><span>${folio.mobile_number}</span></div>` : ''}
    <div class="info-row"><span>Reservation</span><span>${folio.reservation_no || '-'}</span></div>
    <div class="info-row"><span>Room</span><span>${folio.room_number || '-'} ${folio.room_type_name ? `(${folio.room_type_name})` : ''}</span></div>
    <div class="info-row"><span>Check-In</span><span>${fmtDate(folio.checkin_datetime)}</span></div>
    <div class="info-row"><span>Check-Out</span><span>${fmtDate(folio.expected_checkout_datetime)}</span></div>
  </div>
  <div class="info-box">
    <div class="info-box-title">Bill To — ${billToLabel}</div>
    <div style="font-weight:700; font-size:13px; margin-bottom:4px;">${billToName}</div>
    ${billTo !== 'SELF' ? `<div style="font-size:11px;color:#6b7280;">Invoice raised to ${billToLabel.toLowerCase()} account</div>` : ''}
  </div>
</div>

<div class="section-title">Charges</div>
<table>
  <thead><tr>
    <th>Date</th><th>Description</th>
    <th class="amt">Amount</th><th class="amt">CGST</th><th class="amt">SGST</th><th class="amt">IGST</th>
  </tr></thead>
  <tbody>
    ${chargeRows || '<tr><td colspan="6" style="text-align:center;color:#9ca3af">No charges posted</td></tr>'}
  </tbody>
</table>

${paymentLines.length ? `
<div class="section-title">Payments Received</div>
<table>
  <thead><tr><th>Date</th><th>Description</th><th class="amt">Amount</th></tr></thead>
  <tbody>${paymentRows}</tbody>
</table>` : ''}
${allowanceLines.length ? `
<div class="section-title">Allowances / Waivers</div>
<table>
  <thead><tr><th>Date</th><th>Description</th><th class="amt">Amount</th></tr></thead>
  <tbody>${allowanceRows}</tbody>
</table>` : ''}
${linkedPendingLines.length ? `
<div class="section-title">Linked Child Postings (Not Routed)</div>
<table>
  <thead><tr><th>Date</th><th>Description</th><th class="amt">Net Impact</th></tr></thead>
  <tbody>${linkedPendingRows}</tbody>
</table>
<div style="font-size:11px;color:#6b7280;margin-top:-6px;margin-bottom:10px;">
  Note: These linked child entries are excluded from this folio payable amount until routing is posted.
</div>` : ''}

<div class="summary-box">
  <div class="sum-row"><span>Total Charges</span><span>${fmt(totalCharges)}</span></div>
  ${totalTax > 0 ? `<div class="sum-row"><span>Total Tax (GST)</span><span>${fmt(totalTax)}</span></div>` : ''}
  ${totalAllowances > 0 ? `<div class="sum-row"><span>Allowances / Waivers</span><span style="color:#b45309">-${fmt(totalAllowances)}</span></div>` : ''}
  <div class="sum-row"><span>Payments Received</span><span style="color:#166534">${fmt(totalPayments)}</span></div>
  <div class="sum-row ${balance > 0 ? 'balance-due' : 'total'}">
    <span>${balance > 0 ? 'Balance Due' : 'Credit Balance'}</span>
    <span>${fmt(Math.abs(balance))}</span>
  </div>
</div>

<div class="footer">${hotelName} · ${hotelCity} · Thank you for your stay</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=800,height=700');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const openPostingModal = (presetPostingType = 'MANUAL') => {
    if (!activeRoom?.stay_id || !roomFolio?.folio) return;
    const isMinibarPosting = String(presetPostingType || '').toUpperCase() === 'MINIBAR_ROOM_CHARGE';
    const postingTypeUpper = String(presetPostingType || 'MANUAL').toUpperCase();
    const activeTaxProfiles = (Array.isArray(postingTaxProfiles) ? postingTaxProfiles : []).filter((row) => row?.is_active !== false);
    const defaultTaxProfile = activeTaxProfiles.find((row) => String(row.tax_regime || '').toUpperCase() === 'GST') || activeTaxProfiles[0] || null;
    const defaultGstPercent = defaultTaxProfile ? resolveEffectiveTaxPercent(defaultTaxProfile) : 0;
    const matchedMasterService = postingTypeOptions.find((row) => String(row?.posting_type || '').toUpperCase() === postingTypeUpper) || null;
    setPostingModal({
      stay_id: activeRoom.stay_id,
      folio_id: roomFolio.folio.folio_id,
      folio_label: roomFolio.folio.folio_label || 'Main Folio'
    });
    setPostingForm({
      service_key: matchedMasterService?.service_key || '',
      posting_type: presetPostingType || 'MANUAL',
      source_module: matchedMasterService?.source_module || (isMinibarPosting ? 'HOUSEKEEPING' : 'FRONT_OFFICE'),
      allowance_mode: 'PERCENT',
      allowance_percent: '',
      allowance_base_amount: String(Math.max(0, Number(roomFolio?.folio?.balance || 0)).toFixed(2)),
      tax_profile_id: defaultTaxProfile ? String(defaultTaxProfile.id) : '',
      debit_amount: '',
      credit_amount: '',
      revenue_code: matchedMasterService?.revenue_code || '',
      revenue_description: matchedMasterService?.revenue_description || '',
      tax_mode: 'EXCLUSIVE',
      gst_percent: defaultTaxProfile ? String(defaultGstPercent) : '',
      cgst_amount: '',
      sgst_amount: '',
      igst_amount: '',
      tax_amount: '',
      reference_type: '',
      reference_id: '',
      remarks: '',
      payment_mode: 'CASH'
    });
    setPostingError('');
  };

  // Admin-only: hard-delete a folio_ledger row. Backend captures a snapshot
  // in pms_folio_ledger_void_log. Recommended path for non-admin staff is to
  // post an ALLOWANCE counter-entry instead.
  const voidLedgerRow = async (line) => {
    if (!line || !line.ledger_id) return;
    const human = `${line.posting_type || 'Posting'} ${Number(line.debit_amount || 0) > 0 ? '₹' + Number(line.debit_amount).toFixed(2) : (Number(line.credit_amount || 0) > 0 ? '-₹' + Number(line.credit_amount).toFixed(2) : '')}`;
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Void this posting?\n\n  ${human}\n  ${line.remarks || ''}\n\nThis is a HARD DELETE (admin-only). A snapshot is kept in the audit log.\n\nEnter reason (required, min 3 chars):`);
    if (reason === null) return;
    const trimmed = String(reason || '').trim();
    if (trimmed.length < 3) { alert('Reason is required (min 3 characters)'); return; }
    try {
      // Note: the shared headers() helper in this component doesn't set
      // Content-Type, so Express's json body-parser would silently drop the
      // body and the backend would see reason=undefined → "min 3 characters"
      // error. Always add Content-Type when sending a JSON body.
      const res = await fetch(`/api/v1/pms/folio-ledger/${line.ledger_id}/void`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: trimmed })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Void failed (${res.status})`);
      // Refresh folio by re-fetching via the same path the modal uses.
      const stayId = roomFolio?.stay_id || activeRoom?.stay_id;
      if (stayId) {
        try {
          const r2 = await fetch(`/api/v1/pms/stays/${stayId}/folio`, { headers: headers() });
          const d2 = await r2.json();
          if (r2.ok) setRoomFolio(d2);
        } catch (_) { /* non-critical */ }
      }
    } catch (e) {
      alert(`Could not void posting: ${e.message}`);
    }
  };

  const submitPosting = async () => {
    if (!postingModal) return;
    setPostingBusy(true);
    setPostingError('');
    try {
      const isAllowancePosting = String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE';
      const debitAmount = isAllowancePosting ? 0 : (postingForm.debit_amount ? Number(postingForm.debit_amount) : 0);
      const creditAmount = isAllowancePosting ? (postingForm.credit_amount ? Number(postingForm.credit_amount) : 0) : (postingForm.credit_amount ? Number(postingForm.credit_amount) : 0);
      if (isAllowancePosting && (!Number.isFinite(creditAmount) || creditAmount <= 0)) {
        throw new Error('Allowance amount must be greater than 0');
      }
      const payload = {
        posting_type: postingForm.posting_type || 'MANUAL',
        source_module: postingForm.source_module || 'FRONT_OFFICE',
        debit_amount: debitAmount,
        credit_amount: creditAmount,
        debit_includes_tax: !isAllowancePosting && String(postingForm.tax_mode || 'EXCLUSIVE').toUpperCase() === 'INCLUSIVE',
        revenue_code: postingForm.revenue_code || '',
        revenue_description: postingForm.revenue_description || '',
        gst_percent: isAllowancePosting ? 0 : (postingForm.gst_percent ? Number(postingForm.gst_percent) : 0),
        cgst_amount: isAllowancePosting ? 0 : (postingForm.cgst_amount ? Number(postingForm.cgst_amount) : 0),
        sgst_amount: isAllowancePosting ? 0 : (postingForm.sgst_amount ? Number(postingForm.sgst_amount) : 0),
        igst_amount: isAllowancePosting ? 0 : (postingForm.igst_amount ? Number(postingForm.igst_amount) : 0),
        tax_amount: isAllowancePosting ? 0 : (postingForm.tax_amount ? Number(postingForm.tax_amount) : 0),
        reference_type: postingForm.reference_type || '',
        reference_id: postingForm.reference_id || '',
        remarks: postingForm.remarks || '',
        // Backend ignores this for non-payment posting types — see PAYMENT_POSTING_TYPES.
        payment_mode: postingForm.payment_mode || 'CASH'
      };
      const res = await fetch(`/api/v1/pms/folios/${postingModal.folio_id}/postings`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Posting failed');
      const routingResult = data?.routing || null;
      setPostingModal(null);
      // Reload folio (preserve active tab)
      const reloadFid = activeFolioId || postingModal.folio_id;
      const folioRes = await fetch(`/api/v1/pms/stays/${postingModal.stay_id}/folio${reloadFid ? `?folio_id=${reloadFid}` : ''}`, { headers: headers() });
      const folioData = await folioRes.json();
      if (folioRes.ok) {
        setRoomFolio(folioData);
        setActiveFolioId(folioData?.folio?.folio_id || null);
      }
      // If routing happened, show notification and pre-warm master folio cache
      if (routingResult?.txn) {
        const targetStayId = routingResult.txn.target_folio_id
          ? rack.find((r) => r.stay_id && String(r.stay_id) !== String(postingModal.stay_id) && r.stay_id)?.stay_id
          : null;
        setPostingRoutingMsg({
          routed_amount: routingResult.txn.routed_amount,
          target_folio: routingResult.target_folio,
        });
      } else {
        setPostingRoutingMsg(null);
      }
    } catch (err) {
      setPostingError(err?.message || 'Posting failed');
    } finally {
      setPostingBusy(false);
    }
  };

  const reloadRoomFolioByStay = async (stayId, folioId) => {
    if (!stayId) return;
    const url = folioId
      ? `/api/v1/pms/stays/${stayId}/folio?folio_id=${folioId}`
      : `/api/v1/pms/stays/${stayId}/folio`;
    const folioRes = await fetch(url, { headers: headers() });
    const folioData = await folioRes.json().catch(() => null);
    if (folioRes.ok) {
      setRoomFolio(folioData);
      setActiveFolioId(folioData?.folio?.folio_id || null);
    }
  };

  const openSplitModal = () => {
    const stayId = coModal?.stay_id || activeRoom?.stay_id;
    const folioData = coFolio?.folio || roomFolio?.folio;
    const allLines = (Array.isArray(coFolio?.lines) && coFolio.lines.length)
      ? coFolio.lines
      : (Array.isArray(roomFolio?.lines) ? roomFolio.lines : []);
    const ledgerLines = allLines.filter((line) => {
      const ledgerIdNum = Number(line?.ledger_id);
      return Number.isInteger(ledgerIdNum) && ledgerIdNum > 0;
    });
    const movableLines = ledgerLines.filter((line) => !line?.linked_not_routed);
    const excludedCount = Math.max(0, ledgerLines.length - movableLines.length);
    if (!stayId || !folioData || !ledgerLines.length) {
      setError('No ledger lines available for split.');
      return;
    }
    const splitNoSeed = {};
    ledgerLines.forEach((line) => {
      const ledgerIdNum = Number(line?.ledger_id);
      const splitNo = Number(line?.split_no);
      splitNoSeed[ledgerIdNum] = Number.isInteger(splitNo) && splitNo > 0 ? splitNo : 1;
    });
    setSplitModal({
      stay_id: stayId,
      folio_id: folioData.folio_id,
      folio_label: folioData.folio_label || 'Main Folio',
      room_number: roomFolio?.room_number || activeRoom?.room_number || '-',
      move_lines: movableLines,
      split_lines: ledgerLines,
      excluded_count: excludedCount
    });
    setSplitMode('MOVE_LINES');
    setSplitSelectedLines(new Set());
    setSplitNoDraftByLedger(splitNoSeed);
    setSplitNoBaseByLedger(splitNoSeed);
    setSplitTargetAction('CREATE_NEW');
    setSplitTargetFolioId('');
    setSplitTargetLabel('');
    setSplitError('');
    setSplitStayFolios([]);
    // Fetch all folios for this stay (for "Move to Existing Folio" dropdown)
    setSplitFoliosLoading(true);
    fetch(`/api/v1/pms/stays/${stayId}/folios`, { headers: headers() })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data.folios)) {
          // Exclude the source folio from dropdown options
          setSplitStayFolios(data.folios.filter(fo => Number(fo.folio_id) !== Number(folioData.folio_id)));
        }
      })
      .catch(() => {})
      .finally(() => setSplitFoliosLoading(false));
  };

  const submitSplit = async () => {
    if (!splitModal) return;
    let payload = null;
    if (splitMode === 'SET_SPLIT_NO') {
      const splitRows = (Array.isArray(splitModal.split_lines) ? splitModal.split_lines : []).map((line) => {
        const ledgerId = Number(line.ledger_id);
        const splitNoRaw = splitNoDraftByLedger[ledgerId];
        const splitNoParsed = Number(splitNoRaw);
        const splitNo = Number.isInteger(splitNoParsed) && splitNoParsed > 0 ? splitNoParsed : 1;
        return { ledger_id: ledgerId, split_no: splitNo };
      });
      if (!splitRows.length) {
        setSplitError('No ledger lines available to assign split number');
        return;
      }
      const changedRows = splitRows.filter((row) => row.split_no !== Number(splitNoBaseByLedger[row.ledger_id] || 1));
      if (!changedRows.length) {
        setSplitError('No split number changes to save');
        return;
      }
      payload = { split_rows: changedRows };
    } else {
      if (splitSelectedLines.size === 0) {
        setSplitError('Select at least one ledger line to split');
        return;
      }
      if (splitTargetAction === 'EXISTING_FOLIO' && !(Number(splitTargetFolioId) > 0)) {
        setSplitError('Enter a valid target folio ID');
        return;
      }
      payload = {
        ledger_ids: Array.from(splitSelectedLines),
        action: splitTargetAction,
        target_folio_id: splitTargetAction === 'EXISTING_FOLIO' ? Number(splitTargetFolioId) : undefined,
        folio_label: splitTargetAction === 'CREATE_NEW' ? splitTargetLabel : undefined
      };
    }
    setSplitBusy(true);
    setSplitError('');
    try {
      const res = await fetch(`/api/v1/pms/folios/${splitModal.folio_id}/split`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Split failed');
      setSplitModal(null);
      // Reload folio — stay on current folio tab (for SET_SPLIT_NO) or switch to source folio (for MOVE_LINES)
      const reloadFolioId = splitMode === 'SET_SPLIT_NO' ? splitModal.folio_id : splitModal.folio_id;
      const folioUrl = `/api/v1/pms/stays/${splitModal.stay_id}/folio?folio_id=${reloadFolioId}`;
      const folioRes = await fetch(folioUrl, { headers: headers() });
      const folioData = await folioRes.json();
      if (folioRes.ok) {
        setRoomFolio(folioData);
        setActiveFolioId(folioData?.folio?.folio_id || null);
        if (coModal?.stay_id && Number(coModal.stay_id) === Number(splitModal.stay_id)) {
          setCoFolio(folioData);
        }
      }
    } catch (err) {
      setSplitError(err?.message || 'Split failed');
    } finally {
      setSplitBusy(false);
    }
  };

  const openRouteModal = () => {
    if (!activeRoom?.stay_id || !roomFolio?.folio) return;
    const folioBalance = Number(roomFolio?.folio?.balance || 0);
    const pendingAmount = Math.max(0, folioBalance);
    setRouteModal({
      stay_id: activeRoom.stay_id,
      folio_id: roomFolio.folio.folio_id,
      folio_label: roomFolio.folio.folio_label || 'Main Folio'
    });
    setRouteForm({
      route_to: 'SELF',
      party_id: '',
      amount: pendingAmount > 0 ? toMoney(pendingAmount) : '',
      reference_no: '',
      remarks: '',
      auto_transfer: true
    });
    setRouteError('');
  };

  const submitRoute = async () => {
    if (!routeModal) return;
    const routeTo = String(routeForm.route_to || '').toUpperCase();
    const routeRequiresParty = routeTo !== 'SELF';
    if (!routeTo) {
      setRouteError('Select route type');
      return;
    }
    if (routeRequiresParty && !(Number(routeForm.party_id) > 0)) {
      setRouteError('Party ID is required for selected route');
      return;
    }
    setRouteBusy(true);
    setRouteError('');
    try {
      const payload = {
        route_to: routeTo,
        amount: routeForm.amount ? Number(routeForm.amount) : undefined,
        reference_no: (routeForm.reference_no || '').trim() || undefined,
        remarks: (routeForm.remarks || '').trim() || undefined,
        auto_transfer: !!routeForm.auto_transfer
      };
      if (routeRequiresParty) payload.party_id = Number(routeForm.party_id);

      const res = await fetch(`/api/v1/pms/folios/${routeModal.folio_id}/route`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Route failed');
      setRouteModal(null);
      await reloadRoomFolioByStay(routeModal.stay_id);
    } catch (err) {
      setRouteError(err?.message || 'Route failed');
    } finally {
      setRouteBusy(false);
    }
  };

  const openTransferModal = () => {
    if (!activeRoom?.stay_id || !roomFolio?.folio) return;
    const folioBalance = Number(roomFolio?.folio?.balance || 0);
    const pendingAmount = Math.max(0, folioBalance);
    const billTo = String(roomFolio?.bill_to || '').toUpperCase();
    const corporateId = Number(roomFolio?.corporate_id || 0);
    const travelAgentId = Number(roomFolio?.travel_agent_id || 0);

    let defaultPartyType = 'CORPORATE';
    let defaultPartyId = '';
    if (billTo === 'CORPORATE' && corporateId > 0) {
      defaultPartyType = 'CORPORATE';
      defaultPartyId = String(corporateId);
    } else if (travelAgentId > 0) {
      defaultPartyType = 'TRAVEL_AGENT';
      defaultPartyId = String(travelAgentId);
    } else if (corporateId > 0) {
      defaultPartyType = 'CORPORATE';
      defaultPartyId = String(corporateId);
    }

    setTransferModal({
      stay_id: activeRoom.stay_id,
      folio_id: roomFolio.folio.folio_id,
      folio_label: roomFolio.folio.folio_label || 'Main Folio'
    });
    setTransferForm({
      party_type: defaultPartyType,
      party_id: defaultPartyId,
      amount: pendingAmount > 0 ? toMoney(pendingAmount) : '',
      reference_no: '',
      remarks: ''
    });
    setTransferError('');
  };

  const submitTransfer = async () => {
    if (!transferModal) return;
    if (!(Number(transferForm.party_id) > 0)) {
      setTransferError('Party ID is required');
      return;
    }
    const amount = Number(transferForm.amount || 0);
    if (!(amount > 0)) {
      setTransferError('Amount must be greater than zero');
      return;
    }

    setTransferBusy(true);
    setTransferError('');
    try {
      const payload = {
        folio_id: transferModal.folio_id,
        party_type: String(transferForm.party_type || '').toUpperCase(),
        party_id: Number(transferForm.party_id),
        amount,
        reference_no: (transferForm.reference_no || '').trim() || undefined,
        remarks: (transferForm.remarks || '').trim() || undefined
      };
      const res = await fetch('/api/v1/pms/city-ledger/transfer', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Transfer failed');
      setTransferModal(null);
      await reloadRoomFolioByStay(transferModal.stay_id);
    } catch (err) {
      setTransferError(err?.message || 'Transfer failed');
    } finally {
      setTransferBusy(false);
    }
  };

  const openGuestTransferModal = () => {
    const stayId = coModal?.stay_id || activeRoom?.stay_id;
    const folioData = coFolio?.folio || roomFolio?.folio;
    const allLines = (Array.isArray(coFolio?.lines) && coFolio.lines.length)
      ? coFolio.lines : (Array.isArray(roomFolio?.lines) ? roomFolio.lines : []);
    if (!stayId || !folioData) { setError('No folio available for transfer.'); return; }
    const NON_TRANSFERABLE = new Set(['TRANSFER_OUT', 'TRANSFER_IN', 'ROUTED_OUT', 'ROUTED_IN', 'PAYMENT', 'ADVANCE', 'DEPOSIT']);
    const transferableLines = allLines.filter((line) =>
      Number(line?.ledger_id) > 0 &&
      Number(line?.debit_amount || 0) > 0 &&
      !NON_TRANSFERABLE.has(String(line?.posting_type || '').toUpperCase())
    );
    setGuestTransferModal({
      stay_id: stayId,
      folio_id: folioData.folio_id,
      folio_label: folioData.folio_label || 'Main Folio',
      room_number: roomFolio?.room_number || activeRoom?.room_number || '-',
      lines: transferableLines
    });
    setGuestTransferSelected(new Set());
    setGuestTransferTargetStayId('');
    setGuestTransferRemarks('');
    setGuestTransferError('');
    setGuestTransferSuccess('');
  };

  const submitGuestTransfer = async () => {
    if (!guestTransferModal) return;
    if (guestTransferSelected.size === 0) { setGuestTransferError('Select at least one charge to transfer.'); return; }
    if (!Number(guestTransferTargetStayId) > 0) { setGuestTransferError('Select a destination guest.'); return; }
    setGuestTransferBusy(true);
    setGuestTransferError('');
    setGuestTransferSuccess('');
    try {
      const res = await fetch(`/api/v1/pms/folios/${guestTransferModal.folio_id}/transfer-to-guest`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_stay_id: Number(guestTransferTargetStayId),
          ledger_ids: Array.from(guestTransferSelected),
          remarks: guestTransferRemarks || 'Inter-guest folio transfer'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Transfer failed');
      setGuestTransferSuccess(data.message || 'Transfer successful');
      // Reload source folio (preserve active tab)
      const gtReloadFid = activeFolioId || '';
      const folioRes = await fetch(`/api/v1/pms/stays/${guestTransferModal.stay_id}/folio${gtReloadFid ? `?folio_id=${gtReloadFid}` : ''}`, { headers: headers() });
      const folioData = await folioRes.json();
      if (folioRes.ok) {
        setRoomFolio(folioData);
        setActiveFolioId(folioData?.folio?.folio_id || null);
        if (coModal?.stay_id && Number(coModal.stay_id) === Number(guestTransferModal.stay_id)) setCoFolio(folioData);
      }
      setTimeout(() => setGuestTransferModal(null), 2200);
    } catch (err) {
      setGuestTransferError(err?.message || 'Transfer failed');
    } finally {
      setGuestTransferBusy(false);
    }
  };

  const getPartyMasterOptions = (partyType) => {
    const normalized = String(partyType || '').toUpperCase();
    if (normalized === 'CORPORATE') {
      return (Array.isArray(corporates) ? corporates : [])
        .filter((row) => row?.is_active !== false)
        .map((row) => ({
          id: row.corporate_id,
          label: `${row.corporate_code || `CORP-${row.corporate_id}`} - ${row.corporate_name || 'Corporate'}`
        }));
    }
    if (normalized === 'TRAVEL_AGENT') {
      return (Array.isArray(travelAgents) ? travelAgents : [])
        .filter((row) => row?.is_active !== false)
        .map((row) => ({
          id: row.travel_agent_id,
          label: `${row.agent_code || `TA-${row.travel_agent_id}`} - ${row.agent_name || 'Travel Agent'}`
        }));
    }
    return [];
  };

  const loadLinkedStays = (stayId) => {
    setLinkGroupLoading(true);
    setLinkGroupError('');
    setLinkGroup(null);
    setLinkMembers([]);
    fetch(`/api/v1/pms/linked-stays/${stayId}`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.error || 'Failed to load linked stays');
        setLinkGroup(data.link_group || null);
        setLinkMembers(Array.isArray(data.members) ? data.members : []);
      })
      .catch((err) => setLinkGroupError(err?.message || 'Failed to load linked stays'))
      .finally(() => setLinkGroupLoading(false));
  };

  const loadRoutingRules = (stayId) => {
    setRoutingLoading(true);
    setRoutingError('');
    fetch(`/api/v1/pms/routing-rules?stay_id=${stayId}`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.error || 'Failed to load routing rules');
        setRoutingRules(Array.isArray(data.rules) ? data.rules : []);
      })
      .catch((err) => setRoutingError(err?.message || 'Failed to load routing rules'))
      .finally(() => setRoutingLoading(false));
  };

  const loadAuditLog = (stayId) => {
    setAuditLoading(true);
    setAuditError('');
    fetch(`/api/v1/pms/routing-audit?stay_id=${stayId}`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.error || 'Failed to load audit log');
        setAuditEvents(Array.isArray(data.events) ? data.events : []);
      })
      .catch((err) => setAuditError(err?.message || 'Failed to load audit log'))
      .finally(() => setAuditLoading(false));
  };

  const openRateHistory = (reservationId) => {
    if (!reservationId) return;
    setRateHistoryModal({ reservation_id: reservationId });
    setRateHistoryRows([]);
    setRateHistoryError('');
    setRateHistoryLoading(true);
    fetch(`/api/v1/pms/reservations/${reservationId}/change-log`, { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.error || 'Failed to load rate history');
        setRateHistoryRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => setRateHistoryError(err?.message || 'Failed to load rate history'))
      .finally(() => setRateHistoryLoading(false));
  };

  const openLinkedRoomsModal = () => {
    if (!activeRoom?.stay_id) return;
    const stayId = activeRoom.stay_id;
    const stayNo = activeRoom.stay_no || `Stay #${stayId}`;
    setLinkedRoomsModal({ stay_id: stayId, stay_no: stayNo });
    setLinkTab('LINKED_STAYS');
    setLinkTargetStayId('');
    setLinkReason('');
    setUnlinkMode('STOP_FUTURE_ONLY');
    setLinkError('');
    setNewRoute({ from_stay_id: '', to_stay_id: '', routing_mode: 'ALL_CHARGES', valid_from: '', valid_to: '', percentage: '', transaction_code: '', precedence_rank: '', reason: '' });
    setNewRouteError('');
    setEditRoute(null);
    setEditRouteError('');
    setRouteCloseError('');
    setReversalError('');
    loadLinkedStays(stayId);
    loadRoutingRules(stayId);
    loadAuditLog(stayId);
  };

  const submitLink = async () => {
    if (!linkedRoomsModal || !linkTargetStayId) {
      setLinkError('Select a stay to link');
      return;
    }
    setLinkBusy(true);
    setLinkError('');
    try {
      const res = await fetch('/api/v1/pms/linked-stays/link', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ master_stay_id: linkedRoomsModal.stay_id, child_stay_id: Number(linkTargetStayId), reason: linkReason || 'Linked by front office' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Link failed');
      setLinkTargetStayId('');
      setLinkReason('');
      loadLinkedStays(linkedRoomsModal.stay_id);
      loadAuditLog(linkedRoomsModal.stay_id);
    } catch (err) {
      setLinkError(err?.message || 'Link failed');
    } finally {
      setLinkBusy(false);
    }
  };

  const submitUnlink = async (childStayId) => {
    if (!linkedRoomsModal) return;
    setLinkBusy(true);
    setLinkError('');
    try {
      const res = await fetch('/api/v1/pms/linked-stays/unlink', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ master_stay_id: linkedRoomsModal.stay_id, child_stay_id: childStayId, mode: unlinkMode, reason: linkReason || 'Unlinked by front office' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Unlink failed');
      setLinkReason('');
      loadLinkedStays(linkedRoomsModal.stay_id);
      loadAuditLog(linkedRoomsModal.stay_id);
    } catch (err) {
      setLinkError(err?.message || 'Unlink failed');
    } finally {
      setLinkBusy(false);
    }
  };

  const submitMakeMaster = async (newMasterStayId) => {
    if (!linkedRoomsModal || !linkGroup?.link_group_id) return;
    setLinkBusy(true);
    setLinkError('');
    try {
      const res = await fetch('/api/v1/pms/linked-stays/make-master', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_master_stay_id: linkGroup.master_stay_id, new_master_stay_id: newMasterStayId, reason: linkReason || 'Master changed by front office' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Make master failed');
      setLinkReason('');
      loadLinkedStays(linkedRoomsModal.stay_id);
      loadAuditLog(linkedRoomsModal.stay_id);
    } catch (err) {
      setLinkError(err?.message || 'Make master failed');
    } finally {
      setLinkBusy(false);
    }
  };

  const submitNewRoute = async () => {
    if (!newRoute.from_stay_id || !newRoute.to_stay_id) {
      setNewRouteError('Select From and To stays');
      return;
    }
    setNewRouteBusy(true);
    setNewRouteError('');
    try {
      const payload = {
        from_stay_id: Number(newRoute.from_stay_id),
        to_stay_id: Number(newRoute.to_stay_id),
        routing_mode: newRoute.routing_mode,
        valid_from: newRoute.valid_from || todayIso(),
      };
      if (newRoute.valid_to) payload.valid_to = newRoute.valid_to;
      if (newRoute.routing_mode === 'PERCENT_SPLIT') payload.percentage = Number(newRoute.percentage) || 100;
      if (newRoute.routing_mode === 'BY_CODE_OR_DATE') payload.transaction_code = newRoute.transaction_code;
      if (newRoute.precedence_rank) payload.precedence_rank = Number(newRoute.precedence_rank);
      if (newRoute.reason) payload.reason = newRoute.reason;
      const res = await fetch('/api/v1/pms/routing-rules', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to create routing rule');
      setNewRoute({ from_stay_id: '', to_stay_id: '', routing_mode: 'ALL_CHARGES', valid_from: '', valid_to: '', percentage: '', transaction_code: '', precedence_rank: '', reason: '' });
      loadRoutingRules(linkedRoomsModal.stay_id);
      loadAuditLog(linkedRoomsModal.stay_id);
    } catch (err) {
      setNewRouteError(err?.message || 'Failed to create routing rule');
    } finally {
      setNewRouteBusy(false);
    }
  };

  const submitCloseRoute = async (ruleId) => {
    setRouteCloseBusy(ruleId);
    setRouteCloseError('');
    try {
      const res = await fetch(`/api/v1/pms/routing-rules/${ruleId}/close`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Closed by front office' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to close routing rule');
      loadRoutingRules(linkedRoomsModal.stay_id);
      loadAuditLog(linkedRoomsModal.stay_id);
    } catch (err) {
      setRouteCloseError(err?.message || 'Failed to close routing rule');
    } finally {
      setRouteCloseBusy('');
    }
  };

  const submitEditRoute = async () => {
    if (!editRoute || !linkedRoomsModal) return;
    setEditRouteBusy(true);
    setEditRouteError('');
    try {
      const payload = {
        valid_from: editRoute.valid_from,
        valid_to: editRoute.valid_to || null,
        reason: editRoute.reason || 'Updated by front office',
      };
      if (editRoute.percentage) payload.percentage = Number(editRoute.percentage);
      if (editRoute.precedence_rank) payload.precedence_rank = Number(editRoute.precedence_rank);
      if (editRoute.routing_mode !== 'ALL_CHARGES') payload.transaction_code = editRoute.transaction_code || null;
      const res = await fetch(`/api/v1/pms/routing-rules/${editRoute.routing_rule_id}`, {
        method: 'PUT',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to update routing rule');
      setEditRoute(null);
      loadRoutingRules(linkedRoomsModal.stay_id);
      loadAuditLog(linkedRoomsModal.stay_id);
    } catch (err) {
      setEditRouteError(err?.message || 'Failed to update routing rule');
    } finally {
      setEditRouteBusy(false);
    }
  };

  const submitReversal = async (txnId) => {
    setReversalBusy(String(txnId));
    setReversalError('');
    try {
      const res = await fetch('/api/v1/pms/routing-reversal', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ routing_txn_ids: [txnId], reason: 'Manual reversal by front office' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Reversal failed');
      loadAuditLog(linkedRoomsModal.stay_id);
      loadRoutingRules(linkedRoomsModal.stay_id);
    } catch (err) {
      setReversalError(err?.message || 'Reversal failed');
    } finally {
      setReversalBusy('');
    }
  };

  useEffect(() => {
    if (!selectedProperty?.id) {
      setDefaultCheckoutTime('11:00');
      return;
    }
    fetch('/api/v1/pms/property-config', { headers: headers() })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to load property config');
        const cfgTime = String(data?.default_checkout_time || '').slice(0, 5);
        setDefaultCheckoutTime(/^\d{2}:\d{2}$/.test(cfgTime) ? cfgTime : '11:00');
        // Use property business date if available (set by Night Audit)
        if (data?.business_date && /^\d{4}-\d{2}-\d{2}$/.test(data.business_date)) {
          setDate(data.business_date);
        }
      })
      .catch(() => setDefaultCheckoutTime('11:00'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id, date, rackFloorFilter, rackBlockFilter, rackRoomTypeFilter]);

  useEffect(() => {
    const loadRackFilters = async () => {
      if (!selectedProperty?.id) {
        setRackFloors([]);
        setRackBlocks([]);
        setRackRoomTypes([]);
        return;
      }
      try {
        const [roomsRes, typesRes] = await Promise.all([
          fetch('/api/v1/pms/rooms?all=1', { headers: headers() }),
          fetch('/api/v1/pms/room-types?all=1', { headers: headers() })
        ]);
        const roomsData = await roomsRes.json().catch(() => ([]));
        const typesData = await typesRes.json().catch(() => ([]));
        if (roomsRes.ok) {
          const rows = Array.isArray(roomsData) ? roomsData : [];
          const floors = Array.from(new Set(rows.map((r) => String(r.floor_no || '').trim()).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
          const blocks = Array.from(new Set(rows.map((r) => String(r.block_name || '').trim()).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
          setRackFloors(floors);
          setRackBlocks(blocks);
        } else {
          setRackFloors([]);
          setRackBlocks([]);
        }
        if (typesRes.ok) {
          setRackRoomTypes(Array.isArray(typesData) ? typesData : []);
        } else {
          setRackRoomTypes([]);
        }
      } catch (_) {
        setRackFloors([]);
        setRackBlocks([]);
        setRackRoomTypes([]);
      }
    };
    loadRackFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  // Load housekeeping staff for "Mark Clean" picker
  useEffect(() => {
    if (!selectedProperty?.id) return setHkStaff([]);
    (async () => {
      try {
        const res = await fetch('/api/v1/pms/staff?department=HOUSEKEEPING', { headers: headers() });
        const data = await res.json().catch(() => []);
        setHkStaff(Array.isArray(data) ? data : []);
      } catch (_) { setHkStaff([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  // Night Audit Exceptions — pending arrivals, overdue checkouts, open F&B
  // bills that need front-office attention. Purely computed server-side; this
  // just polls it so the banner stays live without a manual refresh.
  const [nightAuditExceptions, setNightAuditExceptions] = useState({ pending_arrivals: [], overdue_checkouts: [], open_bills: [], rate_inconsistencies: [], total: 0 });
  useEffect(() => {
    if (!selectedProperty?.id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch('/api/v1/pms/night-audit/exceptions', { headers: headers() });
        const data = await res.json().catch(() => null);
        if (!cancelled && res.ok && data) setNightAuditExceptions(data);
      } catch (_) {}
    };
    load();
    const t = setInterval(load, 120000); // refresh every 2 min
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const isLateCheckout = (room) => {
    if (String(room.occupancy_status || '').toUpperCase() !== 'OCCUPIED') return false;
    const dep = room.checkout_date || room.expected_checkout_date || room.departure_date;
    if (!dep) return false;
    const match = String(dep).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return false;
    const [, y, m, d] = match;
    const time = defaultCheckoutTime || '11:00';
    const [hh, mm] = time.split(':').map(Number);
    const expected = new Date(Number(y), Number(m) - 1, Number(d), hh || 11, mm || 0, 0);
    if (new Date() <= expected) return false;
    // If guest checked in AFTER the expected checkout time, they are not "late for checkout"
    // (e.g. guest checks in at 5 PM on departure day — their stay window hasn't expired yet)
    if (room.actual_arrival_datetime) {
      const arrivalDt = new Date(room.actual_arrival_datetime);
      if (!Number.isNaN(arrivalDt.getTime()) && arrivalDt >= expected) return false;
    }
    return true;
  };

  const lateCheckoutRooms = useMemo(
    () => rack.filter((r) => isLateCheckout(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rack, defaultCheckoutTime]
  );

  const updateHousekeeping = async (roomId, status, e, staffId, remarks) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!roomId) return;
    setHkUpdatingRoomId(String(roomId));
    try {
      const res = await fetch(`/api/v1/pms/rooms/${roomId}/housekeeping`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, staff_id: staffId || null, remarks: remarks || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update housekeeping status');
      if (selectedProperty?.module_housekeeping_enabled && String(status).toUpperCase() === 'DIRTY') {
        await fetch('/api/v1/pms/housekeeping/tasks', {
          method: 'POST',
          headers: { ...headers(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ room_id: roomId, priority: 'NORMAL', source: 'FRONT_OFFICE', notes: 'Marked needs cleaning from Command Center' })
        }).catch(() => {});
      }
      load();
    } catch (err) {
      setError(err?.message || 'Failed to update housekeeping status');
    } finally {
      setHkUpdatingRoomId('');
    }
  };

  const tileAccent = (row) => {
    const maint = String(row.maintenance_status || '').toUpperCase();
    const occ = String(row.occupancy_status || '').toUpperCase();
    if (maint === 'OUT_OF_ORDER' || maint === 'PAINTING' || maint === 'BLOCKED')
      return { border: '#ef4444', cardBg: '#fef2f2', badgeBg: '#ef4444', badgeText: '#fff', label: 'OUT OF ORDER' };
    if (occ === 'OCCUPIED')
      return { border: '#3b82f6', cardBg: '#ffffff', badgeBg: '#3b82f6', badgeText: '#fff', label: 'OCCUPIED' };
    if (occ === 'RESERVED')
      return { border: '#8b5cf6', cardBg: '#faf5ff', badgeBg: '#8b5cf6', badgeText: '#fff', label: 'RESERVED' };
    const resStatus = String(row.reservation_status || '').toUpperCase();
    if (row.reservation_id && ['CONFIRMED', 'RESERVED', 'GUARANTEED'].includes(resStatus))
      return { border: '#8b5cf6', cardBg: '#faf5ff', badgeBg: '#8b5cf6', badgeText: '#fff', label: 'RESERVED' };
    const hk = String(row.housekeeping_status || '').toUpperCase();
    if (hk === 'DIRTY')
      return { border: '#f59e0b', cardBg: '#fffbeb', badgeBg: '#f59e0b', badgeText: '#fff', label: 'DIRTY' };
    return { border: '#22c55e', cardBg: '#f0fdf4', badgeBg: '#22c55e', badgeText: '#fff', label: 'VACANT' };
  };

  // Map room_id → highest-priority active request for badge display
  const hkReqByRoom = useMemo(() => {
    const map = {};
    hkRequests.forEach((r) => {
      const rid = String(r.room_id);
      if (!map[rid]) map[rid] = r;
    });
    return map;
  }, [hkRequests]);

  // Map room_number → AI HK priority score for badge display
  const aiHkByRoom = useMemo(() => {
    const map = {};
    aiHkScores.forEach((s) => { if (s.room_number && !map[s.room_number]) map[s.room_number] = s; });
    return map;
  }, [aiHkScores]);

  const priorityBadgeStyle = (reqType) => {
    const t = String(reqType || '').toUpperCase();
    if (t === 'VIP_CLEANING') return { bg: '#7c3aed', label: 'VIP' };
    if (t === 'EARLY_CHECKIN_PREP') return { bg: '#2563eb', label: 'EARLY C/I' };
    return { bg: '#ea580c', label: 'EXPRESS' }; // EXPRESS_CLEANING default
  };

  const availableRooms = useMemo(
    () => rack.filter((r) => String(r.occupancy_status || '').toUpperCase() === 'VACANT'
      && !['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(String(r.maintenance_status || '').toUpperCase())),
    [rack]
  );

  const swappableStays = useMemo(
    () => rack.filter((r) => Number(r.stay_id) > 0
      && Number(r.stay_id) !== Number(activeRoom?.stay_id)
      && String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED'),
    [rack, activeRoom?.stay_id]
  );
  // Room type options for filter dropdown
  const matrixRoomTypeOptions = useMemo(() => {
    const opts = [];
    for (const [rtId, rows] of Object.entries(matrixByRoomType)) {
      if (rows.length > 0) {
        const name = rows[0].room_type_name || `Type ${rtId}`;
        const physical = Number(rows[0].physical_inventory || 0);
        opts.push({ id: rtId, name, physical });
      }
    }
    opts.sort((a, b) => a.name.localeCompare(b.name));
    return opts;
  }, [matrixByRoomType]);

  // Filtered matrix: when a room type is selected, build totals-like array from by_room_type
  const filteredMatrix = useMemo(() => {
    if (matrixRoomTypeFilter === 'ALL') return matrix;
    const rows = matrixByRoomType[matrixRoomTypeFilter];
    if (!rows?.length) return [];
    return rows.map(r => ({
      date: r.date,
      physical: Number(r.physical_inventory || 0),
      ooo: Number(r.out_of_order || 0),
      reserved: Number(r.reserved || 0),
      ari: Number(r.arrivals || 0),
      dep: Number(r.departures || 0),
      inh: Number(r.stayovers || 0),
      sellable: Number(r.sellable_inventory || 0),
      avail: Number(r.available_inventory || 0)
    }));
  }, [matrix, matrixByRoomType, matrixRoomTypeFilter]);

  // Room type summary for first date (when ALL is selected)
  const roomTypeSummary = useMemo(() => {
    if (matrixRoomTypeFilter !== 'ALL' || !matrix.length) return [];
    const firstDate = matrix[0]?.date;
    if (!firstDate) return [];
    const summary = [];
    for (const [rtId, rows] of Object.entries(matrixByRoomType)) {
      const dayRow = rows.find(r => r.date === firstDate);
      if (!dayRow) continue;
      summary.push({
        id: rtId,
        name: dayRow.room_type_name || `Type ${rtId}`,
        total: Number(dayRow.physical_inventory || 0),
        ooo: Number(dayRow.out_of_order || 0),
        sellable: Number(dayRow.sellable_inventory || 0),
        reserved: Number(dayRow.reserved || 0),
        available: Number(dayRow.available_inventory || 0)
      });
    }
    summary.sort((a, b) => a.name.localeCompare(b.name));
    return summary;
  }, [matrix, matrixByRoomType, matrixRoomTypeFilter]);

  const matrixRows = useMemo(() => ([
    { key: 'ari', label: 'Arrival', labelColor: '#1d4ed8' },
    { key: 'dep', label: 'Departure', labelColor: '#6d28d9' },
    { key: 'inh', label: 'In house', labelColor: '#0f766e' },
    { key: 'ooo', label: 'Out of order', labelColor: '#b91c1c' },
    { key: 'avail', label: 'Avail', labelColor: '#166534' }
  ]), []);
  const metricTextColor = (metricKey, value) => {
    const v = Number(value || 0);
    if (metricKey === 'avail') return v <= 5 ? '#b91c1c' : (v <= 12 ? '#b45309' : '#166534');
    if (metricKey === 'ooo') return v > 0 ? '#b91c1c' : '#475569';
    if (metricKey === 'ari') return '#1d4ed8';
    if (metricKey === 'dep') return '#6d28d9';
    if (metricKey === 'inh') return '#0f766e';
    return '#1f2937';
  };
  // ER-MOM-08: color the AI forecast occupancy cell — hotter demand reads warmer
  const forecastOccColor = (pct) => {
    const v = Number(pct || 0);
    if (v >= 80) return '#b91c1c';
    if (v >= 60) return '#c2410c';
    if (v >= 35) return '#166534';
    return '#64748b';
  };
  const hasForecastOverlay = Object.keys(forecastMap).length > 0;

  const ARRIVAL_STATUS_STYLE = {
    upcoming: { label: 'Upcoming', bg: '#eef2f6', color: '#4b5a66' },
    checked_in: { label: 'Checked In', bg: '#e3f2e8', color: '#1f7a43' },
    no_show: { label: 'No Show', bg: '#fbe7e4', color: '#a6362b' },
    cancelled: { label: 'Cancelled', bg: '#eef0f1', color: '#6b7280' }
  };
  const arrivalsTotal = arrivals.length;
  const arrivalsUpcoming = arrivals.filter(r => r.arrival_status === 'upcoming').length;
  const arrivalsCheckedIn = arrivals.filter(r => r.arrival_status === 'checked_in').length;
  const arrivalsNoShow = arrivals.filter(r => r.arrival_status === 'no_show').length;
  const arrivalsProgressPct = arrivalsTotal > 0 ? Math.round((arrivalsCheckedIn / arrivalsTotal) * 100) : 0;
  const arrivalsSources = Array.from(new Set(arrivals.map(r => r.booking_source).filter(Boolean))).sort();
  const filteredArrivals = arrivals.filter(r =>
    (arrivalsStatusFilter === 'all' || r.arrival_status === arrivalsStatusFilter) &&
    (!arrivalsSourceFilter || r.booking_source === arrivalsSourceFilter) &&
    (!arrivalsPaymentFilter || r.payment_status === arrivalsPaymentFilter)
  );

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.7rem', borderBottom: '1px solid var(--theme-border)', paddingBottom: '0.6rem' }}>
        {[{ key: 'ROOM_RACK', label: 'Room Rack' }, { key: 'ARRIVALS', label: 'Arrivals' }].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.45rem 0.95rem', fontSize: '0.85rem', fontWeight: 700, borderRadius: '7px',
              border: '1px solid ' + (activeTab === tab.key ? 'transparent' : 'var(--theme-border)'),
              background: activeTab === tab.key ? 'var(--theme-primary-dark)' : '#fff',
              color: activeTab === tab.key ? '#fff' : 'var(--theme-text-secondary)', cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ROOM_RACK' && (<>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.25rem', flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <h3 style={{ margin: 0, color: 'var(--theme-primary-dark)', fontSize: isMobile ? '0.95rem' : undefined }}>Front Office - Command Center</h3>
          <span className="tag tag-primary" style={{ fontSize: '0.75rem' }}>{rack.length} Room{rack.length !== 1 ? 's' : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
          {onNewReservation && <button onClick={onNewReservation} style={{ padding: isMobile ? '0.45rem 0.8rem' : '0.5rem 1.1rem', fontSize: isMobile ? '0.82rem' : '0.92rem', fontWeight: 700, borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #1e40af, #3b82f6)', color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(30,64,175,0.3)', letterSpacing: '0.02em', transition: 'transform 0.15s, box-shadow 0.15s', flex: isMobile ? 1 : undefined }} onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(30,64,175,0.4)'; }} onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,64,175,0.3)'; }}>+ Reservation</button>}
          {onNewWalkIn && <button onClick={onNewWalkIn} style={{ padding: isMobile ? '0.45rem 0.8rem' : '0.5rem 1.1rem', fontSize: isMobile ? '0.82rem' : '0.92rem', fontWeight: 700, borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #15803d, #22c55e)', color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(21,128,61,0.3)', letterSpacing: '0.02em', transition: 'transform 0.15s, box-shadow 0.15s', flex: isMobile ? 1 : undefined }} onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(21,128,61,0.4)'; }} onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(21,128,61,0.3)'; }}>+ Walk-In</button>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'var(--theme-border-light)', borderRadius: 8, padding: '0.2rem 0.4rem', border: '1px solid var(--theme-border)' }}>
            <button onClick={() => setDate(toIsoDate(addDays(date, -1)))} className="btn-muted btn-sm rounded" title="Previous Day">&larr;</button>
            <button onClick={() => setDate(toIsoDate(new Date()))} className="btn-muted btn-sm rounded">Today</button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ fontSize: '0.82rem' }} />
            <button onClick={() => setDate(toIsoDate(addDays(date, 1)))} className="btn-muted btn-sm rounded" title="Next Day">&rarr;</button>
          </div>
          <button onClick={load} disabled={loading} className="btn-primary btn-sm rounded">{loading ? 'Loading...' : 'Refresh'}</button>
          <button
            onClick={() => setLockReadModalOpen(true)}
            className="btn-muted btn-sm rounded"
            title="Diagnose a guest key card (Read on encoder)"
            style={{ padding: isMobile ? '0.35rem 0.7rem' : '0.4rem 0.9rem' }}
          >
            🔑 Read Card
          </button>
        </div>
      </div>
      {error ? <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div> : null}
      {changeRoomMessage ? <div className="success-banner" style={{ marginTop: '0.5rem' }}>{changeRoomMessage}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit,minmax(155px,1fr))', gap: isMobile ? '0.4rem' : '0.6rem', marginTop: '0.7rem' }}>
        {[
          { label: 'Pax Count', value: summary?.pax_count, bg: '#dbeafe', color: '#2563eb', type: 'pax_count' },
          { label: 'Expected Arrivals', value: summary?.expected_arrivals, bg: '#d1fae5', color: '#059669', type: 'expected_arrivals' },
          { label: 'Expected Departures', value: summary?.expected_departures, bg: '#ede9fe', color: '#7c3aed', type: 'expected_departures' },
          { label: 'Check-In Today', value: summary?.checkin_for_day, bg: '#cffafe', color: '#0891b2', type: 'checkin_today' },
          { label: 'Checkout Today', value: summary?.checkout_for_day, bg: '#fef3c7', color: 'var(--theme-warning)', type: 'checkout_today' },
          { label: 'Stay Over', value: summary?.stay_over, bg: '#fce7f3', color: '#db2777', type: 'stay_over' },
          { label: 'Breakfast Guests', value: summary?.breakfast_guest_count, bg: '#fff7ed', color: '#c2410c', type: 'breakfast_guests' }
        ].map((item) => (
          <div key={item.label} onClick={() => Number(item.value || 0) > 0 && loadSummaryDetail(item.type, item.label, item.color)}
            style={{ background: item.bg, borderRadius: 10, padding: '0.7rem 0.9rem', borderLeft: `4px solid ${item.color}`,
              cursor: Number(item.value || 0) > 0 ? 'pointer' : 'default', transition: 'transform 0.1s' }}
            onMouseEnter={e => { if (Number(item.value || 0) > 0) e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
            <div style={{ fontSize: '0.7rem', color: item.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: item.color }}>{Number(item.value || 0)}</div>
          </div>
        ))}
        {/* Occupancy Card */}
        {(() => {
          const occupied = rack.filter(r => String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED').length;
          const blocked = rack.filter(r => ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(String(r.maintenance_status || '').toUpperCase())).length;
          const total = rack.length;
          const sellable = Math.max(0, total - blocked);
          // Occupancy = occupied ÷ sellable (total − out-of-order) — the standard hotel metric.
          const pct = sellable > 0 ? Math.round((occupied / sellable) * 100) : 0;
          return (
            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '0.7rem 0.9rem', borderLeft: '4px solid #166534' }}>
              <div style={{ fontSize: '0.7rem', color: '#166534', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Occupancy</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#166534' }}>{pct}%</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', marginTop: '0.1rem' }}>{occupied} occupied / {sellable} sellable</div>
              <div style={{ background: 'var(--theme-border)', borderRadius: 99, height: 5, marginTop: '0.35rem', overflow: 'hidden' }}>
                <div style={{ background: pct >= 80 ? 'var(--theme-success)' : pct >= 50 ? 'var(--theme-warning)' : 'var(--theme-danger)', height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 99, transition: 'width 0.5s ease' }} />
              </div>
            </div>
          );
        })()}
      </div>

      {/* Room Status Summary Bar — clickable filter chips */}
      {rack.length > 0 && (() => {
        const vacant = rack.filter(r => {
          const occ = String(r.occupancy_status || '').toUpperCase();
          const maint = String(r.maintenance_status || '').toUpperCase();
          const hk = String(r.housekeeping_status || '').toUpperCase();
          if (occ !== 'VACANT' || ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint) || hk === 'DIRTY') return false;
          const rs = String(r.reservation_status || '').toUpperCase();
          if (r.reservation_id && ['CONFIRMED', 'RESERVED', 'GUARANTEED'].includes(rs)) return false;
          return true;
        }).length;
        const occupied = rack.filter(r => String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED').length;
        const reserved = rack.filter(r => {
          const o = String(r.occupancy_status || '').toUpperCase();
          if (o === 'RESERVED') return true;
          if (o !== 'VACANT') return false;
          return r.reservation_id && ['CONFIRMED', 'RESERVED', 'GUARANTEED'].includes(String(r.reservation_status || '').toUpperCase());
        }).length;
        const blocked = rack.filter(r => ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(String(r.maintenance_status || '').toUpperCase())).length;
        const dirty = rack.filter(r => {
          const occ = String(r.occupancy_status || '').toUpperCase();
          const maint = String(r.maintenance_status || '').toUpperCase();
          return occ === 'VACANT' && !['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint)
            && String(r.housekeeping_status || '').toUpperCase() === 'DIRTY';
        }).length;
        const chipBase = { display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 20, padding: '3px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', border: '2px solid transparent' };
        const chipActive = (isActive) => isActive ? { boxShadow: '0 0 0 2px #1e40af', transform: 'scale(1.05)' } : {};
        const toggleFilter = (val) => setRackStatusFilter(prev => prev === val ? '' : val);
        return (
          <div className="action-bar" style={{ marginTop: '0.6rem', padding: '0.5rem 0.7rem', background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginRight: '0.1rem' }}>Room Status:</span>
            <span onClick={() => setRackStatusFilter('')}
              style={{ ...chipBase, background: !rackStatusFilter ? '#1e293b' : 'var(--theme-border-light)', color: !rackStatusFilter ? '#fff' : '#475569', border: !rackStatusFilter ? '2px solid #1e293b' : '1px solid #cbd5e1' }}>
              All
            </span>
            <span onClick={() => toggleFilter('VACANT')}
              style={{ ...chipBase, background: '#f0fdf4', color: '#166534', border: rackStatusFilter === 'VACANT' ? '2px solid #166534' : '1px solid #bbf7d0', ...chipActive(rackStatusFilter === 'VACANT') }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />{vacant} Vacant
            </span>
            <span onClick={() => toggleFilter('OCCUPIED')}
              style={{ ...chipBase, background: '#eff6ff', color: '#1e40af', border: rackStatusFilter === 'OCCUPIED' ? '2px solid #1e40af' : '1px solid #bfdbfe', ...chipActive(rackStatusFilter === 'OCCUPIED') }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }} />{occupied} Occupied
            </span>
            <span onClick={() => toggleFilter('RESERVED')}
              style={{ ...chipBase, background: '#fffbeb', color: '#92400e', border: rackStatusFilter === 'RESERVED' ? '2px solid #92400e' : '1px solid #fde68a', ...chipActive(rackStatusFilter === 'RESERVED') }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />{reserved} Reserved
            </span>
            <span onClick={() => toggleFilter('DIRTY')}
              style={{ ...chipBase, background: '#fefce8', color: '#854d0e', border: rackStatusFilter === 'DIRTY' ? '2px solid #854d0e' : '1px solid #fde047', ...chipActive(rackStatusFilter === 'DIRTY') }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#eab308' }} />{dirty} Dirty
            </span>
            {blocked > 0 && (
              <span onClick={() => toggleFilter('OOO')}
                style={{ ...chipBase, background: '#fef2f2', color: '#991b1b', border: rackStatusFilter === 'OOO' ? '2px solid #991b1b' : '1px solid #fecaca', ...chipActive(rackStatusFilter === 'OOO') }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />{blocked} Out of Order
              </span>
            )}
          </div>
        );
      })()}

      {/* Late Checkout Alert Banner */}
      {showLateCheckoutAlert && lateCheckoutRooms.length > 0 && (
        <div style={{ marginTop: '0.8rem', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 8, padding: '0.6rem 0.8rem' }}>
          <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 13, marginBottom: 6 }}>
            ⚠ Late Checkout Alert — {lateCheckoutRooms.length} room{lateCheckoutRooms.length > 1 ? 's have' : ' has'} passed checkout time and not yet checked out
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {lateCheckoutRooms.map((r) => (
              <div key={r.room_id}
                onClick={() => { setActiveRoom(r); setChangeTargetRoomId(''); setSwapTargetStayId(''); setChangeRoomError(''); setChangeRoomMessage(''); setSwapRoomError(''); }}
                style={{ background: 'var(--theme-danger)', color: '#fff', borderRadius: 6, padding: '3px 10px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Room {r.room_number}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>{r.primary_guest_name || '-'}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>Dep: {formatDepartureWithPolicyTime(r.checkout_date || r.expected_checkout_date || r.departure_date, defaultCheckoutTime)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Night Audit Exceptions Banner — pending arrivals, overdue checkouts, open F&B bills */}
      {showNightAuditAlert && nightAuditExceptions.total > 0 && (
        <div style={{ marginTop: '0.8rem', background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 8, padding: '0.6rem 0.8rem' }}>
          <div style={{ fontWeight: 700, color: '#92400e', fontSize: 13, marginBottom: 6 }}>
            ⏰ Night Audit Exceptions — {nightAuditExceptions.total} item{nightAuditExceptions.total > 1 ? 's need' : ' needs'} attention
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {nightAuditExceptions.pending_arrivals.map((p) => (
              <div key={`pa-${p.reservation_id}`}
                onClick={() => onEditReservation && onEditReservation(p.reservation_id)}
                style={{ background: p.late_arrival_flag ? '#f59e0b' : '#dc2626', color: '#fff', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, fontWeight: 700, cursor: onEditReservation ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{p.late_arrival_flag ? 'Expected Late Arrival' : 'Pending Arrival'} — {p.reservation_no}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>{p.primary_guest_name || '-'} · Arr: {p.arrival_date}</span>
                {p.late_arrival_note ? <span style={{ fontWeight: 400, fontSize: 11 }}>"{p.late_arrival_note}"</span> : null}
              </div>
            ))}
            {nightAuditExceptions.overdue_checkouts.map((o) => (
              <div key={`oc-${o.stay_id}`}
                onClick={() => {
                  const room = rack.find(r => r.room_number === o.room_number);
                  if (room) { setActiveRoom(room); setChangeTargetRoomId(''); setSwapTargetStayId(''); setChangeRoomError(''); setChangeRoomMessage(''); setSwapRoomError(''); }
                }}
                style={{ background: '#dc2626', color: '#fff', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Overdue Checkout — Room {o.room_number}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>{o.primary_guest_name || '-'} · Dep: {o.departure_date}</span>
              </div>
            ))}
            {nightAuditExceptions.open_bills.map((b) => (
              <div key={`ob-${b.bill_id}`}
                onClick={() => onOpenExceptionBill && onOpenExceptionBill(b)}
                style={{ background: '#7c3aed', color: '#fff', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, fontWeight: 700, cursor: onOpenExceptionBill ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Open Bill — {b.outlet_name || 'Outlet'} {b.table_no ? `Table ${b.table_no}` : ''}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>{b.bill_no || ''} · ₹{Number(b.grand_total || 0).toFixed(2)}</span>
              </div>
            ))}
            {(nightAuditExceptions.rate_inconsistencies || []).map((r) => (
              <div key={`ri-${r.ledger_id}`}
                onClick={() => {
                  const room = rack.find(rm => rm.room_number === r.room_number);
                  if (room) { setActiveRoom(room); setChangeTargetRoomId(''); setSwapTargetStayId(''); setChangeRoomError(''); setChangeRoomMessage(''); setSwapRoomError(''); }
                }}
                title={r.explained ? 'A matching Rate History entry exists for this change' : 'No Rate History entry found for this change — verify it was intentional'}
                style={{ background: r.explained ? '#b45309' : '#dc2626', color: '#fff', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{r.explained ? 'Rate Changed' : 'Rate Changed — Unexplained'} — Room {r.room_number}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>
                  {r.primary_guest_name || '-'} · {r.reservation_no} · {r.posting_date}: ₹{Number(r.old_amount).toFixed(2)} → ₹{Number(r.new_amount).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guest Checkout Requests Alert */}
      {checkoutRequests.length > 0 && (
        <div style={{ marginTop: '0.8rem', background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 8, padding: '0.6rem 0.8rem' }}>
          <div style={{ fontWeight: 700, color: '#92400e', fontSize: 13, marginBottom: 6 }}>
            Checkout Requests — {checkoutRequests.length} guest{checkoutRequests.length > 1 ? 's' : ''} requesting checkout
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {checkoutRequests.map((cr) => (
              <div key={cr.id}
                style={{ background: '#f59e0b', color: '#fff', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, fontWeight: 600, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>Room {cr.room_number || '-'}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>{cr.guest_name || '-'}</span>
                <span style={{ fontWeight: 400, fontSize: 11 }}>
                  {cr.preferred_time === 'NOW' ? 'Wants checkout now' : cr.preferred_time === '30MIN' ? 'In 30 minutes' : 'At checkout time'}
                  {' — ' + (cr.status === 'PENDING' ? 'Pending' : 'Acknowledged')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '0.8rem', padding: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '0.5rem', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '0.4rem' : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="section-head" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Live Room Rack</span>
            <span className="tag tag-muted">{rack.length} rooms</span>
          </div>
          <button onClick={() => setShowPriorityReqForm(true)}
            style={{ padding: '0.3rem 0.7rem', borderRadius: 6, border: '1px solid #ea580c', background: '#fff7ed', color: '#c2410c', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
            Priority Cleaning Request {hkRequests.length > 0 ? `(${hkRequests.length})` : ''}
          </button>
        </div>
        <div className="filter-bar" style={{ padding: '0.5rem 0.6rem', marginBottom: '0.55rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit,minmax(170px,1fr))', gap: '0.45rem', alignItems: 'end' }}>
            <div>
              <span className="form-label">Floor</span>
              <select value={rackFloorFilter} onChange={(e) => setRackFloorFilter(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm w-full">
                <option value="">All Floors</option>
                {rackFloors.map((floor) => <option key={floor} value={floor}>{floor}</option>)}
              </select>
            </div>
            <div>
              <span className="form-label">Block</span>
              <select value={rackBlockFilter} onChange={(e) => setRackBlockFilter(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm w-full">
                <option value="">All Blocks</option>
                {rackBlocks.map((block) => <option key={block} value={block}>{block}</option>)}
              </select>
            </div>
            <div>
              <span className="form-label">Room Type</span>
              <select value={rackRoomTypeFilter} onChange={(e) => setRackRoomTypeFilter(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm w-full">
                <option value="">All Room Types</option>
                {rackRoomTypes.map((rt) => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}{rt.is_active === false ? ' (Inactive)' : ''}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={() => { setRackFloorFilter(''); setRackBlockFilter(''); setRackRoomTypeFilter(''); setRackStatusFilter(''); }} disabled={loading} className="btn-muted btn-sm rounded">
                Reset Filters
              </button>
            </div>
          </div>
        </div>
        {/* Color Legend removed — summary bar above serves as legend + filter */}
        {/* Room Cards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(auto-fill, minmax(170px, 1fr))' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? '0.4rem' : '0.6rem' }}>
          {rack.filter(r => {
            if (!rackStatusFilter) return true;
            const occ = String(r.occupancy_status || '').toUpperCase();
            const maint = String(r.maintenance_status || '').toUpperCase();
            const hk = String(r.housekeeping_status || '').toUpperCase();
            const isMaint = ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint);
            const rs = String(r.reservation_status || '').toUpperCase();
            const hasRes = r.reservation_id && ['CONFIRMED', 'RESERVED', 'GUARANTEED'].includes(rs);
            if (rackStatusFilter === 'VACANT') return occ === 'VACANT' && !isMaint && hk !== 'DIRTY' && !hasRes;
            if (rackStatusFilter === 'OCCUPIED') return occ === 'OCCUPIED';
            if (rackStatusFilter === 'RESERVED') return occ === 'RESERVED' || (occ === 'VACANT' && hasRes);
            if (rackStatusFilter === 'DIRTY') return occ === 'VACANT' && !isMaint && hk === 'DIRTY';
            if (rackStatusFilter === 'OOO') return isMaint;
            return true;
          }).map((r) => {
            const late = isLateCheckout(r);
            const noshowRisk = noshowScores.find(s => s.reservation_id === r.reservation_id);
            const accent = tileAccent(r);
            const occ = String(r.occupancy_status || '').toUpperCase();
            const isVacant = occ === 'VACANT' && !['OUT_OF_ORDER','PAINTING','BLOCKED'].includes(String(r.maintenance_status || '').toUpperCase());
            return (
              <div key={r.room_id}
                onClick={() => { setActiveRoom(r); setChangeTargetRoomId(''); setSwapTargetStayId(''); setChangeRoomError(''); setChangeRoomMessage(''); setSwapRoomError(''); }}
                style={{
                  background: accent.cardBg,
                  border: late ? '2px solid var(--theme-danger)' : `1px solid ${accent.border}33`,
                  borderLeft: `4px solid ${late ? 'var(--theme-danger)' : accent.border}`,
                  borderRadius: '8px',
                  padding: '0.55rem 0.6rem',
                  minHeight: '96px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  display: 'flex', flexDirection: 'column', gap: '0.3rem',
                  transition: 'box-shadow 0.15s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
              >
                {/* Zone A: Room Number + Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, letterSpacing: '-0.01em' }}>{r.room_number}</div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
                    {late && (
                      <span style={{ background: 'var(--theme-danger)', color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4, letterSpacing: '0.05em', lineHeight: 1.2 }}>LATE</span>
                    )}
                    {r.channel_booking_id && (
                      <span style={{ background: '#7c3aed', color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4, lineHeight: 1.2 }}>OTA</span>
                    )}
                    {noshowRisk && (
                      <span style={{ background: noshowRisk.risk_level === 'HIGH' ? 'var(--theme-danger)' : '#ca8a04', color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4, lineHeight: 1.2 }}>RISK</span>
                    )}
                    {hkReqByRoom[String(r.room_id)] && (() => {
                      const pb = priorityBadgeStyle(hkReqByRoom[String(r.room_id)].request_type);
                      return <span style={{ background: pb.bg, color: '#fff', fontSize: '0.64rem', fontWeight: 700,
                        padding: '2px 6px', borderRadius: 4, lineHeight: 1.2 }}>{pb.label}</span>;
                    })()}
                    {aiHkByRoom[r.room_number] && (() => {
                      const aiS = aiHkByRoom[r.room_number];
                      return <span style={{
                        background: aiS.urgency === 'CRITICAL' ? 'var(--theme-danger)' : '#ea580c',
                        color: '#fff', fontSize: '0.64rem', fontWeight: 700,
                        padding: '2px 6px', borderRadius: 4, lineHeight: 1.2
                      }}>AI #{aiS.priority_rank}</span>;
                    })()}
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
                      color: accent.badgeText, background: accent.badgeBg,
                      padding: '2px 8px', borderRadius: 4, lineHeight: 1.2 }}>
                      {accent.label}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.room_type_name}
                </div>

                {/* Block reason on tile */}
                {r.block_reason && ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(String(r.maintenance_status || '').toUpperCase()) && (
                  <>
                    <div style={{ fontSize: '0.74rem', color: '#b91c1c', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={r.block_notes ? `${r.block_reason.replace(/_/g, ' ')} — ${r.block_notes}` : r.block_reason.replace(/_/g, ' ')}>
                      {r.block_reason.replace(/_/g, ' ')}{r.block_notes ? ` — ${r.block_notes}` : ''}
                    </div>
                    {r.block_end_date && (
                      <div style={{ fontSize: '0.7rem', color: '#7c2d12', fontWeight: 500 }}>
                        Blocked till {new Date(r.block_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </div>
                    )}
                  </>
                )}

                {/* Vacant hint + HK toggle */}
                {isVacant && (() => {
                  const isDirty = String(r.housekeeping_status || '').toUpperCase() === 'DIRTY';
                  const isHkBusy = hkUpdatingRoomId === String(r.room_id);
                  return (
                    <div style={{ borderTop: '1px dashed var(--theme-border)', paddingTop: '0.35rem', marginTop: '0.1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}
                      onClick={(e) => e.stopPropagation()}>
                      {isDirty ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hkStaff.length === 0) {
                              updateHousekeeping(r.room_id, 'CLEAN', e);
                            } else {
                              setHkStaffPickRoom({ room_id: r.room_id, room_number: r.room_number });
                              setHkSelectedStaff(hkStaff.length === 1 ? String(hkStaff[0].staff_id) : '');
                              setHkRemarks('');
                            }
                          }}
                          disabled={isHkBusy}
                          style={{
                            padding: '3px 14px', fontSize: '0.7rem', fontWeight: 700, borderRadius: 4,
                            border: '1px solid var(--theme-success)', cursor: isHkBusy ? 'wait' : 'pointer',
                            background: '#22c55e', color: '#fff'
                          }}
                        >
                          {isHkBusy ? 'Updating...' : 'Mark Clean'}
                        </button>
                      ) : (
                        <button
                          onClick={(e) => updateHousekeeping(r.room_id, 'DIRTY', e)}
                          disabled={isHkBusy}
                          style={{
                            padding: '3px 14px', fontSize: '0.7rem', fontWeight: 600, borderRadius: 4,
                            border: '1px solid var(--theme-border)', cursor: isHkBusy ? 'wait' : 'pointer',
                            background: '#fff', color: 'var(--theme-text-secondary)'
                          }}
                        >
                          {isHkBusy ? 'Updating...' : 'Needs Cleaning'}
                        </button>
                      )}
                      <span style={{ fontSize: '0.7rem', color: 'var(--theme-text-muted)' }}>Click card to check in or block</span>
                    </div>
                  );
                })()}

                {/* Zone B: Guest & Booking */}
                {!isVacant && (
                  <div style={{ borderTop: '1px solid var(--theme-border-light)', paddingTop: '0.3rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.primary_guest_name || '-'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.76rem', color: 'var(--theme-text-secondary)' }}>
                        {r.reservation_no || '-'}
                      </span>
                      {formatRackPax(r) !== '-' && (
                        <span style={{ fontSize: '0.76rem', color: 'var(--theme-text-secondary)' }}>
                          {formatRackPax(r)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Zone C: Dates & Meal Plan */}
                {!isVacant && (
                  <div style={{ borderTop: '1px solid var(--theme-border-light)', paddingTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)' }}>
                        In: {fmtDateShortYear(r.actual_arrival_date)}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: late ? '#b91c1c' : 'var(--theme-text-secondary)', fontWeight: late ? 700 : 400 }}>
                        Out: {formatDepartureWithPolicyTime(r.checkout_date || r.expected_checkout_date || r.departure_date, defaultCheckoutTime)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.2rem' }}>
                      <span style={{ fontSize: '0.73rem', color: 'var(--theme-text-muted)', fontStyle: 'italic' }}>
                        {r.meal_plan_name || r.rate_plan_code || ''}
                      </span>
                      {r.channel_booking_id && r.booking_source && (
                        <span style={{ fontSize: '0.7rem', color: '#991b1b', fontWeight: 600 }}>
                          {r.booking_source}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!rack.length ? <div style={{ color: 'var(--theme-text-secondary)', padding: '1rem', fontSize: '0.9rem' }}>No room rack data available</div> : null}
        </div>
      </div>

      <div style={{ marginTop: '0.8rem', border: '1px solid #cbd5e0', borderRadius: '8px', background: '#fff' }}>
        <div style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--theme-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <span style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>
            Room Availability
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.78rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              Room Type
              <select value={matrixRoomTypeFilter} onChange={(e) => setMatrixRoomTypeFilter(e.target.value)} style={{ fontSize: '0.8rem', padding: '0.25rem 0.35rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontWeight: 600 }}>
                <option value="ALL">All Room Types</option>
                {matrixRoomTypeOptions.map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.name} ({rt.physical})</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '0.78rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              From
              <input type="date" value={snapshotFrom} onChange={(e) => { const v = e.target.value; setSnapshotFrom(v); if (snapshotTo && v && snapshotTo < v) setSnapshotTo(v); }} style={{ fontSize: '0.8rem', padding: '0.25rem 0.35rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
            </label>
            <label style={{ fontSize: '0.78rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              To
              <input type="date" value={snapshotTo} onChange={(e) => {
                const maxTo = toIsoDate(addDays(new Date(snapshotFrom), 30));
                setSnapshotTo(e.target.value > maxTo ? maxTo : e.target.value);
              }} min={snapshotFrom} max={toIsoDate(addDays(new Date(snapshotFrom), 30))} style={{ fontSize: '0.8rem', padding: '0.25rem 0.35rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
            </label>
            <button onClick={() => loadSnapshot()} disabled={snapshotLoading} style={{ padding: '0.3rem 0.7rem', borderRadius: '4px', border: 'none', background: 'var(--theme-primary)', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: snapshotLoading ? 0.6 : 1 }}>
              {snapshotLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left', minWidth: '120px', position: 'sticky', left: 0, background: 'var(--theme-primary)', zIndex: 1 }}>Date</th>
              {filteredMatrix.map((m) => (
                <th key={m.date} style={{ padding: '0.45rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {fmtDateShortYear(m.date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixRows.map((row) => (
              <tr key={row.key} style={{ borderBottom: '1px solid var(--theme-border)', background: row.key === 'avail' ? 'var(--theme-bg-light)' : '#fff' }}>
                <td style={{ padding: '0.45rem', fontWeight: 700, color: row.labelColor, textAlign: 'left', whiteSpace: 'nowrap', minWidth: '120px', position: 'sticky', left: 0, background: row.key === 'avail' ? 'var(--theme-bg-light)' : '#fff', zIndex: 1 }}>
                  {row.label}
                </td>
                {filteredMatrix.map((m) => (
                  <td key={`${row.key}-${m.date}`} style={{ padding: '0.45rem', textAlign: 'center', color: metricTextColor(row.key, m[row.key]), fontWeight: row.key === 'avail' ? 700 : 600 }}>
                    {Number(m[row.key] || 0)}
                  </td>
                ))}
              </tr>
            ))}
            {hasForecastOverlay && filteredMatrix.length > 0 && (
              <tr style={{ borderTop: '2px solid #f59e0b', background: '#fffbeb' }}>
                <td style={{ padding: '0.45rem', fontWeight: 700, color: '#b45309', textAlign: 'left', whiteSpace: 'nowrap', minWidth: '120px', position: 'sticky', left: 0, background: '#fffbeb', zIndex: 1 }}
                  title="AI-projected property occupancy for this date (latest forecast). Property-level — not split by room type.">
                  🔮 AI Forecast Occ%
                </td>
                {filteredMatrix.map((m) => {
                  const f = forecastMap[m.date];
                  if (!f) return <td key={`fc-${m.date}`} style={{ padding: '0.45rem', textAlign: 'center', color: '#cbd5e0' }}>–</td>;
                  const pct = Number(f.projected_occupancy || 0);
                  const warn = String(f.warning_type || '').toUpperCase();
                  const isHigh = warn.includes('HIGH');
                  const isLow = warn.includes('LOW');
                  const mark = isHigh ? '▲' : isLow ? '▼' : (warn ? '•' : '');
                  const title = `Projected ${pct}% occ (band ${Number(f.confidence_low || 0)}–${Number(f.confidence_high || 0)}%, ${f.confidence_level || '—'} confidence)`
                    + `\nProj ADR ₹${Number(f.projected_adr || 0).toFixed(0)} · RevPAR ₹${Number(f.projected_revpar || 0).toFixed(0)}`
                    + `\nHorizon ${f.forecast_horizon || '—'} · generated ${String(f.generated_at || '').slice(0, 10)}`
                    + (f.warning_message ? `\n⚠ ${f.warning_message}` : '');
                  return (
                    <td key={`fc-${m.date}`} title={title} style={{ padding: '0.45rem', textAlign: 'center', fontWeight: 700, color: forecastOccColor(pct), whiteSpace: 'nowrap' }}>
                      {pct}%{mark ? <span style={{ marginLeft: 3, color: isHigh ? '#b91c1c' : isLow ? '#0369a1' : '#b45309' }}>{mark}</span> : null}
                    </td>
                  );
                })}
              </tr>
            )}
            {!filteredMatrix.length ? <tr><td style={{ padding: '0.6rem' }} colSpan={2}>No matrix data available</td></tr> : null}
          </tbody>
        </table>
        </div>
        {hasForecastOverlay && (
          <div style={{ padding: '0.25rem 0.6rem 0', fontSize: '0.68rem', color: '#b45309' }}>
            🔮 AI Forecast overlay — projected occupancy from the latest AI batch. Hover a cell for confidence band, ADR/RevPAR &amp; demand notes. ▲ high demand · ▼ low demand.
          </div>
        )}

        {/* Room Type Breakdown Summary (shown when All Room Types selected) */}
        {matrixRoomTypeFilter === 'ALL' && roomTypeSummary.length > 1 && (
          <div style={{ padding: '0.5rem 0.6rem 4rem', borderTop: '1px solid var(--theme-border)' }}>
            <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', marginBottom: '0.3rem' }}>
              Room Type Breakdown {matrix.length > 0 ? `(${fmtDateShortYear(matrix[0].date)})` : ''}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary, #1a365d)' }}>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 700, color: '#fff' }}>Room Type</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#fff' }}>Total</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#fca5a5' }}>OOO</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#fff' }}>Sellable</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#c4b5fd' }}>Reserved</th>
                  <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#86efac' }}>Available</th>
                </tr>
              </thead>
              <tbody>
                {roomTypeSummary.map(rt => (
                  <tr key={rt.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                    onClick={() => setMatrixRoomTypeFilter(String(rt.id))}
                    title={`Click to filter: ${rt.name}`}>
                    <td style={{ padding: '0.3rem 0.5rem', fontWeight: 600, color: '#1a365d' }}>{rt.name}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{rt.total}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: rt.ooo > 0 ? '#b91c1c' : '#94a3b8' }}>{rt.ooo}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{rt.sellable}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: '#6d28d9', fontWeight: 600 }}>{rt.reserved}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: rt.available <= 2 ? '#b91c1c' : '#166534', fontWeight: 700 }}>{rt.available}</td>
                  </tr>
                ))}
                <tr style={{ background: '#f0fdf4', fontWeight: 700 }}>
                  <td style={{ padding: '0.3rem 0.5rem', color: '#1a365d' }}>TOTAL</td>
                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{roomTypeSummary.reduce((s, r) => s + r.total, 0)}</td>
                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: '#b91c1c' }}>{roomTypeSummary.reduce((s, r) => s + r.ooo, 0)}</td>
                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{roomTypeSummary.reduce((s, r) => s + r.sellable, 0)}</td>
                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: '#6d28d9' }}>{roomTypeSummary.reduce((s, r) => s + r.reserved, 0)}</td>
                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: '#166534' }}>{roomTypeSummary.reduce((s, r) => s + r.available, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}

      {activeTab === 'ARRIVALS' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.7rem', flexDirection: isMobile ? 'column' : 'row' }}>
            <h3 style={{ margin: 0, color: 'var(--theme-primary-dark)', fontSize: isMobile ? '0.95rem' : undefined, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Arrivals — {fmtDateDMY ? fmtDateDMY(date) : date}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'var(--theme-border-light)', borderRadius: 8, padding: '0.2rem 0.4rem', border: '1px solid var(--theme-border)' }}>
              <button onClick={() => setDate(toIsoDate(addDays(date, -1)))} className="btn-muted btn-sm rounded" title="Previous Day">&larr;</button>
              <button onClick={() => setDate(toIsoDate(new Date()))} className="btn-muted btn-sm rounded">Today</button>
              <button onClick={() => setDate(toIsoDate(addDays(new Date(), 1)))} className="btn-muted btn-sm rounded">Tomorrow</button>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ fontSize: '0.82rem' }} />
              <button onClick={() => setDate(toIsoDate(addDays(date, 1)))} className="btn-muted btn-sm rounded" title="Next Day">&rarr;</button>
              <button onClick={loadArrivals} disabled={arrivalsLoading} className="btn-primary btn-sm rounded">{arrivalsLoading ? 'Loading...' : 'Refresh'}</button>
            </div>
          </div>

          {arrivalsError ? <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{arrivalsError}</div> : null}

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : '1fr 1fr 1fr 1fr 1.6fr',
            gap: isMobile ? '0.55rem' : '0.75rem',
            marginBottom: '0.9rem'
          }}>
            <div style={{ background: '#eef2f6', borderRadius: 12, padding: '0.85rem 1rem', borderLeft: '4px solid #4b5a66', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', display: 'flex', flexDirection: 'column', gap: '0.3rem', minHeight: '4.6rem', justifyContent: 'center' }}>
              <div style={{ fontSize: '0.68rem', color: '#4b5a66', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Arrivals</div>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#4b5a66', lineHeight: 1 }}>{arrivalsTotal}</div>
            </div>
            <div style={{ background: '#eef2f6', borderRadius: 12, padding: '0.85rem 1rem', borderLeft: '4px solid #4b5a66', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', display: 'flex', flexDirection: 'column', gap: '0.3rem', minHeight: '4.6rem', justifyContent: 'center' }}>
              <div style={{ fontSize: '0.68rem', color: '#4b5a66', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Upcoming</div>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#4b5a66', lineHeight: 1 }}>{arrivalsUpcoming}</div>
            </div>
            <div style={{ background: '#e3f2e8', borderRadius: 12, padding: '0.85rem 1rem', borderLeft: '4px solid #1f7a43', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', display: 'flex', flexDirection: 'column', gap: '0.3rem', minHeight: '4.6rem', justifyContent: 'center' }}>
              <div style={{ fontSize: '0.68rem', color: '#1f7a43', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Checked In</div>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#1f7a43', lineHeight: 1 }}>{arrivalsCheckedIn}</div>
            </div>
            <div style={{ background: '#fbe7e4', borderRadius: 12, padding: '0.85rem 1rem', borderLeft: '4px solid #a6362b', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', display: 'flex', flexDirection: 'column', gap: '0.3rem', minHeight: '4.6rem', justifyContent: 'center' }}>
              <div style={{ fontSize: '0.68rem', color: '#a6362b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>No Show</div>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#a6362b', lineHeight: 1 }}>{arrivalsNoShow}</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 12, padding: '0.85rem 1rem', border: '1px solid var(--theme-border)', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.4rem', minHeight: '4.6rem', gridColumn: isMobile ? '1 / -1' : undefined }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <span>Arrival progress</span><strong style={{ fontSize: '1rem', color: '#1f7a43' }}>{arrivalsProgressPct}%</strong>
              </div>
              <div style={{ height: 8, borderRadius: 100, background: '#eef2f6', overflow: 'hidden', display: 'flex' }}>
                <div style={{ height: '100%', width: `${arrivalsProgressPct}%`, background: '#1f7a43', borderRadius: 100, transition: 'width 0.3s ease' }} />
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--theme-text-secondary)' }}>
                {arrivalsCheckedIn} arrived{arrivalsNoShow > 0 ? ` · ${arrivalsNoShow} no-show` : ''} · {arrivalsUpcoming} still expected
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginRight: '0.1rem' }}>Arrival Status:</span>
            {(() => {
              // Same "colored chip" look as the Room Status bar above, for visual
              // consistency across the two Command Center tabs.
              const chipBase = { display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 20, padding: '3px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', border: '2px solid transparent' };
              const chipActive = (isActive) => isActive ? { boxShadow: '0 0 0 2px currentColor', transform: 'scale(1.05)' } : {};
              const chips = [
                { key: 'all', label: 'All', count: arrivalsTotal, dot: '#475569', bg: '#f1f5f9', fg: '#334155', border: '#cbd5e1' },
                { key: 'upcoming', label: 'Pending', count: arrivalsUpcoming, dot: '#f59e0b', bg: '#fffbeb', fg: '#92400e', border: '#fde68a' },
                { key: 'checked_in', label: 'Checked In', count: arrivalsCheckedIn, dot: '#22c55e', bg: '#f0fdf4', fg: '#166534', border: '#bbf7d0' },
                { key: 'no_show', label: 'No Show', count: arrivalsNoShow, dot: '#ef4444', bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' },
              ];
              return chips.map((c) => {
                const isActive = arrivalsStatusFilter === c.key;
                return (
                  <span key={c.key} onClick={() => setArrivalsStatusFilter(c.key)}
                    style={{ ...chipBase, background: c.bg, color: c.fg, border: isActive ? `2px solid ${c.fg}` : `1px solid ${c.border}`, ...chipActive(isActive) }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot }} />{c.count} {c.label}
                  </span>
                );
              });
            })()}
            <select value={arrivalsSourceFilter} onChange={e => setArrivalsSourceFilter(e.target.value)} style={{ fontSize: '0.78rem', padding: '0.3rem 0.5rem' }}>
              <option value="">Source: All</option>
              {arrivalsSources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={arrivalsPaymentFilter} onChange={e => setArrivalsPaymentFilter(e.target.value)} style={{ fontSize: '0.78rem', padding: '0.3rem 0.5rem' }}>
              <option value="">Payment: All</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="unpaid">Unpaid</option>
            </select>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--theme-text-secondary)' }}>
              Showing {filteredArrivals.length} of {arrivalsTotal}
            </span>
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 880 }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary-dark, #1e3a5f)' }}>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Guest</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Reservation</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Room</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'center', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Tot. Rooms</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Meal Plan</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'center', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Pax</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Source</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Payment</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Status</th>
                  <th style={{ padding: '0.5rem 0.7rem', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: '#fff', fontWeight: 700, letterSpacing: '0.02em' }}>Quick Action</th>
                </tr>
              </thead>
              <tbody>
                {arrivalsLoading ? (
                  <tr><td colSpan={10} style={{ padding: '1.2rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>Loading arrivals…</td></tr>
                ) : filteredArrivals.length === 0 ? (
                  <tr><td colSpan={10} style={{ padding: '1.2rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
                    {arrivalsTotal === 0 ? 'No arrivals for this date.' : 'No arrivals match the current filters.'}
                  </td></tr>
                ) : filteredArrivals.map(row => {
                  const st = ARRIVAL_STATUS_STYLE[row.arrival_status] || ARRIVAL_STATUS_STYLE.upcoming;
                  const payLabel = row.payment_status === 'paid' ? 'Paid' : (row.payment_status === 'partial' ? 'Partial' : 'Unpaid');
                  const payColor = row.payment_status === 'paid' ? '#1f7a43' : (row.payment_status === 'partial' ? '#8a5200' : '#a6362b');
                  const cancelled = row.arrival_status === 'cancelled';
                  return (
                    <tr key={row.reservation_id} style={{ borderTop: '1px solid var(--theme-border)', opacity: cancelled ? 0.6 : 1 }}>
                      <td style={{ padding: '0.5rem 0.7rem', fontWeight: 600, textDecoration: cancelled ? 'line-through' : 'none' }}>{row.guest_name}</td>
                      <td style={{ padding: '0.5rem 0.7rem', color: 'var(--theme-text-secondary)' }}>{row.reservation_no}</td>
                      <td style={{ padding: '0.5rem 0.7rem' }}>
                        {row.room_number || '—'}
                        {row.room_type_name ? <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>{row.room_type_name}</span> : null}
                      </td>
                      <td style={{ padding: '0.5rem 0.7rem', textAlign: 'center', fontWeight: 600 }}>{row.tot_no_rooms || '—'}</td>
                      <td style={{ padding: '0.5rem 0.7rem', color: 'var(--theme-text-secondary)' }}>{row.meal_plan_label || row.meal_plan_code || '—'}</td>
                      <td style={{ padding: '0.5rem 0.7rem', textAlign: 'center' }}>{row.total_pax}</td>
                      <td style={{ padding: '0.5rem 0.7rem' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', background: '#f8fafc', border: '1px solid var(--theme-border)', padding: '0.15rem 0.5rem', borderRadius: 6 }}>
                          {row.booking_source || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem 0.7rem', color: payColor, fontWeight: 600, fontSize: '0.8rem' }}>{payLabel}</td>
                      <td style={{ padding: '0.5rem 0.7rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.25rem 0.6rem', borderRadius: 100, background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem 0.7rem', whiteSpace: 'nowrap' }}>
                        {row.arrival_status === 'upcoming' && (
                          <>
                            <button
                              onClick={() => { setActiveTab('ROOM_RACK'); openCheckInModal({ room_id: row.room_id }); }}
                              disabled={!row.room_id}
                              className="btn-primary btn-sm rounded"
                              style={{ marginRight: '0.35rem' }}
                              title={row.room_id ? 'Check in this reservation' : 'No room assigned yet — assign a room first'}
                            >
                              Check-in
                            </button>
                            <button onClick={() => onEditReservation && onEditReservation(row.reservation_id)} className="btn-muted btn-sm rounded" style={{ marginRight: '0.35rem' }}>Edit</button>
                            <button
                              onClick={() => markNoShow(row.reservation_id)}
                              disabled={noShowBusy === row.reservation_id}
                              className="btn-muted btn-sm rounded"
                            >
                              {noShowBusy === row.reservation_id ? 'Marking…' : 'Mark No Show'}
                            </button>
                          </>
                        )}
                        {row.arrival_status === 'checked_in' && (
                          <>
                            <button
                              onClick={() => { setActiveTab('ROOM_RACK'); openCheckoutModal({ stay_id: row.stay_id }); }}
                              disabled={!row.stay_id}
                              className="btn-primary btn-sm rounded"
                              style={{ marginRight: '0.35rem' }}
                            >
                              Checkout
                            </button>
                            <button onClick={() => onEditReservation && onEditReservation(row.reservation_id)} className="btn-muted btn-sm rounded">View</button>
                          </>
                        )}
                        {(row.arrival_status === 'no_show' || row.arrival_status === 'cancelled') && (
                          <button onClick={() => onEditReservation && onEditReservation(row.reservation_id)} className="btn-muted btn-sm rounded">View</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
            Payment status reflects advance paid at booking vs. the room rate — a guest who settles in full at the front desk after arrival will still show Partial/Unpaid here until that changes.
          </div>
        </div>
      )}

      {checkInModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '1rem' }}>
          <div style={{ width: 'min(720px,100%)', maxHeight: isMobile ? '100vh' : '90vh', height: isMobile ? '100vh' : 'auto', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: isMobile ? 0 : '10px', border: '1px solid #d9e2ec', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem 0.9rem', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: 0, fontSize: isMobile ? '0.95rem' : '1.05rem', color: 'var(--theme-primary)', fontWeight: 700 }}>
                {checkInResult ? 'Check-In Successful' : `Reservation Check-In — Room ${checkInModal.room_number}`}
              </h4>
              <button onClick={() => setCheckInModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', fontWeight: 700, color: 'var(--theme-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: '0.1rem 0.5rem' }} title="Close" aria-label="Close">×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: isMobile ? '0.8rem' : '1rem 1.3rem 1.3rem', flex: 1, minHeight: 0 }}>

            {checkInResult ? (
              <div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', padding: '0.8rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.4rem', fontSize: '0.91rem' }}>
                  <div><b>Reservation:</b> {checkInResult.reservation_no}</div>
                  <div><b>Guest:</b> {checkInResult.primary_guest_name || checkInSelection?.primary_guest_name || '-'}</div>
                  <div><b>Room:</b> {checkInModal.room_number}</div>
                  <div><b>Room Type:</b> {checkInSelection?.room_type_name || checkInModal.room_type_name || '-'}</div>
                  <div><b>Stay ID:</b> {checkInResult.stay_id}</div>
                  <div><b>Folio ID:</b> {checkInResult.folio_id}</div>
                </div>
                {selectedProperty?.code && (() => {
                  const portalUrl = buildGuestPortalUrl(selectedProperty.code);
                  return (
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '7px', padding: '0.6rem 0.8rem', marginTop: '0.8rem', fontSize: '0.84rem' }}>
                      <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '0.3rem' }}>Guest Portal Link</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input readOnly value={portalUrl} style={{ flex: 1, border: '1px solid #93c5fd', borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '0.82rem', background: '#fff', color: '#1e3a5f' }} onClick={e => e.target.select()} />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(portalUrl); }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Copy</button>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>Share this link with the guest for room service, housekeeping & bill access</div>
                    </div>
                  );
                })()}
                {(upsellLoading || upsellOffers.length > 0) && (
                  <div style={{ marginTop: '1rem', border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 7, padding: '0.7rem 0.8rem' }}>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6, fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>⭐ Upsell Opportunities</span>
                      {upsellCtx && <span style={{ fontWeight: 500, fontSize: '0.72rem', color: '#b45309' }}>{upsellCtx.meal_plan} plan · {upsellCtx.pax} pax · {upsellCtx.nights}n</span>}
                    </div>
                    {upsellLoading ? (
                      <div style={{ fontSize: '0.82rem', color: '#b45309' }}>Finding offers…</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {upsellOffers.map((o) => {
                          const added = upsellAddedIds.includes(o.product_id);
                          return (
                            <div key={o.product_id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #fcd34d', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.86rem', color: '#1f2937' }}>
                                  {o.name} <span style={{ fontSize: '0.68rem', color: '#9a3412', background: '#ffedd5', borderRadius: 4, padding: '0 5px' }}>{o.category}</span>
                                </div>
                                <div style={{ fontSize: '0.74rem', color: '#6b7280' }}>{o.price_note} · GST {o.gst_percent}%</div>
                              </div>
                              <div style={{ fontWeight: 700, fontSize: '0.86rem', color: '#166534', whiteSpace: 'nowrap' }}>₹{Number(o.gross_amount).toFixed(2)}</div>
                              <button disabled={added || upsellBusyId === o.product_id} onClick={() => acceptUpsell(o)}
                                style={{ border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 700, cursor: added ? 'default' : 'pointer', background: added ? '#bbf7d0' : '#f59e0b', color: added ? '#166534' : '#fff', whiteSpace: 'nowrap' }}>
                                {added ? '✓ Added' : upsellBusyId === o.product_id ? '…' : 'Add'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {upsellError && <div style={{ color: '#b91c1c', fontSize: '0.76rem', marginTop: 4 }}>{upsellError}</div>}
                  </div>
                )}
                {(upgradeOffers.length > 0 || upgradeDone) && (
                  <div style={{ marginTop: '0.8rem', border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 7, padding: '0.7rem 0.8rem' }}>
                    <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 6, fontSize: '0.9rem' }}>⬆️ Room Upgrades</div>
                    {upgradeDone ? (
                      <div style={{ fontSize: '0.84rem', color: '#166534' }}>✓ Upgraded to <b>{upgradeDone.name}</b> (Room {upgradeDone.room}) — ₹{Number(upgradeDone.gross).toFixed(2)} added to folio.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {upgradeOffers.map((u) => (
                          <div key={u.room_type_id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.86rem', color: '#1f2937' }}>{u.room_type_name} <span style={{ fontSize: '0.68rem', color: '#1e40af', background: '#dbeafe', borderRadius: 4, padding: '0 5px' }}>Room {u.to_room_number}</span></div>
                              <div style={{ fontSize: '0.74rem', color: '#6b7280' }}>+₹{Number(u.delta_gross).toFixed(2)} for {u.nights} night{u.nights > 1 ? 's' : ''} (incl. tax)</div>
                            </div>
                            <div style={{ fontWeight: 700, fontSize: '0.86rem', color: '#1e40af', whiteSpace: 'nowrap' }}>+₹{Number(u.delta_gross).toFixed(2)}</div>
                            <button disabled={upgradeBusyId === u.room_type_id} onClick={() => acceptUpgrade(u)}
                              style={{ border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', background: '#2563eb', color: '#fff', whiteSpace: 'nowrap' }}>
                              {upgradeBusyId === u.room_type_id ? '…' : 'Upgrade'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                  <button onClick={() => setCheckInModal(null)} className="btn-success btn-sm rounded">Close</button>
                </div>
              </div>
            ) : (
              <div>
                {/* Step Indicator */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', fontSize: 13 }}>
                  {[{ n: 1, label: 'Reservation Details' }, { n: 2, label: 'ID Compliance' }].map((s, i) => (
                    <React.Fragment key={s.n}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: checkInStep > s.n ? '#166534' : checkInStep === s.n ? 'var(--theme-primary)' : '#e5e7eb',
                          color: checkInStep >= s.n ? '#fff' : '#6b7280', fontWeight: 700, fontSize: 12 }}>
                          {checkInStep > s.n ? '✓' : s.n}
                        </div>
                        <span style={{ fontWeight: checkInStep === s.n ? 700 : 400, color: checkInStep === s.n ? 'var(--theme-primary)' : '#6b7280' }}>{s.label}</span>
                      </div>
                      {i === 0 && <div style={{ flex: 1, height: 2, background: checkInStep > 1 ? '#166534' : '#e5e7eb', margin: '0 8px' }} />}
                    </React.Fragment>
                  ))}
                </div>

                {checkInLoading ? <div style={{ color: '#4a5568', marginBottom: '0.6rem' }}>Loading eligible reservations...</div> : null}

                {/* Step 1: Reservation Details */}
                {checkInStep === 1 && (
                  <>
                    <div style={{ marginBottom: '0.8rem' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.87rem', fontWeight: 600 }}>
                        Select Reservation
                        <select value={checkInSelectionId} onChange={(e) => onCheckInSelectionChange(e.target.value)} style={{ padding: '0.35rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                          <option value="">-- Select Eligible Reservation --</option>
                          {checkInOptions.map((opt) => (
                            <option key={opt.line_id || opt.reservation_id} value={opt.reservation_id}>
                              {(opt.reservation_no || `RES-${opt.reservation_id}`)} | {opt.primary_guest_name || '-'} | {opt.arrival_date || '-'} to {opt.departure_date || '-'}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    {checkInSelection ? (
                      <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.65rem', marginBottom: '0.9rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.3rem 1rem', fontSize: '0.9rem' }}>
                        <div><b>Resv No:</b> {checkInSelection.reservation_no || '-'}</div>
                        <div><b>Status:</b> <span style={{ color: '#1d4ed8', fontWeight: 600 }}>{checkInSelection.status || '-'}</span></div>
                        <div><b>Guest:</b> {checkInSelection.primary_guest_name || '-'}</div>
                        <div><b>Source:</b> {checkInSelection.booking_source || '-'}</div>
                        <div><b>Arrival:</b> {checkInSelection.arrival_date || '-'}</div>
                        <div><b>Departure:</b> {formatDepartureWithPolicyTime(checkInSelection.departure_date, defaultCheckoutTime)}</div>
                        <div><b>Room Type:</b> {checkInSelection.room_type_name || '-'}</div>
                        <div><b>Room No:</b> {checkInSelection.room_number || checkInModal.room_number || '-'}</div>
                        <div><b>Rate Plan:</b> {checkInSelection.rate_plan_name || checkInSelection.rate_plan_code || '-'}</div>
                        <div><b>Meal Plan:</b> {checkInSelection.meal_plan_name || '-'}</div>
                        <div><b>Rate Amount:</b> {toMoney(checkInSelection.rate_amount)}</div>
                        <div><b>Advance:</b> {toMoney(checkInSelection.credit_hold_amount)}</div>
                        <div><b>Notes:</b> {checkInSelection.reservation_notes || (checkInSelection.corporate_name ? `Corporate: ${checkInSelection.corporate_name}` : (checkInSelection.travel_agent_name ? `Travel Agent: ${checkInSelection.travel_agent_name}` : '-'))}</div>
                      </div>
                    ) : null}

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                        Check-In Date &amp; Time
                        <input
                          type="datetime-local"
                          value={checkInForm.checkin_datetime}
                          onChange={(e) => setCheckInForm((f) => ({ ...f, checkin_datetime: e.target.value }))}
                          style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                        Checkout Date &amp; Time
                        <input
                          type="datetime-local"
                          value={checkInForm.checkout_datetime}
                          onChange={(e) => setCheckInForm((f) => ({ ...f, checkout_datetime: e.target.value }))}
                          style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                        />
                      </label>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.7rem' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                        Advance / Deposit (optional)
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={checkInForm.deposit_amount}
                          onChange={(e) => setCheckInForm((f) => ({ ...f, deposit_amount: e.target.value }))}
                          style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                        Deposit Remarks
                        <input
                          type="text"
                          placeholder="e.g. Cash advance"
                          value={checkInForm.deposit_remarks}
                          onChange={(e) => setCheckInForm((f) => ({ ...f, deposit_remarks: e.target.value }))}
                          style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                        />
                      </label>
                    </div>

                    {checkInError ? <div className="error-banner">{checkInError}</div> : null}

                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button onClick={() => setCheckInModal(null)} disabled={checkInBusy} className="btn-muted btn-sm rounded">Cancel</button>
                      <button onClick={goToComplianceStep} disabled={!checkInSelection || checkInLoading} className="btn-primary btn-sm rounded" style={{ opacity: (!checkInSelection || checkInLoading) ? 0.7 : 1 }}>Next: ID Compliance →</button>
                    </div>
                  </>
                )}

                {/* Step 2: ID Compliance */}
                {checkInStep === 2 && (
                  <>
                    <div style={{ marginBottom: 10, padding: '6px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 12, color: '#0369a1' }}>
                      Guest: <b>{checkInSelection?.primary_guest_name || '-'}</b> &nbsp;|&nbsp; Resv: <b>{checkInSelection?.reservation_no || '-'}</b>
                    </div>

                    <PmsComplianceCheckin
                      values={checkInComplianceData}
                      onChange={(name, value) => setCheckInComplianceData((prev) => ({ ...prev, [name]: value }))}
                      fieldRules={checkInFieldRules}
                      errors={checkInComplianceErrors}
                      policyConfig={checkInPolicyConfig}
                      countryOptions={checkInCountryOptions}
                      idTypeOptions={checkInIdTypeOptions}
                      hotelCountry={selectedProperty?.country || 'India'}
                    />

                    <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 7 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={checkInOverrideMode}
                          onChange={(e) => setCheckInOverrideMode(e.target.checked)} />
                        Override compliance (skip mandatory fields with reason)
                      </label>
                      {checkInOverrideMode && (
                        <textarea
                          value={checkInOverrideReason}
                          onChange={(e) => setCheckInOverrideReason(e.target.value)}
                          placeholder="Reason for override (required for audit log)…"
                          rows={2}
                          style={{ width: '100%', marginTop: 8, padding: '6px 8px', border: '1px solid #fbbf24', borderRadius: 5, fontSize: 12, boxSizing: 'border-box', resize: 'vertical' }}
                        />
                      )}
                    </div>

                    {checkInError ? <div className="error-banner">{checkInError}</div> : null}

                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: 14 }}>
                      <button onClick={() => setCheckInStep(1)} disabled={checkInBusy} className="btn-muted btn-sm rounded">← Back</button>
                      <button onClick={() => setCheckInModal(null)} disabled={checkInBusy} className="btn-muted btn-sm rounded">Cancel</button>
                      <button onClick={submitCheckIn} disabled={checkInBusy || (checkInOverrideMode && !checkInOverrideReason.trim())} className="btn-primary btn-sm rounded" style={{ opacity: checkInBusy ? 0.7 : 1 }}>{checkInBusy ? 'Processing...' : 'Confirm Check-In'}</button>
                    </div>
                  </>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      ) : null}

      {coModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ width: 'min(740px,96vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setCoModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', fontWeight: 700, color: 'var(--theme-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Close">x</button>

            {coResult ? (
              <div>
                <h4 style={{ margin: '0 0 0.6rem 0', color: '#166534' }}>Checkout Successful</h4>
                <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '7px', padding: '0.65rem', marginBottom: '0.7rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.35rem' }}>
                  <div><b>Invoice:</b> {coResult.invoice_no || (coResult.billed_on_linked_master ? 'Billed on linked master room' : '-')}</div>
                  <div><b>Guest:</b> {coResult.guest_name || '-'}</div>
                  <div><b>Room:</b> {(Array.isArray(coResult.room_numbers) && coResult.room_numbers.length ? coResult.room_numbers.join(', ') : coResult.room_number) || '-'}</div>
                  <div><b>Settlement:</b> {coResult.settlement_mode || '-'}</div>
                  <div><b>Total Debit:</b> {toMoney(coResult.total_debit)}</div>
                  <div><b>Total Credit:</b> {toMoney(coResult.total_credit)}</div>
                  <div><b>Net Payable:</b> <span style={{ color: '#c53030', fontWeight: 700 }}>{toMoney(coResult.balance)}</span></div>
                </div>
                <div className="action-bar" style={{ justifyContent: 'flex-end' }}>
                  <button onClick={() => setInvoiceModal(coResult)} className="btn-muted btn-sm rounded">View Invoice</button>
                  <button onClick={() => handleInvoicePrint(coResult)} className="btn-primary btn-sm rounded">Print Invoice</button>
                  <button onClick={() => shareInvoiceWhatsApp(coResult)} className="btn-success btn-sm rounded">Share WhatsApp</button>
                  <button onClick={() => shareInvoiceEmail(coResult)} className="btn-muted btn-sm rounded">Share Email</button>
                  <button onClick={() => { setCoModal(null); setCoResult(null); }} className="btn-success btn-sm rounded">Done</button>
                </div>
              </div>
            ) : (
              <div>
                <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Stay Checkout</h4>
                <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.65rem', marginBottom: '0.7rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.3rem 0.8rem', fontSize: '0.9rem' }}>
                  <div><b>Reservation:</b> {coModal.reservation_no || '-'}</div>
                  <div><b>Guest:</b> {coModal.primary_guest_name || '-'}</div>
                  <div><b>Room:</b> {coModal.room_number || '-'}</div>
                  <div><b>Stay ID:</b> {coModal.stay_id || '-'}</div>
                  <div><b>Booked Arrival:</b> {fmtDateShortYear(coFolio?.booked_arrival_date || coModal.arrival_date)}</div>
                  <div><b>Booked Departure:</b> {formatDepartureWithPolicyTime(coFolio?.booked_departure_date || coModal.departure_date, defaultCheckoutTime)}</div>
                  <div><b>Actual Check-In:</b> {formatDateTime(coFolio?.checkin_datetime)}</div>
                </div>

                {coLoading ? <div style={{ color: '#4a5568', marginBottom: '0.5rem' }}>Loading folio...</div> : null}
                {coError ? <div style={{ color: '#c53030', marginBottom: '0.5rem' }}>{coError}</div> : null}

                {coFolio ? (
                  <div style={{ marginBottom: '0.7rem' }}>
                    <div style={{ fontWeight: 700, color: '#2d3748', marginBottom: '0.3rem' }}>Folio Preview</div>
                    {(() => {
                      const summary = getCheckoutSettlementSummary();
                      return (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.45rem', marginBottom: '0.45rem' }}>
                            <div style={{ border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.45rem', background: 'var(--theme-bg-light)' }}>
                              <div style={{ fontSize: '0.78rem', color: '#4a5568', fontWeight: 700, marginBottom: '0.25rem' }}>Booking Settlement</div>
                                <div style={{ display: 'grid', gap: '0.2rem', fontSize: '0.83rem' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Reservation Amount</span><b>{toMoney(summary.reservationAmount)}</b></div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Advance Collected</span><b style={{ color: '#276749' }}>{toMoney(summary.advanceCollected)}</b></div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #cbd5e0', paddingTop: '0.2rem' }}><span>Booking Amount To Collect</span><b style={{ color: '#c53030' }}>{toMoney(summary.bookingAmountToCollect)}</b></div>
                                  {summary.bookingAdvanceExcess > 0 ? (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Booking Advance Excess / Refund</span><b style={{ color: '#276749' }}>{toMoney(summary.bookingAdvanceExcess)}</b></div>
                                  ) : null}
                                </div>
                              </div>
                            <div style={{ border: '1px solid var(--theme-border)', borderRadius: '6px', padding: '0.45rem', background: 'var(--theme-bg-light)' }}>
                              <div style={{ fontSize: '0.78rem', color: '#4a5568', fontWeight: 700, marginBottom: '0.25rem' }}>Folio Snapshot</div>
                              <div style={{ display: 'grid', gap: '0.2rem', fontSize: '0.83rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Debit</span><b>{toMoney(summary.folioDebit)}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Credit</span><b>{toMoney(summary.folioCredit)}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #cbd5e0', paddingTop: '0.2rem' }}><span>Master Folio Balance</span><b style={{ color: summary.folioBalance > 0 ? '#c53030' : '#276749' }}>{toMoney(Math.abs(summary.folioBalance))}{summary.folioBalance < 0 ? ' Cr' : ''}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Linked Child Pending</span><b style={{ color: summary.linkedPendingBalance > 0 ? '#c53030' : (summary.linkedPendingBalance < 0 ? '#276749' : '#4a5568') }}>{toMoney(Math.abs(summary.linkedPendingBalance))}{summary.linkedPendingBalance < 0 ? ' Cr' : ''}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Scope</span><b>{summary.checkoutScope === 'COMBINED_LINKED' ? 'Combined Linked' : 'Master Only'}</b></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #cbd5e0', paddingTop: '0.2rem' }}><span>Current Balance (Scope)</span><b style={{ color: summary.amountToCollectCurrent > 0 ? '#c53030' : '#276749' }}>{toMoney(Math.abs(summary.amountToCollectCurrent))}{summary.advanceExcessCurrent > 0 ? ' Cr' : ''}</b></div>
                              </div>
                            </div>
                          </div>
                          <div style={{ border: '1px solid #d6bcfa', borderRadius: '6px', padding: '0.45rem', background: '#faf5ff', fontSize: '0.84rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}><span>Late Checkout Charge</span><b>{toMoney(summary.lateCharge)}</b></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #d6bcfa', paddingTop: '0.25rem' }}><span>Amount To Collect (Final)</span><b style={{ color: '#c53030' }}>{toMoney(summary.amountToCollectFinal)}</b></div>
                            {summary.advanceExcessFinal > 0 ? (
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.2rem' }}><span>Final Refund / Carry Forward Credit</span><b style={{ color: '#276749' }}>{toMoney(summary.advanceExcessFinal)}</b></div>
                            ) : null}
                          </div>
                          {Number(coFolio?.linked_child_pending_lines || 0) > 0 ? (
                            <div style={{ border: '1px solid #fbd38d', borderRadius: '6px', padding: '0.45rem', background: '#fffaf0', fontSize: '0.8rem', color: '#744210', marginTop: '0.45rem' }}>
                              Linked child pending postings: <b>{Number(coFolio.linked_child_pending_lines)}</b>.
                              {summary.includesLinkedChildPending
                                ? ` Included in current checkout scope (${toMoney(summary.linkedPendingBalance)}).`
                                : ' Not included in current checkout scope (switch to Combined Linked to include).'}
                            </div>
                          ) : null}
                          {(() => {
                            const billingContext = deriveCheckoutBillingContext(coFolio);
                            const selectedProfile = (Array.isArray(coBillingProfiles) ? coBillingProfiles : [])
                              .find((row) => String(row.billing_profile_id) === String(coForm.billing_profile_id));
                            return (
                              <div style={{ border: '1px solid #bee3f8', borderRadius: '6px', padding: '0.55rem', background: '#ebf8ff', fontSize: '0.84rem', marginTop: '0.45rem' }}>
                                <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.35rem' }}>Invoice Billing Profile</div>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.35rem', marginBottom: '0.35rem' }}>
                                  <div><span style={{ color: '#4a5568' }}>Payer Type:</span> <b>{billingContext.payerType}</b></div>
                                  <div><span style={{ color: '#4a5568' }}>Bill To:</span> <b>{billingContext.billToLabel}</b></div>
                                </div>
                                {/* Guest Billing Info — editable GSTIN + Company */}
                                <div style={{ fontSize: '0.8rem', color: '#2d3748', border: '1px solid #d6e8ff', borderRadius: '5px', background: '#f0f9ff', padding: '0.45rem', marginBottom: '0.4rem' }}>
                                  <div style={{ fontWeight: 600, color: '#2b6cb0', marginBottom: '0.3rem', fontSize: '0.78rem' }}>Customer GST Details (for B2B Invoice)</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.3rem' }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>Customer GSTIN <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></span>
                                      <input placeholder="15-digit GSTIN" value={coForm.customer_gstin} onChange={(e) => setCoForm((f) => ({ ...f, customer_gstin: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15) }))}
                                        style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.82rem', fontFamily: 'monospace' }} />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>Company Name <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></span>
                                      <input placeholder="Company / Trade name" value={coForm.customer_company} onChange={(e) => setCoForm((f) => ({ ...f, customer_company: e.target.value }))}
                                        style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.82rem' }} />
                                    </label>
                                  </div>
                                  {coForm.customer_gstin && coForm.customer_gstin.length === 15 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(coForm.customer_gstin) && (
                                    <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: '0.2rem' }}>Invalid GSTIN format</div>
                                  )}
                                  {(coFolio?.guest_address || coFolio?.guest_email || coFolio?.mobile_number) && (
                                    <div style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#64748b' }}>
                                      {coFolio.guest_address && <div>Address: {coFolio.guest_address}</div>}
                                      {coFolio.guest_email && <div>Email: {coFolio.guest_email}</div>}
                                      {coFolio.mobile_number && <div>Mobile: {coFolio.mobile_number}</div>}
                                    </div>
                                  )}
                                </div>
                                {coBillingLoading ? <div style={{ color: '#4a5568' }}>Loading billing profiles...</div> : null}
                                {coBillingError ? <div style={{ color: '#c53030' }}>{coBillingError}</div> : null}
                                {!coBillingLoading && !coBillingError && (
                                  <>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', marginBottom: '0.3rem', fontWeight: 600 }}>
                                      Billing Profile
                                      <select
                                        value={coForm.billing_profile_id}
                                        onChange={(e) => setCoForm((f) => ({ ...f, billing_profile_id: e.target.value }))}
                                        disabled={!coBillingProfiles.length}
                                      >
                                        <option value="">{coBillingProfiles.length ? 'Select Billing Profile' : 'No profile configured'}</option>
                                        {(Array.isArray(coBillingProfiles) ? coBillingProfiles : []).map((row) => (
                                          <option key={row.billing_profile_id} value={row.billing_profile_id}>
                                            {row.profile_code || `BP-${row.billing_profile_id}`} - {row.profile_name || 'Billing Profile'}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                    {selectedProfile ? (
                                      <div style={{ fontSize: '0.8rem', color: '#2d3748', border: '1px solid #d6e8ff', borderRadius: '5px', background: '#fff', padding: '0.4rem' }}>
                                        <div><b>{selectedProfile.billing_company_name || selectedProfile.profile_name || '-'}</b></div>
                                        {formatBillingProfileAddress(selectedProfile) ? <div>{formatBillingProfileAddress(selectedProfile)}</div> : null}
                                        {selectedProfile.gstin_number ? <div>GSTIN: <b>{selectedProfile.gstin_number}</b></div> : null}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '0.8rem', color: '#4a5568' }}>
                                        No billing profile selected. Invoice will use existing bill-to fallback values.
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          <div style={{ border: '1px solid #cbd5e0', borderRadius: '6px', padding: '0.55rem', background: '#fff', fontSize: '0.84rem', marginTop: '0.45rem' }}>
                            <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.35rem' }}>Final Collection Before Invoice</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                              <span>Amount To Collect From Guest</span>
                              <b style={{ color: '#c53030' }}>{toMoney(summary.amountToCollectFinal)}</b>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.45rem', fontWeight: 600 }}>
                              <input
                                type="checkbox"
                                checked={!!coForm.payment_received}
                                onChange={(e) => setCoForm((f) => ({
                                  ...f,
                                  payment_received: e.target.checked,
                                  payment_amount: e.target.checked
                                    ? (f.payment_amount || Number(summary.amountToCollectFinal || 0).toFixed(2))
                                    : ''
                                }))}
                                disabled={Number(summary.amountToCollectFinal || 0) <= 0}
                              />
                              Mark amount received from guest
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.85rem' }}>
                              <input
                                type="checkbox"
                                checked={!!coForm.split_payment}
                                onChange={(e) => setCoForm((f) => ({ ...f, split_payment: e.target.checked }))}
                                disabled={!coForm.payment_received}
                              />
                              Split this payment across multiple modes
                            </label>
                            {coForm.split_payment ? (() => {
                              const lines = coForm.payment_lines || [];
                              const totalEntered = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
                              const updateLine = (idx, patch) => setCoForm((f) => {
                                const next = [...(f.payment_lines || [])];
                                next[idx] = { ...next[idx], ...patch };
                                return { ...f, payment_lines: next };
                              });
                              const removeLine = (idx) => setCoForm((f) => ({
                                ...f, payment_lines: (f.payment_lines || []).filter((_, i) => i !== idx)
                              }));
                              const addLine = () => setCoForm((f) => ({
                                ...f, payment_lines: [...(f.payment_lines || []), { mode: 'CASH', amount: '', reference_no: '' }]
                              }));
                              return (
                                <div>
                                  {lines.map((line, idx) => {
                                    const modeUpper = String(line.mode || '').toUpperCase();
                                    const refRequired = modeUpper === 'CARD' || modeUpper === 'UPI';
                                    return (
                                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr auto', gap: '0.4rem', marginBottom: '0.4rem', alignItems: 'end' }}>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontWeight: 600, fontSize: '0.8rem' }}>
                                          Mode
                                          <select value={line.mode} onChange={(e) => updateLine(idx, { mode: e.target.value })} disabled={!coForm.payment_received}>
                                            <option value="CASH">CASH</option>
                                            <option value="CARD">CARD</option>
                                            <option value="UPI">UPI</option>
                                            <option value="BANK">BANK</option>
                                          </select>
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontWeight: 600, fontSize: '0.8rem' }}>
                                          Amount
                                          <input type="number" min={0} step="0.01" value={line.amount}
                                            onChange={(e) => updateLine(idx, { amount: e.target.value })}
                                            disabled={!coForm.payment_received} />
                                        </label>
                                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', fontWeight: 600, fontSize: '0.8rem' }}>
                                          {modeUpper === 'UPI' ? 'UPI Transaction ID' : 'Reference No'} {refRequired ? '*' : ''}
                                          <input value={line.reference_no} onChange={(e) => updateLine(idx, { reference_no: e.target.value })}
                                            placeholder={refRequired ? 'Required for this mode' : 'Optional'}
                                            disabled={!coForm.payment_received} />
                                        </label>
                                        <button type="button" onClick={() => removeLine(idx)} disabled={!coForm.payment_received || lines.length <= 1}
                                          title="Remove this payment line"
                                          style={{ padding: '0.4rem 0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>
                                          ×
                                        </button>
                                      </div>
                                    );
                                  })}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.3rem' }}>
                                    <button type="button" onClick={addLine} disabled={!coForm.payment_received}
                                      style={{ padding: '0.3rem 0.7rem', border: '1px solid #cbd5e0', background: '#fff', color: '#334155', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                                      + Add payment mode
                                    </button>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: Math.abs(totalEntered - Number(summary.amountToCollectFinal || 0)) > 0.009 ? '#c53030' : '#166534' }}>
                                      Entered: {toMoney(totalEntered)} of {toMoney(summary.amountToCollectFinal)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })() : (() => {
                              const settlementUpper = String(coForm.settlement_mode || '').toUpperCase();
                              // Settlement Mode already IS the tender for direct-tender
                              // checkouts (CASH/CARD/UPI/BANK) — this extra selector only
                              // adds information for billed modes, where the folio balance
                              // goes to a city ledger but the front desk may still collect
                              // some or all of it directly right now (e.g. via UPI).
                              const isBilledSettlement = ['CORPORATE', 'TRAVEL_AGENT', 'OTA'].includes(settlementUpper);
                              const collectedUpper = String(coForm.payment_collected_mode || '').toUpperCase();
                              const effectiveMode = isBilledSettlement ? collectedUpper : settlementUpper;
                              const refRequired = effectiveMode === 'CARD' || effectiveMode === 'UPI';
                              return (
                                <>
                                  {isBilledSettlement && (
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.18rem', fontWeight: 600, marginBottom: '0.45rem' }}>
                                      Collected Via <span style={{ fontWeight: 400, color: '#718096' }}>(if the front desk is also collecting something directly right now)</span>
                                      <select
                                        value={coForm.payment_collected_mode}
                                        onChange={(e) => setCoForm((f) => ({ ...f, payment_collected_mode: e.target.value }))}
                                        disabled={!coForm.payment_received}
                                      >
                                        <option value="">— none, fully billed —</option>
                                        <option value="CASH">CASH</option>
                                        <option value="CARD">CARD</option>
                                        <option value="UPI">UPI</option>
                                        <option value="BANK">BANK</option>
                                      </select>
                                    </label>
                                  )}
                                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.45rem' }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.18rem', fontWeight: 600 }}>
                                      Received Amount
                                      <input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        value={coForm.payment_amount}
                                        onChange={(e) => setCoForm((f) => ({ ...f, payment_amount: e.target.value }))}
                                        disabled={!coForm.payment_received}
                                      />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.18rem', fontWeight: 600 }}>
                                      {effectiveMode === 'UPI' ? 'UPI Transaction ID' : 'Reference No'} {refRequired ? '*' : ''}
                                      <input
                                        value={coForm.payment_reference_no}
                                        onChange={(e) => setCoForm((f) => ({ ...f, payment_reference_no: e.target.value }))}
                                        placeholder={refRequired ? 'Required for selected mode' : 'Txn/Card/Auth No'}
                                        disabled={!coForm.payment_received}
                                      />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.18rem', fontWeight: 600 }}>
                                      Remarks
                                      <input
                                        value={coForm.payment_remarks}
                                        onChange={(e) => setCoForm((f) => ({ ...f, payment_remarks: e.target.value }))}
                                        placeholder="Final payment at checkout"
                                        disabled={!coForm.payment_received}
                                      />
                                    </label>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ maxHeight: '190px', overflowY: 'auto', border: '1px solid #cbd5e0', borderRadius: '6px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                        <thead>
                          <tr style={{ background: '#ebf8ff' }}>
                            <th style={{ padding: '0.3rem', textAlign: 'center' }}>Split #</th>
                            <th style={{ padding: '0.3rem', textAlign: 'left' }}>Posting</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right' }}>Debit</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right' }}>Credit</th>
                            <th style={{ padding: '0.3rem', textAlign: 'left' }}>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(Array.isArray(coFolio.lines) ? coFolio.lines : []).map((line) => {
                            const splitNo = line.split_no || 1;
                            const isSplit = splitNo > 1;
                            return (
                            <tr key={line.ledger_id} style={{ borderTop: '1px solid #edf2f7', background: isSplit ? '#fffbeb' : undefined }}>
                              <td style={{ padding: '0.3rem', textAlign: 'center' }}>
                                <span style={{ display: 'inline-block', minWidth: '1.4rem', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 700, background: isSplit ? '#fde68a' : 'var(--theme-border)', color: isSplit ? '#92400e' : '#4a5568' }}>
                                  {splitNo}
                                </span>
                              </td>
                              <td style={{ padding: '0.3rem' }}>{line.posting_type || '-'}</td>
                              <td style={{ padding: '0.3rem', textAlign: 'right' }}>{toMoney(line.debit_amount)}</td>
                              <td style={{ padding: '0.3rem', textAlign: 'right' }}>{toMoney(line.credit_amount)}</td>
                              <td style={{ padding: '0.3rem' }}>{line.remarks || '-'}</td>
                            </tr>
                            );
                          })}
                          {!coFolio?.lines?.length ? <tr><td colSpan={4} style={{ padding: '0.5rem', color: '#718096' }}>No folio lines</td></tr> : null}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.45rem' }}>
                      <button
                        onClick={openSplitModal}
                        disabled={coBusy || coLoading || !Array.isArray(coFolio?.lines) || !coFolio.lines.length}
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                      >
                        Split Folio
                      </button>
                    </div>
                  </div>
                ) : null}

                {(() => {
                  const lateMeta = calcLateCharge();
                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.6rem' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.84rem', fontWeight: 600 }}>
                          Actual Checkout
                          <input type="datetime-local" value={coForm.actual_checkout_datetime} onChange={(e) => setCoForm((f) => ({ ...f, actual_checkout_datetime: e.target.value }))} />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.84rem', fontWeight: 600 }}>
                          Late Checkout Charge
                          <input type="number" min={0} step="0.01" value={coForm.late_checkout_charge} onChange={(e) => setCoForm((f) => ({ ...f, late_checkout_charge: Number(e.target.value || 0) }))} />
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.84rem', fontWeight: 600 }}>
                          Checkout Scope
                          <select value={coForm.checkout_scope} onChange={(e) => setCoForm((f) => ({ ...f, checkout_scope: e.target.value }))}>
                            <option value="MASTER_ONLY">Master Folio Only</option>
                            <option value="COMBINED_LINKED">Combined (Master + Linked Child)</option>
                          </select>
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.84rem', fontWeight: 600 }}>
                          Settlement Mode
                          <select
                            value={coForm.settlement_mode}
                            onChange={(e) => setCoForm((f) => ({
                              ...f,
                              settlement_mode: e.target.value,
                              // Clear the other party's id when mode changes so a stale id
                              // from a previous pick doesn't bleed into the new submit.
                              settlement_corporate_id: e.target.value === 'CORPORATE' ? f.settlement_corporate_id : '',
                              settlement_travel_agent_id: e.target.value === 'TRAVEL_AGENT' ? f.settlement_travel_agent_id : ''
                            }))}
                          >
                            <option value="CASH">CASH</option>
                            <option value="CARD">CARD</option>
                            <option value="UPI">UPI</option>
                            <option value="BANK">BANK</option>
                            <option value="CORPORATE">CORPORATE</option>
                            <option value="TRAVEL_AGENT">TRAVEL AGENT</option>
                            <option value="OTA">OTA</option>
                          </select>
                        </label>
                      </div>
                      {/* Settlement entity picker — visible only when the mode needs one. */}
                      {String(coForm.settlement_mode || '').toUpperCase() === 'CORPORATE' ? (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '4px' }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.84rem', fontWeight: 600 }}>
                            Settling Corporate *
                            <select
                              value={coForm.settlement_corporate_id}
                              onChange={(e) => setCoForm((f) => ({ ...f, settlement_corporate_id: e.target.value }))}
                            >
                              <option value="">— select corporate —</option>
                              {(Array.isArray(coCorporates) ? coCorporates : []).map((c) => (
                                <option key={c.corporate_id} value={c.corporate_id}>
                                  {c.corporate_code} — {c.corporate_name}
                                  {c.gst_number ? ` (GST ${c.gst_number})` : ' (no GSTIN)'}
                                </option>
                              ))}
                            </select>
                            <span style={{ fontSize: '0.74rem', fontWeight: 400, color: '#475569' }}>
                              The corporate's GSTIN will appear on the invoice; the open balance moves to their city-ledger account.
                            </span>
                          </label>
                        </div>
                      ) : null}
                      {String(coForm.settlement_mode || '').toUpperCase() === 'TRAVEL_AGENT' ? (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: '4px' }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.84rem', fontWeight: 600 }}>
                            Settling Travel Agent *
                            <select
                              value={coForm.settlement_travel_agent_id}
                              onChange={(e) => setCoForm((f) => ({ ...f, settlement_travel_agent_id: e.target.value }))}
                            >
                              <option value="">— select travel agent —</option>
                              {(Array.isArray(coTravelAgents) ? coTravelAgents : []).map((a) => (
                                <option key={a.travel_agent_id} value={a.travel_agent_id}>
                                  {a.agent_code} — {a.agent_name}
                                  {a.gst_number ? ` (GST ${a.gst_number})` : ' (no GSTIN)'}
                                </option>
                              ))}
                            </select>
                            <span style={{ fontSize: '0.74rem', fontWeight: 400, color: '#155e75' }}>
                              The agent's GSTIN will appear on the invoice; the open balance moves to their commission ledger.
                            </span>
                          </label>
                        </div>
                      ) : null}
                      {String(coForm.settlement_mode || '').toUpperCase() === 'OTA' ? (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '4px', fontSize: '0.82rem', color: '#78350f' }}>
                          <strong>OTA Settlement —</strong> the channel is auto-detected from this reservation's booking source. Use this when the OTA pre-collected payment and will remit later.
                        </div>
                      ) : null}
                      {lateMeta.isLate ? (
                        <div style={{ marginBottom: '0.6rem', color: '#b45309', fontSize: '0.84rem' }}>
                          Late checkout detected ({lateMeta.hoursLate} hr). Suggested charge: {toMoney(lateMeta.suggestedCharge)}.
                          <button style={{ marginLeft: '0.5rem' }} onClick={() => setCoForm((f) => ({ ...f, late_checkout_charge: lateMeta.suggestedCharge }))}>Use Suggested</button>
                        </div>
                      ) : null}
                    </>
                  );
                })()}

                {coError ? <div className="error-banner">{coError}</div> : null}
                <div className="action-bar" style={{ justifyContent: 'flex-end' }}>
                  <button onClick={() => setCoModal(null)} disabled={coBusy} className="btn-muted btn-sm rounded">Cancel</button>
                  <button onClick={submitCheckout} disabled={coBusy || coLoading} className="btn-primary btn-sm rounded" style={{ opacity: coBusy ? 0.75 : 1 }}>{coBusy ? 'Processing...' : 'Confirm Checkout & Raise Invoice'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {invoiceModal ? (() => {
        const allLines = Array.isArray(invoiceModal.lines) ? invoiceModal.lines : [];
        const splitNos = [...new Set(allLines.map(l => Number(l.split_no || 1)))].sort((a, b) => a - b);
        const hasSplits = splitNos.length > 1;
        const activeLines = hasSplits && invoiceSplitTab > 0
          ? allLines.filter(l => Number(l.split_no || 1) === invoiceSplitTab)
          : allLines;
        const activeDebit = activeLines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
        const activeCredit = activeLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
        const activeBalance = activeDebit - activeCredit;
        const splitLabel = hasSplits && invoiceSplitTab > 0 ? `Split ${invoiceSplitTab}` : (hasSplits ? null : null);
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(860px,97vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setInvoiceModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', fontWeight: 700, color: 'var(--theme-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Close">x</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Invoice Preview</h4>
            <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.65rem', marginBottom: '0.7rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.3rem 0.8rem', fontSize: '0.9rem' }}>
              <div><b>Invoice No:</b> {invoiceModal.invoice_no || '-'}</div>
              <div><b>Reservation No:</b> {invoiceModal.reservation_no || '-'}</div>
              <div><b>Guest:</b> {invoiceModal.guest_name || '-'}</div>
              <div><b>Mobile:</b> {invoiceModal.guest_mobile || '-'}</div>
              <div><b>Email:</b> {invoiceModal.guest_email || '-'}</div>
              <div><b>Settlement:</b> {invoiceModal.settlement_mode || '-'}</div>
            </div>

            {hasSplits && (
              <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setInvoiceSplitTab(0)}
                  style={{ padding: '0.25rem 0.8rem', borderRadius: '4px', border: '1px solid #cbd5e0', background: invoiceSplitTab === 0 ? '#2b6cb0' : '#fff', color: invoiceSplitTab === 0 ? '#fff' : '#2d3748', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
                >All Lines</button>
                {splitNos.map(sn => (
                  <button
                    key={sn}
                    onClick={() => setInvoiceSplitTab(sn)}
                    style={{ padding: '0.25rem 0.8rem', borderRadius: '4px', border: '1px solid #cbd5e0', background: invoiceSplitTab === sn ? '#2b6cb0' : '#fff', color: invoiceSplitTab === sn ? '#fff' : '#2d3748', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
                  >Split {sn}</button>
                ))}
              </div>
            )}

            <div style={{ overflowX: 'auto', border: '1px solid #cbd5e0', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', background: '#fff' }}>
                <thead>
                  <tr style={{ background: '#ebf8ff' }}>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Posting</th>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Rate</th>
                    <th style={{ padding: '0.35rem', textAlign: 'center' }}>Tax%</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>SGST</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>CGST</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Debit</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {activeLines.map((line, idx) => {
                    const baseAmt = Number(line.debit_amount || 0) - Number(line.tax_amount || 0);
                    return (
                      <tr key={line.ledger_id || idx} style={{ borderTop: '1px solid #edf2f7' }}>
                        <td style={{ padding: '0.35rem' }}>{idx + 1}</td>
                        <td style={{ padding: '0.35rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{line.posting_date || '-'}</td>
                        <td style={{ padding: '0.35rem' }}>{line.posting_type || '-'}</td>
                        <td style={{ padding: '0.35rem', fontSize: '0.78rem' }}>{line.revenue_desc || line.remarks || '-'}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(line.debit_amount || 0) > 0 ? toMoney(baseAmt) : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'center' }}>{line.gst_percent ? Number(line.gst_percent) + '%' : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(line.sgst_amount || 0) > 0 ? toMoney(line.sgst_amount) : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{Number(line.cgst_amount || 0) > 0 ? toMoney(line.cgst_amount) : ''}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(line.debit_amount)}</td>
                        <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(line.credit_amount)}</td>
                      </tr>
                    );
                  })}
                  {!activeLines.length ? <tr><td colSpan={10} style={{ padding: '0.55rem', color: '#718096' }}>No invoice lines</td></tr> : null}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--theme-bg-light)', fontWeight: 700 }}>
                    <td colSpan={8} style={{ padding: '0.35rem' }}>Totals</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(activeDebit)}</td>
                    <td style={{ padding: '0.35rem', textAlign: 'right' }}>{toMoney(activeCredit)}</td>
                  </tr>
                  <tr style={{ background: '#fff5f5' }}>
                    <td colSpan={8} style={{ padding: '0.35rem', fontWeight: 700, color: '#c53030' }}>Net Payable</td>
                    <td colSpan={2} style={{ padding: '0.35rem', textAlign: 'right', fontWeight: 700, color: '#c53030' }}>{toMoney(activeBalance)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="action-bar" style={{ marginTop: '0.7rem', justifyContent: 'flex-end' }}>
              <button onClick={() => handleInvoicePrint(invoiceModal, activeLines, splitLabel)} className="btn-primary btn-sm rounded">Print{splitLabel ? ` (${splitLabel})` : ''}</button>
              <button onClick={() => shareInvoiceWhatsApp(invoiceModal)} className="btn-success btn-sm rounded">Share WhatsApp</button>
              <button onClick={() => shareInvoiceEmail(invoiceModal)} className="btn-muted btn-sm rounded">Share Email</button>
              <button onClick={() => setInvoiceModal(null)} className="btn-muted btn-sm rounded">Close</button>
            </div>
          </div>
        </div>
        );
      })() : null}

      {activeRoom ? (
        <div onClick={(e) => { if (e.target === e.currentTarget) setActiveRoom(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, overflowY: 'auto', padding: '2vh 0', backdropFilter: 'blur(2px)' }}>
          <div style={{ width: 'min(680px,95vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '12px', border: '1px solid #d9e2ec', padding: '1.2rem', position: 'relative', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)' }}>
            {(() => {
              const isOccupiedRoom = String(activeRoom.occupancy_status || '').toUpperCase() === 'OCCUPIED';
              const canCheckout = Boolean(activeRoom.stay_id) && isOccupiedRoom;
              const activeRoomOcc = String(activeRoom.occupancy_status || '').toUpperCase();
              const activeRoomMaint = String(activeRoom.maintenance_status || '').toUpperCase();
              const activeResStatus = String(activeRoom.reservation_status || '').toUpperCase();
              const isReservedVacant = activeRoomOcc === 'VACANT' && activeRoom.reservation_id && ['CONFIRMED', 'RESERVED', 'GUARANTEED'].includes(activeResStatus);
              const displayOcc = isReservedVacant ? 'RESERVED' : activeRoomOcc;
              const canWalkIn = activeRoomOcc === 'VACANT' && !['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(activeRoomMaint);
              const canBlockRoom = activeRoomOcc === 'VACANT' && !['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(activeRoomMaint);
              const canUnblockRoom = ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(activeRoomMaint);
              const canResvCheckIn = Boolean(activeRoom.room_id)
                && !Boolean(activeRoom.stay_id)
                && activeRoomOcc !== 'OCCUPIED'
                && !['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(activeRoomMaint);
              // REQ-06 — express eligibility for THIS room (undefined => no express affordance)
              const ciElig = expressElig.checkin?.[activeRoom.room_id];
              const coElig = expressElig.checkout?.[activeRoom.stay_id];
              return (
                <>
            <button onClick={() => setActiveRoom(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', fontWeight: 700, color: 'var(--theme-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Close" aria-label="Close">x</button>
            <h4 style={{ marginTop: 0, marginBottom: '0.6rem', color: 'var(--theme-primary-dark)', paddingRight: '1.4rem' }}>Room {activeRoom.room_number} — Actions</h4>
            {/* Room Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.35rem 1rem', fontSize: '0.88rem', marginBottom: '0.6rem' }}>
              <div><span className="form-label">Room Type</span><div>{activeRoom.room_type_name}</div></div>
              <div><span className="form-label">Occupancy</span><div>
                <span className={`tag ${displayOcc === 'OCCUPIED' ? 'tag-info' : displayOcc === 'RESERVED' ? 'tag-warning' : 'tag-success'}`}>{displayOcc}</span>
              </div></div>
              <div><span className="form-label">Maintenance</span><div>
                {['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(activeRoomMaint)
                  ? <span className="tag tag-danger">{activeRoom.maintenance_status}</span>
                  : <span style={{ color: '#475569' }}>{activeRoom.maintenance_status || 'Normal'}</span>}
                {activeRoom.block_reason && ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(activeRoomMaint) && (
                  <div style={{ fontSize: '0.82rem', color: '#b91c1c', fontWeight: 600, marginTop: '0.15rem' }}>
                    {activeRoom.block_reason.replace(/_/g, ' ')}
                    {activeRoom.block_notes && <span style={{ fontWeight: 400, color: '#64748b' }}> — {activeRoom.block_notes}</span>}
                  </div>
                )}
              </div></div>
              <div><span className="form-label">Reservation</span><div>{activeRoom.reservation_no || '-'}</div></div>
              <div><span className="form-label">Guest</span><div style={{ fontWeight: 600 }}>{activeRoom.primary_guest_name || '-'}</div></div>
              <div><span className="form-label">Pax</span><div>{formatRackPax(activeRoom)}</div></div>
              <div><span className="form-label">Meal Plan</span><div>{activeRoom.meal_plan_name || activeRoom.rate_plan_code || '-'}</div></div>
              <div><span className="form-label">Departure</span><div>{formatDepartureWithPolicyTime(activeRoom.departure_date, defaultCheckoutTime)}</div></div>
              {activeRoom.actual_arrival_datetime && (
                <div><span className="form-label">Check-In Time</span><div>{fmtDateTimeMedium(activeRoom.actual_arrival_datetime)}</div></div>
              )}
              {activeRoom.arrival_date && activeRoom.departure_date && (() => {
                const arr = new Date(activeRoom.arrival_date + 'T00:00:00');
                const dep = new Date(activeRoom.departure_date + 'T00:00:00');
                const nights = Math.max(0, Math.round((dep - arr) / 86400000));
                return nights > 0 ? <div><span className="form-label">Nights</span><div style={{ fontWeight: 600 }}>{nights}</div></div> : null;
              })()}
            </div>
            {/* OTA Info Banner */}
            {activeRoom.booking_source && activeRoom.channel_booking_id && (
              <div className="info-banner" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                <span className="tag tag-danger">OTA</span>
                <span style={{ fontWeight: 600 }}>{activeRoom.booking_source}</span>
                {activeRoom.channel_name && <span style={{ color: '#6b7280' }}>({activeRoom.channel_name})</span>}
                {activeRoom.pay_at_hotel
                  ? <span className="tag tag-warning">Pay@Hotel</span>
                  : <span className="tag tag-success">Pre-paid</span>}
              </div>
            )}
            {activeRoom.booking_source && !activeRoom.channel_booking_id && (
              <div style={{ fontSize: '0.88rem', marginBottom: '0.5rem' }}>
                <span className="form-label">Booking Source</span> <span style={{ fontWeight: 600 }}>{activeRoom.booking_source}</span>
              </div>
            )}
            {/* Pre-Check-In Status */}
            {activeRoom.reservation_id && activeRoom.pre_checkin_status && activeRoom.pre_checkin_status !== 'NOT_SENT' && (
              <div style={{ fontSize: '0.82rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span className="form-label" style={{ margin: 0 }}>Pre-Check-In</span>
                <span className={`tag ${activeRoom.pre_checkin_status === 'COMPLETED' ? 'tag-success' : activeRoom.pre_checkin_status === 'PARTIAL' ? 'tag-warning' : 'tag-info'}`}>
                  {activeRoom.pre_checkin_status === 'COMPLETED' ? 'Completed' : activeRoom.pre_checkin_status === 'PARTIAL' ? 'Partial' : 'Sent'}
                </span>
              </div>
            )}
            {/* Guest Portal Link — visible whenever a stay is active so staff
                can re-share the URL after the check-in modal has closed. */}
            {isOccupiedRoom && activeRoom.stay_id && selectedProperty?.code && (() => {
              const portalUrl = buildGuestPortalUrl(selectedProperty.code);
              if (!portalUrl) return null;
              const guestName = encodeURIComponent(activeRoom.guest_name || activeRoom.primary_guest_name || 'Guest');
              const waMsg = encodeURIComponent(`Welcome! Access your room services & bill here: ${portalUrl}`);
              const waLink = `https://wa.me/?text=${waMsg}`;
              return (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '7px', padding: '0.6rem 0.8rem', marginBottom: '0.6rem', fontSize: '0.84rem' }}>
                  <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '0.3rem' }}>Guest Portal Link</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input readOnly value={portalUrl} onClick={e => e.target.select()}
                      style={{ flex: '1 1 240px', minWidth: 0, border: '1px solid #93c5fd', borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '0.82rem', background: '#fff', color: '#1e3a5f' }} />
                    <button type="button" onClick={() => { navigator.clipboard.writeText(portalUrl); }}
                      style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.35rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Copy</button>
                    <a href={waLink} target="_blank" rel="noopener noreferrer"
                      style={{ background: '#16a34a', color: '#fff', textDecoration: 'none', borderRadius: '4px', padding: '0.35rem 0.7rem', fontSize: '0.78rem', fontWeight: 600 }}>WhatsApp</a>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.3rem' }}>
                    For {decodeURIComponent(guestName)} — room service, housekeeping & bill access.
                  </div>
                </div>
              );
            })()}
            {/* Primary Actions */}
            <div className="action-bar">
              {canWalkIn && !isReservedVacant ? <button onClick={() => openWalkInForVacantRoom(activeRoom)} className="btn-success btn-sm rounded">Walk-In Check-In</button> : null}
              {canResvCheckIn ? <button onClick={() => openCheckInModal(activeRoom)} className="btn-primary btn-sm rounded">Resv Check-In</button> : null}
              {canResvCheckIn && ciElig ? (
                ciElig.eligible ? (
                  <button onClick={() => expressCheckIn(activeRoom)} disabled={expressBusy} className="btn-sm rounded" title="One-click check-in — all guards satisfied" style={{ background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 700, cursor: expressBusy ? 'wait' : 'pointer' }}>⚡ Express Check-In</button>
                ) : (
                  <button disabled className="btn-sm rounded" title={`Express unavailable: ${(ciElig.reasons || []).join('; ')}`} style={{ background: '#ede9fe', color: '#a78bda', border: '1px dashed #c4b5fd', fontWeight: 700, cursor: 'not-allowed' }}>⚡ Express Check-In</button>
                )
              ) : null}
              {isReservedVacant && activeRoom.reservation_id && (
                <button onClick={async () => {
                  try {
                    const res = await fetch(`/api/v1/pms/pre-checkin/send/${activeRoom.reservation_id}`, {
                      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
                        'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' }
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data?.error || 'Failed');
                    alert('Pre-check-in link sent!\n' + (data.link || data.full_url));
                    load();
                  } catch (err) { alert(err.message); }
                }} className="btn-muted btn-sm rounded" style={{ background: '#6366f1', color: '#fff', borderColor: '#6366f1' }}>
                  Send Pre-CI Link
                </button>
              )}
              {!activeRoom.stay_id && activeRoom.reservation_id && typeof onEditReservation === 'function' && (
                <button onClick={() => onEditReservation(activeRoom.reservation_id)} className="btn-muted btn-sm rounded" title="Change the room assigned to this reservation before check-in">
                  Reassign Room
                </button>
              )}
              {canCheckout ? <button onClick={() => openCheckoutModal(activeRoom)} className="btn-warning btn-sm rounded">Checkout</button> : null}
              {canCheckout && coElig ? (
                coElig.eligible ? (
                  <button onClick={() => expressCheckout(activeRoom)} disabled={expressBusy} className="btn-sm rounded" title="One-click checkout — folio balance is ₹0" style={{ background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 700, cursor: expressBusy ? 'wait' : 'pointer' }}>⚡ Express Checkout</button>
                ) : (
                  <button disabled className="btn-sm rounded" title={`Express unavailable: ${(coElig.reasons || []).join('; ')}`} style={{ background: '#ede9fe', color: '#a78bda', border: '1px dashed #c4b5fd', fontWeight: 700, cursor: 'not-allowed' }}>⚡ Express Checkout</button>
                )
              ) : null}
              {isOccupiedRoom && activeRoom.stay_id && activeRoom.reservation_id ? (
                <button
                  onClick={() => setLockEncodeModal({
                    reservationId: activeRoom.reservation_id,
                    roomNumber: activeRoom.room_number,
                    guestName: activeRoom.guest_name || activeRoom.primary_guest_name || ''
                  })}
                  className="btn-muted btn-sm rounded"
                  title="Encode a key card for this room — retries automatically if the encoder reports an error"
                  style={{ background: '#0f766e', color: '#fff', borderColor: '#0f766e' }}
                >
                  🔑 Encode Card
                </button>
              ) : null}
              {isOccupiedRoom && activeRoom.stay_id && activeRoom.reservation_id ? (
                <button
                  onClick={() => {
                    setUndoCheckinError(''); setUndoCheckinReason('');
                    setUndoCheckinModal({
                      reservation_id: activeRoom.reservation_id,
                      room_number: activeRoom.room_number,
                      guest_name: activeRoom.guest_name || activeRoom.primary_guest_name || ''
                    });
                  }}
                  className="btn-muted btn-sm rounded"
                  title="Undo a mistaken check-in — only available same-day, before any other charge is posted"
                  style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', fontWeight: 600 }}
                >
                  ↩ Undo Check-In
                </button>
              ) : null}
              {canBlockRoom ? <button onClick={() => { setBlockModal({ room_id: activeRoom.room_id, room_number: activeRoom.room_number }); setBlockReason(''); setBlockType('MAINTENANCE'); }} className="btn-danger btn-sm rounded">Block Room</button> : null}
              {canUnblockRoom ? <button onClick={() => runRoomAction('clear', activeRoom)} className="btn-muted btn-sm rounded">Unblock</button> : null}
            </div>
            {isOccupiedRoom && activeRoom.stay_id ? (
              <div style={{ marginTop: '0.8rem', borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.45rem' }}>Guest Folio</div>
                {/* Folio tabs — show when multiple folios exist */}
                {Array.isArray(roomFolio?.all_folios) && roomFolio.all_folios.length > 1 && (
                  <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    {roomFolio.all_folios.map((fo) => {
                      const isActive = Number(fo.folio_id) === Number(activeFolioId);
                      return (
                        <button
                          key={fo.folio_id}
                          onClick={() => {
                            if (!isActive && activeRoom?.stay_id) {
                              reloadRoomFolioByStay(activeRoom.stay_id, fo.folio_id);
                            }
                          }}
                          style={{
                            padding: '0.25rem 0.6rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                            border: isActive ? '2px solid var(--theme-primary)' : '1px solid #cbd5e0',
                            background: isActive ? '#ebf8ff' : '#fff',
                            color: isActive ? 'var(--theme-primary)' : 'var(--theme-text-secondary)',
                            cursor: isActive ? 'default' : 'pointer',
                          }}
                        >
                          {fo.folio_label || `Folio #${fo.folio_id}`}
                          <span style={{ fontSize: '0.72rem', marginLeft: 4, color: Number(fo.balance) > 0 ? '#c53030' : '#276749' }}>
                            ({toMoney(fo.balance)})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {roomFolioLoading ? (
                  <div style={{ color: '#4a5568' }}>Loading folio...</div>
                ) : roomFolioError ? (
                  <div style={{ color: '#c53030' }}>{roomFolioError}</div>
                ) : roomFolio ? (
                  (() => {
                    const lines = Array.isArray(roomFolio.lines) ? roomFolio.lines : [];
                    const PAYMENT_TYPES = new Set(['ADVANCE', 'PAYMENT', 'ADVANCE_PAYMENT', 'CHECKOUT_PAYMENT', 'REFUND', 'VOID', 'CITY_LEDGER_TRANSFER']);
                    const ALLOWANCE_TYPES = new Set(['ALLOWANCE']);
                    const chargeLines = lines.filter((line) => {
                      const pt = String(line.posting_type || '').toUpperCase();
                      return !PAYMENT_TYPES.has(pt) && !ALLOWANCE_TYPES.has(pt);
                    });
                    const paymentLines = lines.filter((line) => PAYMENT_TYPES.has(String(line.posting_type || '').toUpperCase()));
                    const allowanceLines = lines.filter((line) => ALLOWANCE_TYPES.has(String(line.posting_type || '').toUpperCase()));
                    const totalCharges = chargeLines.reduce((sum, l) => sum + Number(l.debit_amount || 0), 0);
                    const totalPayments = paymentLines.reduce((sum, l) => sum + Number(l.credit_amount || 0), 0);
                    const totalAllowances = allowanceLines.reduce((sum, l) => sum + Number(l.credit_amount || 0), 0);
                    const folioBalance = Number(roomFolio?.folio?.balance || 0);
                    const amountToCollect = checkoutRoundOffEnabled ? Math.round(Math.max(0, folioBalance)) : Math.max(0, folioBalance);
                    const advanceExcess = Math.max(0, -folioBalance);
                    const postingTypeLabel = (pt) => {
                      const map = { ROOM_TARIFF: 'Room Tariff', KOT_ROOM_CHARGE: 'KOT', BAR_ROOM_CHARGE: 'BAR', MINIBAR_ROOM_CHARGE: 'Minibar', MINI_BAR: 'Minibar', LAUNDRY: 'Laundry', ROOM_SERVICE: 'Room Service', MANUAL: 'Manual', ALLOWANCE: 'Allowance / Waiver', LATE_CHECKOUT: 'Late Checkout', ADVANCE: 'Advance', PAYMENT: 'Payment', ADVANCE_PAYMENT: 'Advance Payment', CHECKOUT_PAYMENT: 'Checkout Payment', REFUND: 'Refund', VOID: 'Void', CITY_LEDGER_TRANSFER: 'City Ledger', ROUTED_IN: 'Routed In (Linked)', ROUTED_OUT: 'Routed Out (Linked)', FOLIO_ROUTE_TRANSFER: 'Route Transfer' };
                      return map[String(pt || '').toUpperCase()] || String(pt || '-');
                    };
                    return (
                      <>
                        {/* Summary strip */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.7rem', background: '#f7fafc', borderRadius: 6, padding: '0.5rem 0.7rem', border: '1px solid var(--theme-border)' }}>
                          <div><b>Total Charges:</b> <span style={{ color: '#c53030' }}>{toMoney(totalCharges)}</span></div>
                          <div><b>Allowances:</b> <span style={{ color: '#b45309' }}>{toMoney(totalAllowances)}</span></div>
                          <div><b>Payments Received:</b> <span style={{ color: '#276749' }}>{toMoney(totalPayments)}</span></div>
                          <div><b>Folio Balance:</b> <span style={{ color: folioBalance > 0 ? '#c53030' : '#276749', fontWeight: 700 }}>{toMoney(folioBalance)}</span></div>
                          <div><b>Amount To Collect:</b> {toMoney(amountToCollect)}</div>
                          <div><b>Advance Excess:</b> {toMoney(advanceExcess)}</div>
                        </div>

                        {/* Routing notification banner */}
                        {postingRoutingMsg && (
                          <div style={{ background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, padding: '0.4rem 0.7rem', marginBottom: '0.5rem', fontSize: '0.83rem', color: '#2a69ac', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>Charge automatically routed to master folio — ₹{Number(postingRoutingMsg.routed_amount || 0).toFixed(2)} transferred. View master room to see "Routed In (Linked)" entry.</span>
                            <button onClick={() => setPostingRoutingMsg(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#2a69ac', fontWeight: 700, fontSize: '1rem', marginLeft: 8 }}>×</button>
                          </div>
                        )}
                        {/* All postings table */}
                        <div style={{ fontWeight: 600, fontSize: '0.87rem', marginBottom: '0.3rem' }}>
                          All Folio Postings (Base Room: {roomFolio?.room_number || activeRoom?.room_number || '-'})
                        </div>
                        <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #cbd5e0', borderRadius: '6px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: '#2d3748', color: '#fff', position: 'sticky', top: 0 }}>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left' }}>Type</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left' }}>Room</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'center' }}>Split</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>Charge</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'right' }}>Credit Amount</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left' }}>Status</th>
                                <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left' }}>Remarks</th>
                                {isAdminUser && <th style={{ padding: '0.35rem 0.4rem', textAlign: 'center' }}>Action</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {lines.map((line, idx) => {
                                const pt = String(line.posting_type || '').toUpperCase();
                                const isPayment = PAYMENT_TYPES.has(pt);
                                const isAllowance = ALLOWANCE_TYPES.has(pt);
                                const isCreditAdjustment = isPayment || isAllowance;
                                const isRoutedIn = pt === 'ROUTED_IN';
                                const isRoutedOut = pt === 'ROUTED_OUT';
                                const isRouting = isRoutedIn || isRoutedOut;
                                const remarksText = String(line.remarks || '');
                                const linkedChildFromRemarks = remarksText.match(/Linked child\s+([^:\s]+)/i)?.[1] || null;
                                const routedFromStay = remarksText.match(/from stay\s+(\d+)/i)?.[1] || null;
                                const lineRoom = line.linked_child_room_number
                                  || linkedChildFromRemarks
                                  || (isRoutedIn && routedFromStay ? `Stay ${routedFromStay}` : null)
                                  || roomFolio?.room_number
                                  || activeRoom?.room_number
                                  || '-';
                                const chargeDisplay = isAllowance
                                  ? (Number(line.credit_amount) > 0 ? `-${toMoney(line.credit_amount)}` : '')
                                  : (Number(line.debit_amount) > 0 ? toMoney(line.debit_amount) : '');
                                const paymentDisplay = isAllowance
                                  ? ''
                                  : (Number(line.credit_amount) > 0 ? toMoney(line.credit_amount) : '');
                                const paymentStatusCode = String(line.payment_status || '').toUpperCase();
                                const paymentStatusLabel = (
                                  paymentStatusCode === 'RECEIVED' ? 'Received'
                                    : paymentStatusCode === 'TRANSFERRED' ? 'Transferred'
                                      : paymentStatusCode === 'REVERSED' ? 'Reversed'
                                        : paymentStatusCode === 'REFUNDED' ? 'Refunded'
                                          : ''
                                );
                                let rowBg = idx % 2 === 0 ? '#fff' : '#f7fafc';
                                if (isRoutedIn) rowBg = '#ebf8ff';
                                if (isRoutedOut) rowBg = '#fffbeb';
                                let badgeBg = isCreditAdjustment ? '#c6f6d5' : '#fed7d7';
                                let badgeColor = isCreditAdjustment ? '#276749' : '#9b2c2c';
                                if (isAllowance) { badgeBg = '#feebc8'; badgeColor = '#9c4221'; }
                                if (isRoutedIn) { badgeBg = '#bee3f8'; badgeColor = '#2a69ac'; }
                                if (isRoutedOut) { badgeBg = '#fefcbf'; badgeColor = '#744210'; }
                                return (
                                  <tr key={line.ledger_id || idx} style={{ borderTop: '1px solid #edf2f7', background: rowBg }}>
                                    <td style={{ padding: '0.3rem 0.4rem', whiteSpace: 'nowrap' }}>{line.posting_date ? String(line.posting_date).slice(0, 10) : (line.created_at ? String(line.created_at).slice(0, 10) : '-')}</td>
                                    <td style={{ padding: '0.3rem 0.4rem' }}>
                                      <span style={{ fontSize: '0.78rem', padding: '1px 6px', borderRadius: 4, background: badgeBg, color: badgeColor, fontWeight: 600 }}>
                                        {postingTypeLabel(line.posting_type)}
                                      </span>
                                    </td>
                                    <td style={{ padding: '0.3rem 0.4rem', whiteSpace: 'nowrap', color: '#1f2937', fontWeight: isRouting ? 600 : 500 }}>{lineRoom}</td>
                                    <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                      <span style={{ fontSize: '0.75rem', padding: '1px 6px', borderRadius: 4, background: Number(line.split_no || 1) > 1 ? '#e9d8fd' : '#edf2f7', color: Number(line.split_no || 1) > 1 ? '#553c9a' : '#4a5568', fontWeight: 700 }}>
                                        {Number(line.split_no || 1)}
                                      </span>
                                    </td>
                                    <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', color: isAllowance ? '#b45309' : '#c53030' }}>{chargeDisplay}</td>
                                    <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', color: '#276749' }}>{paymentDisplay}</td>
                                    <td style={{ padding: '0.3rem 0.4rem', whiteSpace: 'nowrap' }}>
                                      {paymentStatusLabel ? (
                                        <span
                                          style={{
                                            fontSize: '0.75rem',
                                            padding: '1px 6px',
                                            borderRadius: 4,
                                            background: paymentStatusCode === 'RECEIVED'
                                              ? '#c6f6d5'
                                              : paymentStatusCode === 'TRANSFERRED'
                                                ? '#bee3f8'
                                                : paymentStatusCode === 'REVERSED'
                                                  ? '#fed7d7'
                                                  : '#e9d8fd',
                                            color: paymentStatusCode === 'RECEIVED'
                                              ? '#276749'
                                              : paymentStatusCode === 'TRANSFERRED'
                                                ? '#2a69ac'
                                                : paymentStatusCode === 'REVERSED'
                                                  ? '#9b2c2c'
                                                  : '#553c9a',
                                            fontWeight: 600
                                          }}
                                        >
                                          {paymentStatusLabel}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td style={{ padding: '0.3rem 0.4rem', color: isRouting ? '#2a69ac' : '#4a5568', fontStyle: isRouting ? 'italic' : 'normal' }}>
                                      {line.remarks || '-'}
                                      {line.payment_mode && (
                                        <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontSize: '0.7rem', fontWeight: 600 }}>{line.payment_mode}</span>
                                      )}
                                    </td>
                                    {isAdminUser && (
                                      <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                        {pt === 'REVERSAL' ? (
                                          <span style={{ fontSize: '0.7rem', color: '#a0aec0' }} title="REVERSAL rows can't be voided — they're linked to Cancel & Reissue invoices">—</span>
                                        ) : (
                                          <button
                                            onClick={() => voidLedgerRow(line)}
                                            title="Void this posting (hard delete, audit-logged, Hotel Admin / Super Admin only)"
                                            style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
                                          >Void</button>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                              {!lines.length && (
                                <tr><td colSpan={isAdminUser ? 9 : 8} style={{ padding: '0.5rem', color: '#718096', textAlign: 'center' }}>No postings yet</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {/* Linked stay charges summary — shown when ROUTED_IN entries exist */}
                        {(() => {
                          const routedInLines = lines.filter((l) => String(l.posting_type || '').toUpperCase() === 'ROUTED_IN');
                          if (!routedInLines.length) return null;
                          const routedTotal = routedInLines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
                          return (
                            <div style={{ marginTop: '0.5rem', background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 6, padding: '0.4rem 0.7rem', fontSize: '0.82rem' }}>
                              <div style={{ fontWeight: 700, color: '#2a69ac', marginBottom: '0.25rem' }}>Linked Stay Charges Routed to this Folio</div>
                              {routedInLines.map((l, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#2c5282', padding: '0.1rem 0' }}>
                                  <span>{l.remarks || 'Routed from linked room'}</span>
                                  <span style={{ fontWeight: 600 }}>+{toMoney(l.debit_amount)}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: '1px solid #bee3f8', marginTop: '0.25rem', paddingTop: '0.25rem', display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#2a69ac' }}>
                                <span>Total Routed In</span>
                                <span>{toMoney(routedTotal)}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()
                ) : (
                  <div style={{ color: '#718096' }}>No folio data available.</div>
                )}
                {roomFolio && activeRoom.stay_id ? (
                  <div className="action-bar" style={{ marginTop: '0.6rem' }}>
                    <button onClick={openPostingModal} className="btn-muted btn-sm rounded">Manual Posting</button>
                    <button onClick={() => openPostingModal('MINIBAR_ROOM_CHARGE')} className="btn-muted btn-sm rounded">Add Minibar Bill</button>
                    <button onClick={printGuestInvoice} className="btn-primary btn-sm rounded">Print Invoice</button>
                    <button onClick={openRouteModal} className="btn-muted btn-sm rounded">Route Folio</button>
                    <button onClick={openTransferModal} className="btn-muted btn-sm rounded">Transfer City Ledger</button>
                    <button onClick={openGuestTransferModal} className="btn-warning btn-sm rounded">Transfer to Guest</button>
                    <button onClick={openLinkedRoomsModal} className="btn-muted btn-sm rounded">Linked Rooms</button>
                    {typeof onEditReservation === 'function' && activeRoom?.reservation_id && (
                      <button onClick={() => onEditReservation(activeRoom.reservation_id)} className="btn-primary btn-sm rounded">Edit Reservation</button>
                    )}
                    {isAdminUser && activeRoom?.reservation_id && (
                      <button onClick={() => openRateHistory(activeRoom.reservation_id)} className="btn-muted btn-sm rounded">Rate History</button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
            {activeRoom.stay_id ? (
              <div style={{ marginTop: '0.7rem', borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem' }}>
                <div className="section-head">Room Operations</div>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.4rem', alignItems: 'end' }}>
                    <div>
                      <span className="form-label">Change Room</span>
                      <select value={changeTargetRoomId} onChange={(e) => setChangeTargetRoomId(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm w-full">
                        <option value="">Select vacant room</option>
                        {availableRooms
                          .filter((r) => Number(r.room_id) !== Number(activeRoom.room_id))
                          .map((r) => {
                            // A dirty room can't take a shifted guest (the server refuses it too).
                            const dirty = String(r.housekeeping_status || '').toUpperCase() === 'DIRTY';
                            return <option key={r.room_id} value={r.room_id} disabled={dirty}>{r.room_number} - {r.room_type_name}{dirty ? ' (Dirty)' : ''}</option>;
                          })}
                      </select>
                    </div>
                    <button onClick={changeRoomForStay} disabled={!changeTargetRoomId} className="btn-primary btn-sm rounded">Change</button>
                  </div>
                  {/* Shown INSIDE this still-open popup on failure (the modal only
                      closes on success, so this is the one place the user is
                      actually looking at when Change Room is rejected). */}
                  {changeRoomError ? <div className="error-banner" style={{ marginTop: '0.3rem' }}>{changeRoomError}</div> : null}
                  {changeTargetRoomId ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
                      <div>
                        <span className="form-label" style={{ fontSize: '0.75rem' }}>New Adults (optional)</span>
                        <input type="number" min="1" placeholder={`Currently ${activeRoom.adults ?? '-'}`} value={changeTargetAdults}
                          onChange={(e) => setChangeTargetAdults(e.target.value)}
                          className="rounded border border-slate-300 px-2 py-1 text-sm w-full" />
                      </div>
                      <div>
                        <span className="form-label" style={{ fontSize: '0.75rem' }}>New Children (optional)</span>
                        <input type="number" min="0" placeholder={`Currently ${activeRoom.children ?? '0'}`} value={changeTargetChildren}
                          onChange={(e) => setChangeTargetChildren(e.target.value)}
                          className="rounded border border-slate-300 px-2 py-1 text-sm w-full" />
                      </div>
                      <div style={{ gridColumn: '1 / -1', fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
                        Leave blank to keep current occupancy. The tariff stays as it is when the room type or occupancy changes — post any difference with Manual Posting.
                      </div>
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.4rem', alignItems: 'end' }}>
                    <div>
                      <span className="form-label">Swap Room</span>
                      <select value={swapTargetStayId} onChange={(e) => setSwapTargetStayId(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm w-full">
                        <option value="">Select occupied stay</option>
                        {swappableStays.map((r) => <option key={r.stay_id} value={r.stay_id}>Stay #{r.stay_id} - {r.room_number} ({r.primary_guest_name || 'Guest'})</option>)}
                      </select>
                    </div>
                    <button onClick={swapRoomForStay} disabled={!swapTargetStayId} className="btn-warning btn-sm rounded">Swap</button>
                  </div>
                  {swapRoomError ? <div className="error-banner" style={{ marginTop: '0.3rem' }}>{swapRoomError}</div> : null}
                </div>
              </div>
            ) : null}

            {/* Recent Housekeeping Log */}
            {hkRoomLog.length > 0 && (
              <div style={{ marginTop: '0.8rem', borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.35rem', fontSize: '0.9rem' }}>Recent Housekeeping Log</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--theme-border-light)' }}>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Time</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Status Change</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Staff</th>
                      <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hkRoomLog.map((log) => (
                      <tr key={log.log_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                        <td style={{ padding: '0.25rem 0.4rem', whiteSpace: 'nowrap' }}>
                          {fmtDateTimeShort(log.changed_at)}
                        </td>
                        <td style={{ padding: '0.25rem 0.4rem' }}>
                          <span style={{ color: 'var(--theme-text-muted)' }}>{log.previous_status || '?'}</span>
                          {' → '}
                          <span style={{ fontWeight: 600, color: log.new_status === 'CLEAN' ? '#166534' : '#b91c1c' }}>{log.new_status}</span>
                        </td>
                        <td style={{ padding: '0.25rem 0.4rem' }}>{log.changed_by_staff_name || log.changed_by_user || '-'}</td>
                        <td style={{ padding: '0.25rem 0.4rem', fontSize: '0.75rem', color: 'var(--theme-text-secondary)' }}>{(log.source || '').replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      {/* Rate History Modal (Hotel Admin only) */}
      {rateHistoryModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(700px,95vw)', maxHeight: '85vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setRateHistoryModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', fontWeight: 700, color: 'var(--theme-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Close">x</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Rate History — Reservation #{rateHistoryModal.reservation_id}</h4>
            {rateHistoryLoading ? (
              <div style={{ color: '#4a5568' }}>Loading...</div>
            ) : rateHistoryError ? (
              <div style={{ color: '#c53030' }}>{rateHistoryError}</div>
            ) : rateHistoryRows.length === 0 ? (
              <div style={{ color: '#718096' }}>No edits recorded for this reservation's rate, rate plan, room type, or stay dates.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '0.4rem' }}>When</th>
                      <th style={{ padding: '0.4rem' }}>Field</th>
                      <th style={{ padding: '0.4rem' }}>Old Value</th>
                      <th style={{ padding: '0.4rem' }}>New Value</th>
                      <th style={{ padding: '0.4rem' }}>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateHistoryRows.map((row) => (
                      <tr key={row.log_id} style={{ borderBottom: '1px solid #edf2f7' }}>
                        <td style={{ padding: '0.4rem' }}>{new Date(row.created_at).toLocaleString()}</td>
                        <td style={{ padding: '0.4rem' }}>{FIELD_LABELS[row.field_name] || row.field_name}</td>
                        <td style={{ padding: '0.4rem', color: '#c53030' }}>{row.old_value}</td>
                        <td style={{ padding: '0.4rem', color: '#276749', fontWeight: 600 }}>{row.new_value}</td>
                        <td style={{ padding: '0.4rem' }}>{row.changed_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Manual Posting Modal */}
      {postingModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(700px,95vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setPostingModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', fontWeight: 700, color: 'var(--theme-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Close">x</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Manual Folio Posting - {postingModal.folio_label}</h4>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Posting Type
                <select
                  value={postingForm.service_key || ''}
                  onChange={(e) => {
                    applyPostingTypeSelection(e.target.value);
                  }}
                  style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                >
                  <option value="">Select Posting Type</option>
                  {postingTypeOptions.map((opt) => (
                    <option key={opt.service_key} value={opt.service_key}>
                      {opt.label} ({opt.posting_type})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Source Module
                <select value={postingForm.source_module} onChange={(e) => setPostingForm((f) => ({ ...f, source_module: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                  <option value="FRONT_OFFICE">Front Office</option>
                  <option value="KOT_POS">KOT/POS</option>
                  <option value="BAR">Bar</option>
                  <option value="HOUSEKEEPING">Housekeeping</option>
                  <option value="BANQUET">Banquet</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Tax Profile
                <select
                  value={postingForm.tax_profile_id || ''}
                  disabled={String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE'}
                  onChange={(e) => {
                    const profileId = e.target.value;
                    if (profileId === NO_TAX_PROFILE_OPTION) {
                      setPostingForm((f) => ({
                        ...f,
                        tax_profile_id: NO_TAX_PROFILE_OPTION,
                        gst_percent: '0',
                        cgst_amount: '0',
                        sgst_amount: '0',
                        igst_amount: '0',
                        tax_amount: '0'
                      }));
                      return;
                    }
                    const selectedProfile = postingTaxProfiles.find((row) => String(row.id) === String(profileId));
                    const taxBreakup = computePostingTaxBreakup({
                      debitAmount: postingForm.debit_amount,
                      selectedProfile,
                      gstPercent: selectedProfile ? resolveEffectiveTaxPercent(selectedProfile) : 0,
                      taxMode: postingForm.tax_mode
                    });
                    setPostingForm((f) => ({
                      ...f,
                      tax_profile_id: profileId,
                      ...taxBreakup
                    }));
                  }}
                  style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                >
                  <option value="">Select Tax Profile</option>
                  <option value={NO_TAX_PROFILE_OPTION}>No Tax (0%)</option>
                  {postingTaxProfiles
                    .filter((row) => row?.is_active !== false)
                    .map((row) => {
                      const effective = resolveEffectiveTaxPercent(row);
                      return (
                        <option key={row.id} value={row.id}>
                          {(row.tax_code || row.tax_name || `Tax ${row.id}`)} ({effective.toFixed(2)}%)
                        </option>
                      );
                    })}
                </select>
              </label>
            </div>

            {String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE' ? (
              <div style={{ marginBottom: '0.6rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.6rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                  Allowance Mode
                  <select
                    value={postingForm.allowance_mode || 'PERCENT'}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setPostingForm((f) => ({
                        ...f,
                        allowance_mode: mode,
                        allowance_percent: mode === 'PERCENT' ? f.allowance_percent : '',
                        credit_amount: mode === 'PERCENT' ? f.credit_amount : ''
                      }));
                    }}
                    style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                  >
                    <option value="PERCENT">Percentage</option>
                    <option value="AMOUNT">Amount</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                  Base Amount
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={postingForm.allowance_base_amount}
                    onChange={(e) => {
                      const nextBase = e.target.value;
                      const mode = String(postingForm.allowance_mode || 'PERCENT').toUpperCase();
                      const percent = Number(postingForm.allowance_percent || 0);
                      const base = Number(nextBase || 0);
                      const computed = mode === 'PERCENT' ? (Number.isFinite(percent) && Number.isFinite(base) ? Number((base * percent / 100).toFixed(2)) : 0) : Number(postingForm.credit_amount || 0);
                      setPostingForm((f) => ({
                        ...f,
                        allowance_base_amount: nextBase,
                        credit_amount: mode === 'PERCENT' ? String(Math.max(0, computed)) : f.credit_amount
                      }));
                    }}
                    style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                  />
                </label>
                {String(postingForm.allowance_mode || 'PERCENT').toUpperCase() === 'PERCENT' ? (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Allowance %
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={postingForm.allowance_percent}
                      onChange={(e) => {
                        const nextPercent = e.target.value;
                        const percent = Number(nextPercent || 0);
                        const base = Number(postingForm.allowance_base_amount || 0);
                        const computed = Number.isFinite(percent) && Number.isFinite(base) ? Number((base * percent / 100).toFixed(2)) : 0;
                        setPostingForm((f) => ({
                          ...f,
                          allowance_percent: nextPercent,
                          credit_amount: String(Math.max(0, computed))
                        }));
                      }}
                      style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                    />
                  </label>
                ) : (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Allowance Amount
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={postingForm.credit_amount}
                      onChange={(e) => setPostingForm((f) => ({ ...f, credit_amount: e.target.value, debit_amount: '' }))}
                      style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                    />
                  </label>
                )}
              </div>
            ) : ['ADVANCE', 'PAYMENT', 'ADVANCE_PAYMENT', 'CHECKOUT_PAYMENT'].includes(String(postingForm.posting_type || '').toUpperCase()) ? (
              <div style={{ marginBottom: '0.6rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600, color: 'var(--theme-success)' }}>
                  Payment / Credit Amount (reduces guest balance)
                  <input
                    type="text"
                    inputMode="decimal"
                    value={postingForm.credit_amount}
                    onChange={(e) => {
                      setPostingForm((f) => ({ ...f, credit_amount: e.target.value, debit_amount: '' }));
                    }}
                    style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #22c55e' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                  Payment Mode
                  <select
                    value={postingForm.payment_mode || 'CASH'}
                    onChange={(e) => setPostingForm((f) => ({ ...f, payment_mode: e.target.value }))}
                    style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="GPAY">GPay (UPI)</option>
                    <option value="PHONEPE">PhonePe (UPI)</option>
                    <option value="PAYTM">Paytm (UPI)</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="NEFT">NEFT</option>
                    <option value="RTGS">RTGS</option>
                    <option value="IMPS">IMPS</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="CORPORATE">Corporate (Bill to Co.)</option>
                    <option value="CITY_LEDGER">City Ledger</option>
                  </select>
                  <small style={{ color: '#718096', fontSize: '0.7rem' }}>
                    GPay / PhonePe / Paytm stored as UPI for reports; put the transaction ID in Reference No below.
                  </small>
                </label>
              </div>
            ) : (
              <div style={{ marginBottom: '0.6rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                  Charge Amount (added to guest bill)
                  <input
                    type="text"
                    inputMode="decimal"
                    value={postingForm.debit_amount}
                    onChange={(e) => {
                      const nextDebit = e.target.value;
                      const selectedProfile = postingTaxProfiles.find((row) => String(row.id) === String(postingForm.tax_profile_id || '')) || null;
                      const taxBreakup = computePostingTaxBreakup({
                        debitAmount: nextDebit,
                        selectedProfile,
                        gstPercent: postingForm.gst_percent,
                        taxMode: postingForm.tax_mode
                      });
                      setPostingForm((f) => ({ ...f, debit_amount: nextDebit, credit_amount: '', ...taxBreakup }));
                    }}
                    style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0', width: '50%' }}
                  />
                </label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.4rem' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4a5568' }}>GST:</span>
                  {[
                    { value: 'EXCLUSIVE', label: 'Exclusive (GST added on top)' },
                    { value: 'INCLUSIVE', label: 'Inclusive (amount already includes GST)' }
                  ].map((opt) => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="posting_tax_mode"
                        checked={String(postingForm.tax_mode || 'EXCLUSIVE').toUpperCase() === opt.value}
                        onChange={() => {
                          const selectedProfile = postingTaxProfiles.find((row) => String(row.id) === String(postingForm.tax_profile_id || '')) || null;
                          const taxBreakup = computePostingTaxBreakup({
                            debitAmount: postingForm.debit_amount,
                            selectedProfile,
                            gstPercent: postingForm.gst_percent,
                            taxMode: opt.value
                          });
                          setPostingForm((f) => ({ ...f, tax_mode: opt.value, ...taxBreakup }));
                        }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Revenue Code
                <input type="text" value={postingForm.revenue_code} onChange={(e) => setPostingForm((f) => ({ ...f, revenue_code: e.target.value }))} placeholder="RC_FOOD" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Revenue Description
                <input type="text" value={postingForm.revenue_description} onChange={(e) => setPostingForm((f) => ({ ...f, revenue_description: e.target.value }))} placeholder="Food & Beverage" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, 1fr)', gap: '0.5rem', marginBottom: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                GST %
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE'}
                  value={postingForm.gst_percent}
                  onChange={(e) => {
                    const nextGst = e.target.value;
                    const selectedProfile = postingTaxProfiles.find((row) => String(row.id) === String(postingForm.tax_profile_id || '')) || null;
                    const taxBreakup = computePostingTaxBreakup({
                      debitAmount: postingForm.debit_amount,
                      selectedProfile,
                      gstPercent: nextGst,
                      taxMode: postingForm.tax_mode
                    });
                    setPostingForm((f) => ({ ...f, ...taxBreakup }));
                  }}
                  style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                CGST
                <input type="number" min="0" step="0.01" disabled={String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE'} value={postingForm.cgst_amount} onChange={(e) => setPostingForm((f) => ({ ...f, cgst_amount: e.target.value }))} style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                SGST
                <input type="number" min="0" step="0.01" disabled={String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE'} value={postingForm.sgst_amount} onChange={(e) => setPostingForm((f) => ({ ...f, sgst_amount: e.target.value }))} style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                IGST
                <input type="number" min="0" step="0.01" disabled={String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE'} value={postingForm.igst_amount} onChange={(e) => setPostingForm((f) => ({ ...f, igst_amount: e.target.value }))} style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
                Total Tax
                <input type="number" min="0" step="0.01" disabled={String(postingForm.posting_type || '').toUpperCase() === 'ALLOWANCE'} value={postingForm.tax_amount} onChange={(e) => setPostingForm((f) => ({ ...f, tax_amount: e.target.value }))} style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Reference Type
                <input type="text" value={postingForm.reference_type} onChange={(e) => setPostingForm((f) => ({ ...f, reference_type: e.target.value }))} placeholder="KOT_ORDER / BAR_ORDER" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Reference ID
                <input type="text" value={postingForm.reference_id} onChange={(e) => setPostingForm((f) => ({ ...f, reference_id: e.target.value }))} placeholder="Order/Invoice #" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600, marginBottom: '0.6rem' }}>
              Remarks
              <textarea value={postingForm.remarks} onChange={(e) => setPostingForm((f) => ({ ...f, remarks: e.target.value }))} rows="2" placeholder="Manual posting notes" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontFamily: 'inherit' }}></textarea>
            </label>

            {postingError ? <div className="error-banner">{postingError}</div> : null}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-muted btn-sm rounded" onClick={() => setPostingModal(null)} disabled={postingBusy}>Cancel</button>
              <button className="btn-primary btn-sm rounded" onClick={submitPosting} disabled={postingBusy}>
                {postingBusy ? 'Posting...' : 'Submit Posting'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Split Folio Modal */}
      {splitModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(800px,95vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setSplitModal(null)} style={{ position: 'absolute', top: '0.45rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}>&times;</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Split Folio - {splitModal.folio_label}</h4>

            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Split Mode
                <select
                  value={splitMode}
                  onChange={(e) => {
                    setSplitMode(e.target.value);
                    setSplitError('');
                  }}
                  style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                >
                  <option value="MOVE_LINES">Move Lines To Another Folio</option>
                  <option value="SET_SPLIT_NO">Set Split Number</option>
                </select>
              </label>
            </div>

            {splitMode === 'MOVE_LINES' ? (
              <>
                <div style={{ marginBottom: '0.7rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Select Ledger Lines to Move</div>
                  {Number(splitModal.excluded_count || 0) > 0 ? (
                    <div style={{ fontSize: '0.8rem', color: '#744210', background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 5, padding: '0.3rem 0.45rem', marginBottom: '0.35rem' }}>
                      {splitModal.excluded_count} line(s) are excluded because they belong to linked/routed folio view rows and cannot be moved from this room folio.
                    </div>
                  ) : null}
                  {(() => {
                    const moveLines = Array.isArray(splitModal.move_lines) ? splitModal.move_lines : [];
                    const selectedCount = moveLines.filter((line) => splitSelectedLines.has(line.ledger_id)).length;
                    const allSelected = moveLines.length > 0 && selectedCount === moveLines.length;
                    return (
                      <>
                        <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #cbd5e0', borderRadius: '6px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                              <tr style={{ background: '#ebf8ff' }}>
                                <th style={{ padding: '0.3rem', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) setSplitSelectedLines(new Set(moveLines.map((line) => line.ledger_id)));
                                      else setSplitSelectedLines(new Set());
                                    }}
                                  />
                                </th>
                                <th style={{ padding: '0.3rem', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '0.3rem', textAlign: 'left' }}>Type</th>
                                <th style={{ padding: '0.3rem', textAlign: 'right' }}>Debit</th>
                                <th style={{ padding: '0.3rem', textAlign: 'right' }}>Credit</th>
                                <th style={{ padding: '0.3rem', textAlign: 'left' }}>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {moveLines.map((line) => (
                                <tr key={line.ledger_id} style={{ borderTop: '1px solid #edf2f7' }}>
                                  <td style={{ padding: '0.3rem', textAlign: 'center' }}>
                                    <input
                                      type="checkbox"
                                      checked={splitSelectedLines.has(line.ledger_id)}
                                      onChange={(e) => {
                                        const newSet = new Set(splitSelectedLines);
                                        if (e.target.checked) newSet.add(line.ledger_id);
                                        else newSet.delete(line.ledger_id);
                                        setSplitSelectedLines(newSet);
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '0.3rem' }}>{line.created_at ? String(line.created_at).slice(0, 16).replace('T', ' ') : '-'}</td>
                                  <td style={{ padding: '0.3rem' }}>{line.posting_type || '-'}</td>
                                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{toMoney(line.debit_amount)}</td>
                                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{toMoney(line.credit_amount)}</td>
                                  <td style={{ padding: '0.3rem' }}>{line.remarks || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ fontSize: '0.85rem', marginTop: '0.3rem', color: '#4a5568' }}>
                          {selectedCount} of {moveLines.length} line(s) selected
                        </div>

                        {/* Selection summary */}
                        {selectedCount > 0 && (() => {
                          const selLines = moveLines.filter((l) => splitSelectedLines.has(l.ledger_id));
                          const totalDebit = selLines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
                          const totalCredit = selLines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);
                          const netAmount = totalDebit - totalCredit;
                          return (
                            <div style={{
                              display: 'flex', gap: '1.2rem', marginTop: '0.35rem', padding: '0.4rem 0.6rem',
                              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.83rem', fontWeight: 600,
                            }}>
                              <span>Total Debit: <span style={{ color: 'var(--theme-danger)' }}>{toMoney(totalDebit)}</span></span>
                              <span>Total Credit: <span style={{ color: 'var(--theme-success)' }}>{toMoney(totalCredit)}</span></span>
                              <span>Net: <span style={{ color: netAmount >= 0 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>{toMoney(Math.abs(netAmount))} {netAmount >= 0 ? 'DR' : 'CR'}</span></span>
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>

                <div style={{ marginBottom: '0.7rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Split Action
                    <select value={splitTargetAction} onChange={(e) => { setSplitTargetAction(e.target.value); setSplitTargetFolioId(''); }} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                      <option value="CREATE_NEW">Create New Folio</option>
                      <option value="EXISTING_FOLIO">Move to Existing Folio</option>
                    </select>
                  </label>
                </div>

                {splitTargetAction === 'CREATE_NEW' ? (
                  <div style={{ marginBottom: '0.7rem' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                      New Folio Label
                      <input type="text" value={splitTargetLabel} onChange={(e) => setSplitTargetLabel(e.target.value)} placeholder="Leave blank for auto: Split 1 / Split 2 / Split 3..." style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                    </label>
                  </div>
                ) : (
                  <div style={{ marginBottom: '0.7rem' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                      Target Folio
                      {splitFoliosLoading ? (
                        <span style={{ fontSize: '0.82rem', color: 'var(--theme-text-muted)', fontWeight: 400 }}>Loading folios...</span>
                      ) : splitStayFolios.length === 0 ? (
                        <span style={{ fontSize: '0.82rem', color: '#f59e0b', fontWeight: 400 }}>No other folios exist for this stay. Use "Create New Folio" instead.</span>
                      ) : (
                        <select value={splitTargetFolioId} onChange={(e) => setSplitTargetFolioId(e.target.value)} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                          <option value="">-- Select target folio --</option>
                          {splitStayFolios.map((fo) => (
                            <option key={fo.folio_id} value={fo.folio_id}>
                              {fo.folio_label || `Folio #${fo.folio_id}`} (Bal: {toMoney(fo.balance)})
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                  </div>
                )}
              </>
            ) : (
              <div style={{ marginBottom: '0.7rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Assign Split Number Per Ledger Line</div>
                <div style={{ fontSize: '0.82rem', color: '#4a5568', marginBottom: '0.4rem' }}>
                  Use this to control how lines are grouped for folio/invoice preview without physically moving lines to another folio.
                </div>
                {(() => {
                  const splitLines = Array.isArray(splitModal.split_lines) ? splitModal.split_lines : [];
                  const changedCount = splitLines.filter((line) => {
                    const ledgerId = Number(line.ledger_id);
                    const next = Number(splitNoDraftByLedger[ledgerId]);
                    const nextSafe = Number.isInteger(next) && next > 0 ? next : 1;
                    const base = Number(splitNoBaseByLedger[ledgerId] || 1);
                    return nextSafe !== base;
                  }).length;
                  return (
                    <>
                      <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #cbd5e0', borderRadius: '6px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                          <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                            <tr style={{ background: '#ebf8ff' }}>
                              <th style={{ padding: '0.3rem', textAlign: 'left' }}>Date</th>
                              <th style={{ padding: '0.3rem', textAlign: 'left' }}>Type</th>
                              <th style={{ padding: '0.3rem', textAlign: 'right' }}>Debit</th>
                              <th style={{ padding: '0.3rem', textAlign: 'right' }}>Credit</th>
                              <th style={{ padding: '0.3rem', textAlign: 'center' }}>Split No</th>
                              <th style={{ padding: '0.3rem', textAlign: 'left' }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {splitLines.map((line) => {
                              const ledgerId = Number(line.ledger_id);
                              const draftRaw = splitNoDraftByLedger[ledgerId];
                              const draftParsed = Number(draftRaw);
                              const splitNo = Number.isInteger(draftParsed) && draftParsed > 0 ? draftParsed : 1;
                              const base = Number(splitNoBaseByLedger[ledgerId] || 1);
                              const isChanged = splitNo !== base;
                              return (
                                <tr key={line.ledger_id} style={{ borderTop: '1px solid #edf2f7', background: isChanged ? '#fffaf0' : '#fff' }}>
                                  <td style={{ padding: '0.3rem' }}>{line.created_at ? String(line.created_at).slice(0, 16).replace('T', ' ') : '-'}</td>
                                  <td style={{ padding: '0.3rem' }}>{line.posting_type || '-'}</td>
                                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{toMoney(line.debit_amount)}</td>
                                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{toMoney(line.credit_amount)}</td>
                                  <td style={{ padding: '0.3rem', textAlign: 'center' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={splitNoDraftByLedger[ledgerId] ?? 1}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        setSplitNoDraftByLedger((prev) => ({ ...prev, [ledgerId]: raw }));
                                      }}
                                      style={{ width: '80px', padding: '0.2rem 0.35rem', borderRadius: '4px', border: '1px solid #cbd5e0', textAlign: 'right' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0.3rem' }}>{line.remarks || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ fontSize: '0.85rem', marginTop: '0.3rem', color: changedCount > 0 ? '#2f855a' : '#4a5568', fontWeight: changedCount > 0 ? 600 : 500 }}>
                        {changedCount} line(s) changed
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {splitError ? <div className="error-banner">{splitError}</div> : null}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-muted btn-sm rounded" onClick={() => setSplitModal(null)} disabled={splitBusy}>Cancel</button>
              <button className="btn-primary btn-sm rounded" onClick={submitSplit} disabled={splitBusy}>
                {splitBusy ? (splitMode === 'SET_SPLIT_NO' ? 'Saving...' : 'Splitting...') : (splitMode === 'SET_SPLIT_NO' ? 'Save Split No' : 'Split Folio')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {/* Route Folio Modal */}
      {routeModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(620px,95vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setRouteModal(null)} style={{ position: 'absolute', top: '0.45rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}>&times;</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Route Folio - {routeModal.folio_label}</h4>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Route To
                <select
                  value={routeForm.route_to}
                  onChange={(e) => setRouteForm((f) => ({ ...f, route_to: e.target.value, party_id: e.target.value === 'SELF' ? '' : f.party_id }))}
                  style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                >
                  <option value="SELF">Self</option>
                  <option value="CORPORATE">Corporate</option>
                  <option value="TRAVEL_AGENT">Travel Agent</option>
                  <option value="OTA_VCC">OTA VCC</option>
                  <option value="CITY_LEDGER">City Ledger</option>
                </select>
              </label>
              {(() => {
                const routeType = String(routeForm.route_to || '').toUpperCase();
                const routeOptions = getPartyMasterOptions(routeType);
                const useMasterOptions = routeOptions.length > 0;
                return (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Party ID {routeType !== 'SELF' ? '*' : ''}
                    {useMasterOptions ? (
                      <select
                        value={routeForm.party_id}
                        onChange={(e) => setRouteForm((f) => ({ ...f, party_id: e.target.value }))}
                        disabled={routeType === 'SELF'}
                        style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                      >
                        <option value="">Select {routeType === 'CORPORATE' ? 'Corporate' : 'Travel Agent'}</option>
                        {routeOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                      </select>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        value={routeForm.party_id}
                        onChange={(e) => setRouteForm((f) => ({ ...f, party_id: e.target.value }))}
                        disabled={routeType === 'SELF'}
                        placeholder="Enter target party ID"
                        style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                      />
                    )}
                  </label>
                );
              })()}
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Amount
                <input type="number" min="0" step="0.01" value={routeForm.amount} onChange={(e) => setRouteForm((f) => ({ ...f, amount: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Reference No
                <input type="text" value={routeForm.reference_no} onChange={(e) => setRouteForm((f) => ({ ...f, reference_no: e.target.value }))} placeholder="Reference" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600, marginBottom: '0.6rem' }}>
              Remarks
              <textarea value={routeForm.remarks} onChange={(e) => setRouteForm((f) => ({ ...f, remarks: e.target.value }))} rows="2" placeholder="Routing remarks" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontFamily: 'inherit' }}></textarea>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.6rem', fontWeight: 600, fontSize: '0.85rem' }}>
              <input type="checkbox" checked={!!routeForm.auto_transfer} onChange={(e) => setRouteForm((f) => ({ ...f, auto_transfer: e.target.checked }))} />
              Auto transfer routed amount to city ledger
            </label>

            {routeError ? <div className="error-banner">{routeError}</div> : null}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-muted btn-sm rounded" onClick={() => setRouteModal(null)} disabled={routeBusy}>Cancel</button>
              <button className="btn-primary btn-sm rounded" onClick={submitRoute} disabled={routeBusy}>
                {routeBusy ? 'Routing...' : 'Confirm Route'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* City Ledger Transfer Modal */}
      {transferModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(620px,95vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem', position: 'relative' }}>
            <button onClick={() => setTransferModal(null)} style={{ position: 'absolute', top: '0.45rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}>&times;</button>
            <h4 style={{ margin: '0 0 0.6rem 0', color: 'var(--theme-primary)' }}>Transfer To City Ledger - {transferModal.folio_label}</h4>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Party Type
                <select value={transferForm.party_type} onChange={(e) => setTransferForm((f) => ({ ...f, party_type: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                  <option value="CORPORATE">Corporate</option>
                  <option value="TRAVEL_AGENT">Travel Agent</option>
                  <option value="OTA">OTA</option>
                  <option value="CITY_LEDGER">City Ledger</option>
                </select>
              </label>
              {(() => {
                const transferType = String(transferForm.party_type || '').toUpperCase();
                const transferOptions = getPartyMasterOptions(transferType);
                const useMasterOptions = transferOptions.length > 0;
                return (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Party ID *
                    {useMasterOptions ? (
                      <select
                        value={transferForm.party_id}
                        onChange={(e) => setTransferForm((f) => ({ ...f, party_id: e.target.value }))}
                        style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                      >
                        <option value="">Select {transferType === 'CORPORATE' ? 'Corporate' : 'Travel Agent'}</option>
                        {transferOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                      </select>
                    ) : (
                      <input type="number" min="1" value={transferForm.party_id} onChange={(e) => setTransferForm((f) => ({ ...f, party_id: e.target.value }))} placeholder="Enter party ID" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                    )}
                  </label>
                );
              })()}
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Amount *
                <input type="number" min="0" step="0.01" value={transferForm.amount} onChange={(e) => setTransferForm((f) => ({ ...f, amount: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                Reference No
                <input type="text" value={transferForm.reference_no} onChange={(e) => setTransferForm((f) => ({ ...f, reference_no: e.target.value }))} placeholder="Reference" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
              </label>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600, marginBottom: '0.6rem' }}>
              Remarks
              <textarea value={transferForm.remarks} onChange={(e) => setTransferForm((f) => ({ ...f, remarks: e.target.value }))} rows="2" placeholder="Transfer remarks" style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontFamily: 'inherit' }}></textarea>
            </label>

            {transferError ? <div className="error-banner">{transferError}</div> : null}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-muted btn-sm rounded" onClick={() => setTransferModal(null)} disabled={transferBusy}>Cancel</button>
              <button className="btn-primary btn-sm rounded" onClick={submitTransfer} disabled={transferBusy}>
                {transferBusy ? 'Transferring...' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Transfer to Guest Modal ── */}
      {guestTransferModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(760px,96vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1.1rem', position: 'relative' }}>
            <button onClick={() => setGuestTransferModal(null)} style={{ position: 'absolute', top: '0.45rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}>&times;</button>
            <h4 style={{ margin: '0 0 0.4rem 0', color: '#7c3aed' }}>↔ Transfer Charges to Another Guest</h4>
            <div style={{ fontSize: '0.84rem', color: '#6b7280', marginBottom: '1rem' }}>
              From: <b>Room {guestTransferModal.room_number}</b> — {guestTransferModal.folio_label}
            </div>

            {guestTransferSuccess ? (
              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: '1rem', color: '#15803d', fontWeight: 600, textAlign: 'center', fontSize: '0.95rem' }}>
                ✓ {guestTransferSuccess}
              </div>
            ) : (
              <>
                {/* Step 1 — Select charges */}
                <div style={{ marginBottom: '0.9rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.87rem', marginBottom: '0.4rem', color: 'var(--theme-text)' }}>Step 1 — Select charges to transfer</div>
                  {guestTransferModal.lines.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: '0.85rem', padding: '0.6rem', background: '#f9fafb', borderRadius: 6 }}>No transferable charge lines found on this folio.</div>
                  ) : (() => {
                    const allSelected = guestTransferModal.lines.length > 0 && guestTransferModal.lines.every((l) => guestTransferSelected.has(l.ledger_id));
                    const selectedTotal = guestTransferModal.lines
                      .filter((l) => guestTransferSelected.has(l.ledger_id))
                      .reduce((s, l) => s + Number(l.debit_amount || 0), 0);
                    return (
                      <>
                        <div style={{ maxHeight: '230px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#faf5ff' }}>
                              <tr>
                                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                                  <input type="checkbox" checked={allSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) setGuestTransferSelected(new Set(guestTransferModal.lines.map((l) => l.ledger_id)));
                                      else setGuestTransferSelected(new Set());
                                    }} />
                                </th>
                                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Type</th>
                                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Description</th>
                                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>Amount</th>
                                <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {guestTransferModal.lines.map((line) => (
                                <tr key={line.ledger_id} style={{ borderTop: '1px solid #f3f4f6', background: guestTransferSelected.has(line.ledger_id) ? '#faf5ff' : '#fff' }}>
                                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'center' }}>
                                    <input type="checkbox" checked={guestTransferSelected.has(line.ledger_id)}
                                      onChange={(e) => {
                                        const s = new Set(guestTransferSelected);
                                        e.target.checked ? s.add(line.ledger_id) : s.delete(line.ledger_id);
                                        setGuestTransferSelected(s);
                                      }} />
                                  </td>
                                  <td style={{ padding: '0.3rem 0.5rem', color: '#6b7280' }}>{line.posting_date || (line.created_at ? String(line.created_at).slice(0, 10) : '-')}</td>
                                  <td style={{ padding: '0.3rem 0.5rem' }}>{line.posting_type || '-'}</td>
                                  <td style={{ padding: '0.3rem 0.5rem' }}>{line.revenue_desc || line.revenue_code || '-'}</td>
                                  <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>₹{Number(line.debit_amount || 0).toFixed(2)}</td>
                                  <td style={{ padding: '0.3rem 0.5rem', color: '#6b7280' }}>{line.remarks || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ fontSize: '0.84rem', marginTop: '0.35rem', color: '#4b5563' }}>
                          {guestTransferSelected.size} of {guestTransferModal.lines.length} line(s) selected
                          {guestTransferSelected.size > 0 && <> — <b style={{ color: '#7c3aed' }}>₹{selectedTotal.toFixed(2)}</b> to transfer</>}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Step 2 — Select destination guest */}
                <div style={{ marginBottom: '0.9rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.87rem', marginBottom: '0.4rem', color: 'var(--theme-text)' }}>Step 2 — Select destination guest</div>
                  {(() => {
                    const inHouseGuests = rack.filter((r) =>
                      Number(r.stay_id) > 0 &&
                      Number(r.stay_id) !== Number(guestTransferModal.stay_id) &&
                      String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED'
                    );
                    return inHouseGuests.length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No other checked-in guests found.</div>
                    ) : (
                      <select value={guestTransferTargetStayId} onChange={(e) => setGuestTransferTargetStayId(e.target.value)}
                        style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.87rem' }}>
                        <option value="">— Select destination guest —</option>
                        {inHouseGuests.map((r) => (
                          <option key={r.stay_id} value={r.stay_id}>
                            Room {r.room_number} — {r.primary_guest_name || 'Guest'} (Stay #{r.stay_id})
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                </div>

                {/* Step 3 — Remarks */}
                <div style={{ marginBottom: '0.9rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.87rem', marginBottom: '0.4rem', color: 'var(--theme-text)' }}>Step 3 — Transfer reason / remarks</div>
                  <input type="text" value={guestTransferRemarks} onChange={(e) => setGuestTransferRemarks(e.target.value)}
                    placeholder="e.g. Guest B paying for all F&B charges"
                    style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.87rem' }} />
                </div>

                {guestTransferError && <div className="error-banner">{guestTransferError}</div>}

                <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
                  <button className="btn-muted btn-sm rounded" onClick={() => setGuestTransferModal(null)} disabled={guestTransferBusy}>Cancel</button>
                  <button className="btn-warning btn-sm rounded" onClick={submitGuestTransfer} disabled={guestTransferBusy || guestTransferSelected.size === 0 || !guestTransferTargetStayId}>
                    {guestTransferBusy ? 'Transferring...' : `Transfer ${guestTransferSelected.size > 0 ? `(${guestTransferSelected.size})` : ''}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Linked Stays & Routing Modal */}
      {linkedRoomsModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 55 }}>
          <div style={{ width: 'min(780px,96vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1rem 1.2rem', position: 'relative' }}>
            <button onClick={() => setLinkedRoomsModal(null)} style={{ position: 'absolute', top: '0.45rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.2rem', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}>&times;</button>
            <h4 style={{ margin: '0 0 0.75rem 0', color: 'var(--theme-primary)' }}>Linked Stays & Routing — {linkedRoomsModal.stay_no}</h4>

            {/* Tab bar */}
            <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.9rem', borderBottom: '2px solid var(--theme-border)' }}>
              {[
                { key: 'LINKED_STAYS', label: 'Linked Stays' },
                { key: 'ROUTING', label: 'Routing Rules' },
                { key: 'AUDIT', label: 'Audit Log' },
              ].map((tab) => (
                <button key={tab.key} onClick={() => setLinkTab(tab.key)}
                  style={{ padding: '0.35rem 0.9rem', border: 'none', borderBottom: linkTab === tab.key ? '2px solid var(--theme-primary)' : '2px solid transparent', background: 'none', color: linkTab === tab.key ? 'var(--theme-primary)' : '#4a5568', fontWeight: linkTab === tab.key ? 700 : 400, cursor: 'pointer', marginBottom: '-2px', fontSize: '0.88rem' }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── LINKED STAYS TAB ── */}
            {linkTab === 'LINKED_STAYS' && (
              <div>
                {linkGroupLoading ? (
                  <div style={{ color: '#4a5568', fontSize: '0.88rem' }}>Loading linked stays...</div>
                ) : linkGroupError ? (
                  <div style={{ color: '#c53030', fontSize: '0.88rem' }}>{linkGroupError}</div>
                ) : linkGroup ? (
                  <div style={{ marginBottom: '0.7rem' }}>
                    <div style={{ fontSize: '0.82rem', color: '#4a5568', marginBottom: '0.5rem' }}>
                      Group: <strong>{linkGroup.link_group_id}</strong> &nbsp;·&nbsp; Master Stay: <strong>#{linkGroup.master_stay_id}</strong> &nbsp;·&nbsp;
                      <span style={{ color: linkGroup.is_active ? '#276749' : '#c53030', fontWeight: 600 }}>{linkGroup.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div style={{ border: '1px solid #cbd5e0', borderRadius: '6px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: '#ebf8ff' }}>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Stay #</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Room</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Role</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Stay Status</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Guest</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Pax</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Meal Plan</th>
                            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linkMembers.map((m) => {
                            const rackRoom = rack.find((r) => String(r.stay_id) === String(m.stay_id));
                            const isMaster = String(m.stay_id) === String(linkGroup.master_stay_id);
                            const isCurrentStay = String(m.stay_id) === String(linkedRoomsModal.stay_id);
                            return (
                              <tr key={m.link_member_id} style={{ borderTop: '1px solid #edf2f7', background: isCurrentStay ? '#f0fff4' : undefined }}>
                                <td style={{ padding: '0.3rem 0.5rem', fontWeight: isMaster ? 700 : 400 }}>
                                  #{m.stay_id} {isMaster ? <span style={{ fontSize: '0.7rem', background: 'var(--theme-primary)', color: '#fff', borderRadius: '3px', padding: '1px 4px', marginLeft: '3px' }}>MASTER</span> : null}
                                </td>
                                <td style={{ padding: '0.3rem 0.5rem' }}>{m.room_number || '-'}</td>
                                <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', color: '#4a5568' }}>{m.relationship_type || '-'}</td>
                                <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', color: m.is_active ? '#276749' : '#c53030' }}>{m.stay_status || '-'}</td>
                                <td style={{ padding: '0.3rem 0.5rem' }}>{rackRoom?.primary_guest_name || '-'}</td>
                                <td style={{ padding: '0.3rem 0.5rem' }}>{formatRackPax(rackRoom)}</td>
                                <td style={{ padding: '0.3rem 0.5rem' }}>{rackRoom?.meal_plan_name || rackRoom?.rate_plan_code || '-'}</td>
                                <td style={{ padding: '0.3rem 0.5rem' }}>
                                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                                    {!isMaster && m.is_active && (
                                      <>
                                        <button className="btn-danger btn-sm rounded" onClick={() => submitUnlink(m.stay_id)} disabled={linkBusy} style={{ fontSize: '0.75rem' }}>
                                          Unlink
                                        </button>
                                        <button className="btn-primary btn-sm rounded" onClick={() => submitMakeMaster(m.stay_id)} disabled={linkBusy} style={{ fontSize: '0.75rem' }}>
                                          Make Master
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#718096', fontSize: '0.88rem', marginBottom: '0.7rem' }}>This stay is not part of a link group.</div>
                )}

                {/* Link a new stay */}
                <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.88rem' }}>Link Another Stay (as Child)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.5rem', marginBottom: '0.4rem' }}>
                    <select value={linkTargetStayId} onChange={(e) => setLinkTargetStayId(e.target.value)}
                      style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.85rem' }}>
                      <option value=''>— Select Stay to Link —</option>
                      {rack.filter((r) =>
                        String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED'
                        && r.stay_id
                        && String(r.stay_id) !== String(linkedRoomsModal?.stay_id)
                        && !linkMembers.some((m) => String(m.stay_id) === String(r.stay_id))
                      ).map((r) => (
                        <option key={r.stay_id} value={r.stay_id}>
                          Room {r.room_number} — {r.primary_guest_name || 'Guest'} (#{r.stay_id})
                        </option>
                      ))}
                    </select>
                    <input type='text' placeholder='Reason (optional)' value={linkReason} onChange={(e) => setLinkReason(e.target.value)}
                      style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.85rem' }} />
                  </div>
                  {linkGroup && (
                    <div style={{ marginBottom: '0.4rem' }}>
                      <label style={{ fontSize: '0.82rem', color: '#4a5568', marginRight: '0.4rem' }}>Unlink mode:</label>
                      <select value={unlinkMode} onChange={(e) => setUnlinkMode(e.target.value)}
                        style={{ padding: '0.25rem 0.4rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }}>
                        <option value='STOP_FUTURE_ONLY'>Stop Future Only</option>
                        <option value='STOP_AND_REVERSE_HISTORY'>Stop & Reverse History</option>
                      </select>
                    </div>
                  )}
                  <button className="btn-primary btn-sm rounded" onClick={submitLink} disabled={linkBusy || !linkTargetStayId}>
                    {linkBusy ? 'Processing...' : 'Link Stay'}
                  </button>
                  {linkError ? <div style={{ color: '#c53030', fontSize: '0.83rem', marginTop: '0.35rem' }}>{linkError}</div> : null}
                </div>
              </div>
            )}

            {/* ── ROUTING RULES TAB ── */}
            {linkTab === 'ROUTING' && (
              <div>
                {routingLoading ? (
                  <div style={{ color: '#4a5568', fontSize: '0.88rem' }}>Loading routing rules...</div>
                ) : routingError ? (
                  <div style={{ color: '#c53030', fontSize: '0.88rem' }}>{routingError}</div>
                ) : routingRules.length > 0 ? (
                  <div style={{ marginBottom: '0.7rem', border: '1px solid #cbd5e0', borderRadius: '6px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#ebf8ff' }}>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>From</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>To</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>Mode</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>Valid From</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>Valid To</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>%</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>Rank</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>Active</th>
                          <th style={{ padding: '0.3rem 0.45rem', textAlign: 'left' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {routingRules.map((rule) => (
                          <tr key={rule.routing_rule_id} style={{ borderTop: '1px solid #edf2f7', background: editRoute?.routing_rule_id === rule.routing_rule_id ? '#fffbeb' : undefined }}>
                            <td style={{ padding: '0.3rem 0.45rem' }}>#{rule.from_stay_id}</td>
                            <td style={{ padding: '0.3rem 0.45rem' }}>#{rule.to_stay_id}</td>
                            <td style={{ padding: '0.3rem 0.45rem', fontSize: '0.77rem' }}>{rule.routing_mode}</td>
                            <td style={{ padding: '0.3rem 0.45rem', fontSize: '0.77rem' }}>{rule.valid_from ? String(rule.valid_from).slice(0, 10) : '-'}</td>
                            <td style={{ padding: '0.3rem 0.45rem', fontSize: '0.77rem' }}>{rule.valid_to ? String(rule.valid_to).slice(0, 10) : '—'}</td>
                            <td style={{ padding: '0.3rem 0.45rem' }}>{rule.percentage != null ? `${rule.percentage}%` : '-'}</td>
                            <td style={{ padding: '0.3rem 0.45rem', fontSize: '0.77rem' }}>{rule.precedence_rank ?? '-'}</td>
                            <td style={{ padding: '0.3rem 0.45rem', color: rule.is_active ? '#276749' : '#c53030' }}>{rule.is_active ? 'Yes' : 'No'}</td>
                            <td style={{ padding: '0.3rem 0.45rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {rule.is_active && (
                                  <>
                                    <button className="btn-primary btn-sm rounded"
                                      onClick={() => editRoute?.routing_rule_id === rule.routing_rule_id ? setEditRoute(null) : setEditRoute({ routing_rule_id: rule.routing_rule_id, routing_mode: rule.routing_mode, valid_from: rule.valid_from ? String(rule.valid_from).slice(0, 10) : '', valid_to: rule.valid_to ? String(rule.valid_to).slice(0, 10) : '', percentage: rule.percentage ?? '', transaction_code: rule.transaction_code || '', precedence_rank: rule.precedence_rank ?? '', reason: '' })}
                                      style={{ fontSize: '0.73rem' }}>
                                      {editRoute?.routing_rule_id === rule.routing_rule_id ? 'Cancel' : 'Edit'}
                                    </button>
                                    <button className="btn-danger btn-sm rounded" onClick={() => submitCloseRoute(rule.routing_rule_id)} disabled={routeCloseBusy === rule.routing_rule_id}
                                      style={{ fontSize: '0.73rem' }}>
                                      {routeCloseBusy === rule.routing_rule_id ? '...' : 'Close'}
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {routeCloseError ? <div style={{ color: '#c53030', fontSize: '0.83rem', padding: '0.3rem 0.5rem' }}>{routeCloseError}</div> : null}

                    {/* Inline edit form */}
                    {editRoute ? (
                      <div style={{ background: '#fffbeb', borderTop: '1px solid #f6e05e', padding: '0.7rem 0.8rem' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.45rem', color: '#744210' }}>Edit Rule #{editRoute.routing_rule_id}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, 1fr)', gap: '0.45rem', marginBottom: '0.45rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Valid From</div>
                            <input type='date' value={editRoute.valid_from} onChange={(e) => setEditRoute((p) => ({ ...p, valid_from: e.target.value }))}
                              style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Valid To (optional)</div>
                            <input type='date' value={editRoute.valid_to} onChange={(e) => setEditRoute((p) => ({ ...p, valid_to: e.target.value }))}
                              style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Percentage %</div>
                            <input type='number' min={1} max={100} value={editRoute.percentage} onChange={(e) => setEditRoute((p) => ({ ...p, percentage: e.target.value }))}
                              style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, 1fr)', gap: '0.45rem', marginBottom: '0.45rem' }}>
                          {editRoute.routing_mode !== 'ALL_CHARGES' && (
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Transaction Code</div>
                              <input type='text' value={editRoute.transaction_code} onChange={(e) => setEditRoute((p) => ({ ...p, transaction_code: e.target.value }))}
                                style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Precedence Rank</div>
                            <input type='number' value={editRoute.precedence_rank} onChange={(e) => setEditRoute((p) => ({ ...p, precedence_rank: e.target.value }))}
                              style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#718096', marginBottom: '0.15rem' }}>Reason</div>
                            <input type='text' value={editRoute.reason} onChange={(e) => setEditRoute((p) => ({ ...p, reason: e.target.value }))}
                              style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} placeholder='Reason for change' />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          <button className="btn-success btn-sm rounded" onClick={submitEditRoute} disabled={editRouteBusy}>
                            {editRouteBusy ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button className="btn-muted btn-sm rounded" onClick={() => setEditRoute(null)}>Cancel</button>
                        </div>
                        {editRouteError ? <div style={{ color: '#c53030', fontSize: '0.82rem', marginTop: '0.3rem' }}>{editRouteError}</div> : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div style={{ color: '#718096', fontSize: '0.88rem', marginBottom: '0.7rem' }}>No routing rules found.</div>
                )}

                {/* Create new routing rule */}
                {linkGroup ? (
                  <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.88rem' }}>Create Routing Rule</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '0.45rem', marginBottom: '0.45rem' }}>
                      <div>
                        <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>From Stay (Child)</div>
                        <select value={newRoute.from_stay_id} onChange={(e) => setNewRoute((p) => ({ ...p, from_stay_id: e.target.value }))}
                          style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }}>
                          <option value=''>— Child Stay —</option>
                          {linkMembers.filter((m) => String(m.stay_id) !== String(linkGroup.master_stay_id) && m.is_active).map((m) => (
                            <option key={m.stay_id} value={m.stay_id}>#{m.stay_id} (Rm {m.room_number || '-'})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>To Stay (Master)</div>
                        <select value={newRoute.to_stay_id} onChange={(e) => setNewRoute((p) => ({ ...p, to_stay_id: e.target.value }))}
                          style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }}>
                          <option value=''>— Master Stay —</option>
                          {linkMembers.filter((m) => String(m.stay_id) === String(linkGroup.master_stay_id)).map((m) => (
                            <option key={m.stay_id} value={m.stay_id}>#{m.stay_id} (Rm {m.room_number || '-'})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Routing Mode</div>
                        <select value={newRoute.routing_mode} onChange={(e) => setNewRoute((p) => ({ ...p, routing_mode: e.target.value }))}
                          style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }}>
                          <option value='ALL_CHARGES'>All Charges</option>
                          <option value='BY_CODE_OR_DATE'>By Code / Date</option>
                          <option value='PERCENT_SPLIT'>Percent Split</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: '0.45rem', marginBottom: '0.45rem' }}>
                      <div>
                        <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Valid From *</div>
                        <input type='date' value={newRoute.valid_from} onChange={(e) => setNewRoute((p) => ({ ...p, valid_from: e.target.value }))}
                          style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Valid To (opt)</div>
                        <input type='date' value={newRoute.valid_to} onChange={(e) => setNewRoute((p) => ({ ...p, valid_to: e.target.value }))}
                          style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} />
                      </div>
                      {newRoute.routing_mode === 'PERCENT_SPLIT' ? (
                        <div>
                          <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Percentage %</div>
                          <input type='number' min={1} max={100} value={newRoute.percentage} onChange={(e) => setNewRoute((p) => ({ ...p, percentage: e.target.value }))}
                            style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} placeholder='e.g. 50' />
                        </div>
                      ) : newRoute.routing_mode === 'BY_CODE_OR_DATE' ? (
                        <div>
                          <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Txn Code</div>
                          <input type='text' value={newRoute.transaction_code} onChange={(e) => setNewRoute((p) => ({ ...p, transaction_code: e.target.value }))}
                            style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} placeholder='e.g. ROOM' />
                        </div>
                      ) : <div />}
                      <div>
                        <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Rank (opt)</div>
                        <input type='number' value={newRoute.precedence_rank} onChange={(e) => setNewRoute((p) => ({ ...p, precedence_rank: e.target.value }))}
                          style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} placeholder='100' />
                      </div>
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.77rem', color: '#718096', marginBottom: '0.15rem' }}>Reason (optional)</div>
                      <input type='text' value={newRoute.reason} onChange={(e) => setNewRoute((p) => ({ ...p, reason: e.target.value }))}
                        style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.82rem' }} placeholder='e.g. Family billing — room charges to master' />
                    </div>
                    <button className="btn-primary btn-sm rounded" onClick={submitNewRoute} disabled={newRouteBusy || !newRoute.from_stay_id || !newRoute.to_stay_id}>
                      {newRouteBusy ? 'Creating...' : 'Create Rule'}
                    </button>
                    {newRouteError ? <div style={{ color: '#c53030', fontSize: '0.83rem', marginTop: '0.35rem' }}>{newRouteError}</div> : null}
                  </div>
                ) : (
                  <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem', color: '#718096', fontSize: '0.85rem' }}>
                    Link stays first (Linked Stays tab) to create routing rules.
                  </div>
                )}
              </div>
            )}

            {/* ── AUDIT LOG TAB ── */}
            {linkTab === 'AUDIT' && (
              <div>
                {auditLoading ? (
                  <div style={{ color: '#4a5568', fontSize: '0.88rem' }}>Loading audit log...</div>
                ) : auditError ? (
                  <div style={{ color: '#c53030', fontSize: '0.88rem' }}>{auditError}</div>
                ) : auditEvents.length > 0 ? (
                  <div style={{ border: '1px solid #cbd5e0', borderRadius: '6px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#ebf8ff' }}>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Time</th>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Event Type</th>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Action</th>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Reason</th>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>By</th>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Reverse</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditEvents.map((ev, i) => {
                          const isReversibleTxn = ev.event_type === 'ROUTING_TXN' && ev.action_type === 'CREATE';
                          const isBeingReversed = reversalBusy === String(ev.entity_id);
                          return (
                            <tr key={i} style={{ borderTop: '1px solid #edf2f7' }}>
                              <td style={{ padding: '0.3rem 0.5rem', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{fmtDateTimeMedium(ev.acted_at)}</td>
                              <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>{ev.event_type || '-'}</td>
                              <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem', color: '#4a5568' }}>{ev.action_type || '-'}</td>
                              <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>{ev.action_reason || '-'}</td>
                              <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>{ev.acted_by || '-'}</td>
                              <td style={{ padding: '0.3rem 0.5rem' }}>
                                {isReversibleTxn && (
                                  <button className="btn-danger btn-sm rounded" onClick={() => submitReversal(ev.entity_id)} disabled={isBeingReversed} style={{ fontSize: '0.73rem' }}>
                                    {isBeingReversed ? '...' : 'Reverse'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {reversalError ? <div style={{ color: '#c53030', fontSize: '0.83rem', padding: '0.3rem 0.5rem' }}>{reversalError}</div> : null}
                  </div>
                ) : (
                  <div style={{ color: '#718096', fontSize: '0.88rem' }}>No audit events found.</div>
                )}
              </div>
            )}

            <div style={{ marginTop: '0.9rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-muted btn-sm rounded" onClick={() => setLinkedRoomsModal(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ══ Mark Clean — Staff Picker Modal ══ */}
      {hkStaffPickRoom && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={() => { setHkStaffPickRoom(null); setHkSelectedStaff(''); setHkRemarks(''); }}>
          <div style={{ width: 'min(380px,90vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.25rem', boxShadow: '0 10px 30px rgba(15,23,42,0.2)' }}
            onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 0.15rem', color: 'var(--theme-primary)', fontSize: '1rem' }}>Mark Room Clean</h4>
            <p style={{ margin: '0 0 1rem', color: 'var(--theme-text-secondary)', fontSize: '0.85rem' }}>Room {hkStaffPickRoom.room_number}</p>
            <div style={{ marginBottom: '0.75rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Cleaned By</label>
              <select
                value={hkSelectedStaff}
                onChange={(e) => setHkSelectedStaff(e.target.value)}
                className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full"
              >
                <option value="">-- Select Staff --</option>
                {hkStaff.map((s) => (
                  <option key={s.staff_id} value={s.staff_id}>{s.staff_name}{s.shift && s.shift !== 'GENERAL' ? ` (${s.shift})` : ''}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Remarks (optional)</label>
              <input
                placeholder="e.g. Deep cleaned, linen changed"
                value={hkRemarks}
                onChange={(e) => setHkRemarks(e.target.value)}
                className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setHkStaffPickRoom(null); setHkSelectedStaff(''); setHkRemarks(''); }}
                className="btn-muted btn-sm rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateHousekeeping(hkStaffPickRoom.room_id, 'CLEAN', null, hkSelectedStaff || null, hkRemarks);
                  setHkStaffPickRoom(null);
                  setHkSelectedStaff('');
                  setHkRemarks('');
                }}
                disabled={hkUpdatingRoomId === String(hkStaffPickRoom.room_id) || !hkSelectedStaff}
                className="btn-success btn-sm rounded"
              >
                {hkUpdatingRoomId === String(hkStaffPickRoom.room_id) ? 'Updating...' : 'Confirm Clean'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Priority Cleaning Request Modal ══ */}
      {showPriorityReqForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={() => setShowPriorityReqForm(false)}>
          <div style={{ width: 'min(440px,92vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.25rem', boxShadow: '0 10px 30px rgba(15,23,42,0.2)' }}
            onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 0.8rem', color: 'var(--theme-primary)', fontSize: '1rem' }}>Priority Cleaning Request</h4>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              <div>
                <label className="form-label" style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.82rem' }}>Room</label>
                <select value={prReqForm.room_id} onChange={(e) => setPrReqForm({ ...prReqForm, room_id: e.target.value })}
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full">
                  <option value="">Select Room</option>
                  {rack.slice().sort((a, b) => String(a.room_number || '').localeCompare(String(b.room_number || ''), undefined, { numeric: true }))
                    .map((rm) => <option key={rm.room_id} value={rm.room_id}>{rm.room_number} ({String(rm.housekeeping_status || 'CLEAN').toUpperCase()})</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.82rem' }}>Request Type</label>
                  <select value={prReqForm.request_type} onChange={(e) => setPrReqForm({ ...prReqForm, request_type: e.target.value })}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full">
                    <option value="EXPRESS_CLEANING">Express Cleaning</option>
                    <option value="VIP_CLEANING">VIP Cleaning</option>
                    <option value="EARLY_CHECKIN_PREP">Early Check-in Prep</option>
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.82rem' }}>Priority</label>
                  <select value={prReqForm.priority} onChange={(e) => setPrReqForm({ ...prReqForm, priority: e.target.value })}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full">
                    <option value="EXPRESS">Express</option>
                    <option value="VIP">VIP</option>
                    <option value="EARLY_CHECKIN">Early Check-in</option>
                    <option value="NORMAL">Normal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="form-label" style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.82rem' }}>Remarks (optional)</label>
                <input value={prReqForm.remarks} onChange={(e) => setPrReqForm({ ...prReqForm, remarks: e.target.value })}
                  placeholder="e.g. VIP guest arriving at 2 PM"
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setShowPriorityReqForm(false)} className="btn-muted btn-sm rounded">Cancel</button>
              <button
                disabled={!prReqForm.room_id}
                onClick={async () => {
                  try {
                    const res = await fetch('/api/v1/pms/housekeeping/requests', {
                      method: 'POST',
                      headers: { ...headers(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        room_id: Number(prReqForm.room_id),
                        request_type: prReqForm.request_type,
                        priority: prReqForm.priority,
                        remarks: prReqForm.remarks,
                        source_module: 'FRONT_OFFICE'
                      })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.error || 'Failed');
                    setShowPriorityReqForm(false);
                    setPrReqForm({ room_id: '', request_type: 'EXPRESS_CLEANING', priority: 'EXPRESS', remarks: '' });
                    load();
                  } catch (err) { setError(err?.message || 'Failed to send request'); }
                }}
                className="btn-success btn-sm rounded">
                Send Request
              </button>
            </div>

            {/* Active requests tracker */}
            {hkRequests.length > 0 && (
              <div style={{ marginTop: '1rem', borderTop: '1px solid var(--theme-border)', paddingTop: '0.7rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: '0.4rem' }}>Active Requests</div>
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--theme-border-light)' }}>
                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Room</th>
                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Priority</th>
                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hkRequests.map((rq) => (
                        <tr key={rq.request_id} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                          <td style={{ padding: '3px 6px', fontWeight: 600 }}>{rq.room_number || '-'}</td>
                          <td style={{ padding: '3px 6px' }}>
                            <span style={{
                              fontSize: '0.7rem', padding: '1px 5px', borderRadius: 3, fontWeight: 600, color: '#fff',
                              background: String(rq.request_type || '').includes('VIP') ? '#7c3aed' : String(rq.request_type || '').includes('EARLY') ? '#2563eb' : '#ea580c'
                            }}>
                              {(rq.request_type || '').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ padding: '3px 6px' }}>{rq.priority}</td>
                          <td style={{ padding: '3px 6px' }}>
                            <span style={{ fontWeight: 600, color: rq.request_status === 'OPEN' ? '#92400e' : '#1d4ed8' }}>
                              {(rq.request_status || '').replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ Block Room — Confirmation Modal ══ */}
      {blockModal && (() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const closeModal = () => {
          setBlockModal(null);
          setBlockReason('');
          setBlockType('MAINTENANCE');
          const t = new Date().toISOString().slice(0, 10);
          const tm = new Date(); tm.setDate(tm.getDate() + 1);
          setBlockStartDate(t);
          setBlockEndDate(tm.toISOString().slice(0, 10));
        };
        const daysSpan = (() => {
          if (!blockStartDate || !blockEndDate) return 0;
          return Math.round((new Date(blockEndDate) - new Date(blockStartDate)) / 86400000) + 1;
        })();
        const validRange = blockStartDate && blockEndDate && blockEndDate >= blockStartDate && blockEndDate >= todayStr;
        const tooLong = daysSpan > 365;
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={closeModal}>
          <div style={{ width: 'min(440px,92vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.25rem', boxShadow: '0 10px 30px rgba(15,23,42,0.2)' }}
            onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 0.15rem', color: '#b91c1c', fontSize: '1rem' }}>Block Room</h4>
            <p style={{ margin: '0 0 1rem', color: 'var(--theme-text-secondary)', fontSize: '0.85rem' }}>
              Room {blockModal.room_number} will be unavailable for the chosen dates.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <div>
                <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>From Date</label>
                <input
                  type="date"
                  value={blockStartDate}
                  min={todayStr}
                  onChange={(e) => {
                    setBlockStartDate(e.target.value);
                    if (blockEndDate && e.target.value && blockEndDate < e.target.value) setBlockEndDate(e.target.value);
                  }}
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full"
                />
              </div>
              <div>
                <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>To Date</label>
                <input
                  type="date"
                  value={blockEndDate}
                  min={blockStartDate || todayStr}
                  onChange={(e) => setBlockEndDate(e.target.value)}
                  className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full"
                />
              </div>
            </div>
            {validRange && (
              <div style={{ fontSize: '0.75rem', color: tooLong ? '#b91c1c' : 'var(--theme-text-secondary)', marginBottom: '0.5rem' }}>
                {tooLong
                  ? `${daysSpan} days — maximum 365 days per block`
                  : `Blocked for ${daysSpan} ${daysSpan === 1 ? 'day' : 'days'}`}
              </div>
            )}
            <div style={{ marginBottom: '0.75rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Block Reason</label>
              <select
                value={blockType}
                onChange={(e) => setBlockType(e.target.value)}
                className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full"
              >
                <option value="MAINTENANCE">Maintenance / Repair</option>
                <option value="PLUMBING">Plumbing Issue</option>
                <option value="ELECTRICAL">Electrical Issue</option>
                <option value="PAINTING">Painting / Renovation</option>
                <option value="PEST_CONTROL">Pest Control</option>
                <option value="DEEP_CLEANING">Deep Cleaning</option>
                <option value="MANAGEMENT">Management Block</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Notes (optional)</label>
              <input
                placeholder="Additional details about the block"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="rounded border border-slate-300 px-2 py-1.5 text-sm w-full"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} className="btn-muted btn-sm rounded">Cancel</button>
              <button
                disabled={!validRange || tooLong}
                onClick={() => {
                  runRoomAction('block', { room_id: blockModal.room_id }, {
                    block_reason: blockType,
                    block_notes: blockReason.trim(),
                    block_start_date: blockStartDate,
                    block_end_date: blockEndDate,
                  });
                }}
                className="btn-danger btn-sm rounded"
                style={{ opacity: (!validRange || tooLong) ? 0.5 : 1, cursor: (!validRange || tooLong) ? 'not-allowed' : 'pointer' }}
              >
                Confirm Block
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Summary Detail Modal ──────────────────────────────────────── */}
      {summaryDetail && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '5vh' }}
          onClick={() => setSummaryDetail(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 900, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '14px 20px', borderBottom: '2px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: summaryDetail.color }}>{summaryDetail.label}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                  {(() => {
                    const t = getSummaryDetailTotals(summaryDetail.rows);
                    return `${date} — ${summaryDetail.rows?.length || 0} record(s) · ${t.pax} pax`;
                  })()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => {
                  const printW = window.open('', '_blank');
                  if (!printW) return;
                  const rows = summaryDetail.rows || [];
                  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
                  if (summaryDetail.type === 'breakfast_guests') {
                    const totalPax = rows.reduce((s, r) => s + (Number(r.total_pax) || 0), 0);
                    printW.document.write(`<html><head><title>Breakfast Preparation Sheet — ${date}</title>
                      <style>
                        body{font-family:Arial,sans-serif;padding:20px;color:#111}
                        h2{margin:0 0 2px;font-size:20px}
                        .sub{font-size:13px;color:#444;margin-bottom:14px}
                        table{width:100%;border-collapse:collapse}
                        th,td{border:1px solid #999;padding:9px 10px;text-align:left;font-size:15px}
                        th{background:#f0f0f0;font-weight:700;text-transform:uppercase;font-size:12px}
                        td.num{text-align:center}
                        .served{width:70px;text-align:center}
                        tfoot td{font-weight:700;background:#fafafa;font-size:15px}
                        @media print{body{padding:8px}}
                      </style>
                      </head><body>
                      <h2>Breakfast Preparation Sheet</h2>
                      <div class="sub">${esc(selectedProperty?.name ? selectedProperty.name + ' — ' : '')}${esc(date)} · Printed ${esc(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }))}</div>
                      <table>
                        <thead><tr><th>Room</th><th>Guest Name</th><th>Room Type</th><th class="num">Pax</th><th>Meal Plan</th><th class="served">Served</th></tr></thead>
                        <tbody>${rows.map(r => `<tr>
                            <td>${esc(r.room_number) || '-'}</td>
                            <td>${esc(r.guest_name) || '-'}</td>
                            <td>${esc(r.room_type_name) || '-'}</td>
                            <td class="num">${esc(r.total_pax) || '-'}</td>
                            <td>${esc(r.meal_plan_label || r.meal_plan_code) || '-'}</td>
                            <td class="served"></td>
                          </tr>`).join('')}</tbody>
                        <tfoot><tr><td colspan="3">Total</td><td class="num">${totalPax}</td><td colspan="2"></td></tr></tfoot>
                      </table>
                      </body></html>`);
                  } else {
                    const cols = getSummaryDetailColumns(summaryDetail.type);
                    const totals = getSummaryDetailTotals(rows);
                    printW.document.write(`<html><head><title>${summaryDetail.label} — ${date}</title>
                      <style>body{font-family:Arial,sans-serif;font-size:12px;padding:15px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:11px}th{background:#f0f0f0;font-weight:700}h2{margin:0 0 8px;font-size:16px}tfoot td{font-weight:700;background:#f7f7f7}</style>
                      </head><body><h2>${summaryDetail.label} — ${date}</h2>
                      <table><thead><tr>${cols.map(c => `<th>${c.header}</th>`).join('')}</tr></thead>
                      <tbody>${rows.map(r => `<tr>${cols.map(c => `<td>${c.render(r)}</td>`).join('')}</tr>`).join('')}</tbody>
                      <tfoot><tr>${cols.map((c, i) => `<td>${esc(getSummaryDetailFooter(c, totals, i))}</td>`).join('')}</tr></tfoot></table>
                      </body></html>`);
                  }
                  printW.document.close();
                  printW.focus();
                  printW.print();
                }} style={{ padding: '5px 12px', fontSize: '0.78rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                  {summaryDetail.type === 'breakfast_guests' ? 'Print Kitchen Sheet' : 'Print'}
                </button>
                <button onClick={() => setSummaryDetail(null)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#666', lineHeight: 1 }}>×</button>
              </div>
            </div>
            {/* Body */}
            <div style={{ overflow: 'auto', flex: 1, padding: '0' }}>
              {summaryDetail.loading ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--theme-text-secondary)' }}>Loading...</div>
              ) : summaryDetail.error ? (
                <div style={{ padding: 20, color: 'var(--theme-danger)' }}>{summaryDetail.error}</div>
              ) : !summaryDetail.rows?.length ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--theme-text-secondary)' }}>No records found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: summaryDetail.color || '#1e3a5f', position: 'sticky', top: 0 }}>
                      {getSummaryDetailColumns(summaryDetail.type).map(col => (
                        <th key={col.key} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: '0.75rem', color: '#fff', textTransform: 'uppercase', borderBottom: 'none', whiteSpace: 'nowrap' }}>{col.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summaryDetail.rows.map((row, idx) => (
                      <tr key={row.stay_id || row.reservation_id || idx} style={{ borderBottom: '1px solid #f0f0f0', background: idx % 2 === 1 ? '#fafbfc' : '#fff' }}>
                        {getSummaryDetailColumns(summaryDetail.type).map(col => (
                          <td key={col.key} style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{col.render(row)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const totals = getSummaryDetailTotals(summaryDetail.rows);
                      return (
                        <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1', position: 'sticky', bottom: 0 }}>
                          {getSummaryDetailColumns(summaryDetail.type).map((col, i) => (
                            <td key={col.key} style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontWeight: 700, color: '#0f172a' }}>
                              {getSummaryDetailFooter(col, totals, i)}
                            </td>
                          ))}
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {lockReadModalOpen && (
        <LockReadCardModal token={token} onClose={() => setLockReadModalOpen(false)} />
      )}
      {lockEncodeModal && (
        <LockEncodeModal
          token={token}
          selectedProperty={selectedProperty}
          reservationId={lockEncodeModal.reservationId}
          roomNumber={lockEncodeModal.roomNumber}
          guestName={lockEncodeModal.guestName}
          onClose={() => setLockEncodeModal(null)}
        />
      )}

      {/* ── Undo Check-In Modal (REQ-13) ── */}
      {undoCheckinModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !undoCheckinBusy) setUndoCheckinModal(null); }}>
          <div style={{ width: 'min(460px, 96vw)', background: '#fff', borderRadius: 10, border: '1px solid #d9e2ec', padding: '1.1rem 1.2rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.3rem', color: '#92400e' }}>↩ Undo Check-In</h3>
            <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.7rem' }}>
              Room <b>{undoCheckinModal.room_number || '-'}</b> · <b>{undoCheckinModal.guest_name || '-'}</b>
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: '#78350f', marginBottom: '0.8rem' }}>
              Only works the same day as check-in. Every charge and payment on this stay's folio is automatically
              reversed, the room frees up, and the booking reverts to CONFIRMED (pending arrival) — or its prior
              status if it had one. If real cash/card was collected, hand it back separately — reversing the
              ledger only corrects the paper trail. A check-in welcome SMS, if already sent, cannot be recalled.
            </div>
            {undoCheckinError && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{undoCheckinError}</div>}
            <label style={{ display: 'grid', gap: 3, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.9rem' }}>Reason (audited)
              <input value={undoCheckinReason} onChange={(e) => setUndoCheckinReason(e.target.value)}
                placeholder="e.g. Checked in the wrong room by mistake"
                style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => setUndoCheckinModal(null)} disabled={undoCheckinBusy}
                style={{ padding: '0.45rem 0.9rem', border: '1px solid #cbd5e0', background: '#fff', color: '#334155', borderRadius: 6, cursor: 'pointer', fontSize: '0.83rem' }}>Cancel</button>
              <button onClick={submitUndoCheckin} disabled={undoCheckinBusy}
                style={{ padding: '0.45rem 1rem', border: 'none', background: '#b45309', color: '#fff', borderRadius: 6, cursor: undoCheckinBusy ? 'default' : 'pointer', fontSize: '0.83rem', fontWeight: 600, opacity: undoCheckinBusy ? 0.6 : 1 }}>
                {undoCheckinBusy ? 'Undoing…' : 'Undo Check-In'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Column definitions for Summary Detail panel ──────────────────────────────
// Pax total for the KPI detail tables (footer row).
function getSummaryDetailTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return { pax: list.reduce((s, r) => s + (Number(r.total_pax) || 0), 0) };
}

// Footer cell text per column key for the Total row.
function getSummaryDetailFooter(col, totals, colIndex) {
  if (col.key === 'total_pax') return String(totals.pax);
  return colIndex === 0 ? 'Total' : '';
}

function getSummaryDetailColumns(type) {
  const fmtDt = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    if (isNaN(d)) return v;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const fmtDtTime = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    if (isNaN(d)) return v;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const common = [
    { key: 'room_number', header: 'Room', render: r => r.room_number || '-' },
    { key: 'guest_name', header: 'Guest Name', render: r => r.guest_name || '-' },
    { key: 'room_type_name', header: 'Room Type', render: r => r.room_type_name || '-' },
    // No. of Rooms entered on the reservation (pms_reservation_header.room_count)
    { key: 'tot_no_rooms', header: 'Tot. Rooms', render: r => r.tot_no_rooms || '-' },
    { key: 'guest_mobile', header: 'Mobile', render: r => r.guest_mobile || '-' },
    { key: 'total_pax', header: 'Pax', render: r => r.total_pax || '-' },
    { key: 'reservation_no', header: 'Res. No', render: r => r.reservation_no || '-' },
  ];

  switch (type) {
    case 'expected_arrivals':
      return [
        ...common,
        { key: 'arrival_date', header: 'Arrival', render: r => fmtDt(r.arrival_date) },
        { key: 'departure_date', header: 'Departure', render: r => fmtDt(r.departure_date) },
        { key: 'rate_plan_code', header: 'Rate Plan', render: r => r.rate_plan_code || '-' },
        { key: 'status', header: 'Status', render: r => r.status || '-' },
      ];
    case 'expected_departures':
      return [
        ...common,
        { key: 'departure_date', header: 'Departure', render: r => fmtDt(r.departure_date) },
        { key: 'checkin_datetime', header: 'Checked In', render: r => fmtDtTime(r.checkin_datetime) },
      ];
    case 'checkin_today':
      return [
        ...common,
        { key: 'checkin_datetime', header: 'Check-In Time', render: r => fmtDtTime(r.checkin_datetime) },
        { key: 'stay_status', header: 'Status', render: r => r.stay_status || '-' },
      ];
    case 'checkout_today':
      return [
        ...common,
        { key: 'actual_checkout_datetime', header: 'Checkout Time', render: r => fmtDtTime(r.actual_checkout_datetime) },
        { key: 'invoice_no', header: 'Invoice', render: r => r.invoice_no || '-' },
        { key: 'settlement_mode', header: 'Settlement', render: r => r.settlement_mode || '-' },
      ];
    case 'stay_over':
      return [
        ...common,
        { key: 'arrival_date', header: 'Arrival', render: r => fmtDt(r.arrival_date) },
        { key: 'departure_date', header: 'Departure', render: r => fmtDt(r.departure_date) },
        { key: 'checkin_datetime', header: 'Checked In', render: r => fmtDtTime(r.checkin_datetime) },
      ];
    case 'pax_count':
      return [
        ...common,
        { key: 'departure_date', header: 'Departure', render: r => fmtDt(r.departure_date) },
        { key: 'checkin_datetime', header: 'Checked In', render: r => fmtDtTime(r.checkin_datetime) },
      ];
    case 'breakfast_guests':
      return [
        ...common,
        { key: 'meal_plan_label', header: 'Meal Plan', render: r => r.meal_plan_label || r.meal_plan_code || '-' },
        { key: 'departure_date', header: 'Departure', render: r => fmtDt(r.departure_date) },
        { key: 'checkin_datetime', header: 'Checked In', render: r => fmtDtTime(r.checkin_datetime) },
      ];
    default:
      return common;
  }
}

export default PmsCommandCenter;
